import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { FaChevronDown, FaChevronRight, FaSyncAlt, FaTrash } from "react-icons/fa";
import { Eye, List, Pencil, RefreshCw, Trash2 } from "lucide-react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import CognitrackXInvoice from "./CognitrackXInvoice";
import { apiRequest } from "../api";
import { getSelectedCompany, getSessionUserId } from "../auth";
import { getPatientLabels, shouldUseClientTerminology } from "../uiTerminology";
import "./Patients.css";

const API_BASE = process.env.REACT_APP_API_URL_BASE;
const PATIENTS_API_URL = `${API_BASE}/api/patients/`;
const PATIENTS_VIEW_API_URL = `${API_BASE}/api/patients-view/`;
const COMPANIES_API = `${API_BASE}/api/companies/`;
const PATIENT_TYPES_API = `${API_BASE}/api/patient-types/`;
const COMPANY_PATIENTS_API = `${API_BASE}/api/company-patients/`;
const COMPANY_PEOPLE_API = `${API_BASE}/api/company-people/`;
const PATIENT_PEOPLE_API = `${API_BASE}/api/patient-people/`;
const PATIENT_PHONES_API = `${API_BASE}/api/patient-phones/`;
const PATIENT_ADDRESSES_API = `${API_BASE}/api/patient-addresses/`;
const PATIENT_EVENTS_API = `${API_BASE}/api/patient-events/`;
const PATIENT_ORDERS_API = `${API_BASE}/api/patient-orders`;
const INJURY_EVENT_TYPES_API = `${API_BASE}/api/injury-event-types/`;
const PATIENT_ASSESSMENT_ATTEMPTS_API = `${API_BASE}/api/patient-assessment-attempts/`;
const PATIENT_ATTEMPT_PROGRESS_API = `${API_BASE}/api/patient-attempt-progress/`;
const PATIENT_RESPONSES_HISTORY_API = `${API_BASE}/api/patient-responses-history/`;
const PATIENT_ASSESSMENT_LOGS_API = `${API_BASE}/api/patient-assessment-logs`;
const PATIENT_DOCUMENTS_API = `${API_BASE}/api/patient-documents/`;
const DOCUMENT_TYPES_API = `${API_BASE}/api/document-types`;
const DOCUMENT_UPLOAD_GET_LINK_API = `${API_BASE}/api/document-upload/get-link/`;
const DOCUMENT_DOWNLOAD_GET_LINK_API = `${API_BASE}/api/document-download/get-link/`;
const PATIENT_TOKENS_CREATE_API = `${API_BASE}/api/patient-tokens/create/`;
const PATIENT_TOKENS_ROTATE_API = `${API_BASE}/api/patient-tokens/rotate/`;
const SEND_EMAIL_API = `${API_BASE}/api/send-email`;
const SEND_EMAIL_API_ALT = `${API_BASE}/api/send-email/`;
const ASSESSMENTS_API = `${API_BASE}/api/assessments/`;
const COGNITRACKX_REPORT_HTML_API = `${API_BASE}/api/reports/cognitrackx/html`;
const PEOPLE_API = `${API_BASE}/api/people/`;
const PERSON_TYPES_API = `${API_BASE}/api/person-types/`;
const PHONE_TYPES_API = `${API_BASE}/api/phone-types/`;
const ADDRESS_TYPES_API = `${API_BASE}/api/address-types/`;
const LANGUAGES_API = `${API_BASE}/api/languages/`;
const INTEGRATION_OPTIONS_API = `${API_BASE}/api/integration-options/`;
const BODYIQ_SEND_ORDER_API = `${API_BASE}/api/integrations/bodyiq/send-order/`;

const parseAttemptLogJson = (value) => {
  if (value && typeof value === "object") return value;
  if (typeof value !== "string") return null;

  const trimmedValue = value.trim();
  if (!trimmedValue.startsWith("{") && !trimmedValue.startsWith("[")) return null;

  try {
    return JSON.parse(trimmedValue);
  } catch {
    return null;
  }
};

const renderHighlightedJson = (value) => {
  const jsonText = JSON.stringify(value, null, 2);
  const tokenPattern = /("(?:\\u[a-fA-F0-9]{4}|\\[^u]|[^\\"])*"\s*:)|("(?:\\u[a-fA-F0-9]{4}|\\[^u]|[^\\"])*")|\b(true|false)\b|\b(null)\b|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)/g;
  const renderedTokens = [];
  let lastIndex = 0;
  let tokenMatch;

  while ((tokenMatch = tokenPattern.exec(jsonText)) !== null) {
    if (tokenMatch.index > lastIndex) {
      renderedTokens.push(jsonText.slice(lastIndex, tokenMatch.index));
    }

    const token = tokenMatch[0];
    const tokenClass = tokenMatch[1]
      ? "assessment-log-json-key"
      : tokenMatch[2]
        ? "assessment-log-json-string"
        : tokenMatch[3]
          ? "assessment-log-json-boolean"
          : tokenMatch[4]
            ? "assessment-log-json-null"
            : "assessment-log-json-number";

    renderedTokens.push(
      <span className={tokenClass} key={`${tokenMatch.index}-${tokenClass}`}>
        {token}
      </span>
    );
    lastIndex = tokenPattern.lastIndex;
  }

  if (lastIndex < jsonText.length) {
    renderedTokens.push(jsonText.slice(lastIndex));
  }

  return renderedTokens;
};

const US_STATES = [
  { value: "AL", label: "Alabama" },
  { value: "AK", label: "Alaska" },
  { value: "AZ", label: "Arizona" },
  { value: "AR", label: "Arkansas" },
  { value: "CA", label: "California" },
  { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" },
  { value: "DE", label: "Delaware" },
  { value: "FL", label: "Florida" },
  { value: "GA", label: "Georgia" },
  { value: "HI", label: "Hawaii" },
  { value: "ID", label: "Idaho" },
  { value: "IL", label: "Illinois" },
  { value: "IN", label: "Indiana" },
  { value: "IA", label: "Iowa" },
  { value: "KS", label: "Kansas" },
  { value: "KY", label: "Kentucky" },
  { value: "LA", label: "Louisiana" },
  { value: "ME", label: "Maine" },
  { value: "MD", label: "Maryland" },
  { value: "MA", label: "Massachusetts" },
  { value: "MI", label: "Michigan" },
  { value: "MN", label: "Minnesota" },
  { value: "MS", label: "Mississippi" },
  { value: "MO", label: "Missouri" },
  { value: "MT", label: "Montana" },
  { value: "NE", label: "Nebraska" },
  { value: "NV", label: "Nevada" },
  { value: "NH", label: "New Hampshire" },
  { value: "NJ", label: "New Jersey" },
  { value: "NM", label: "New Mexico" },
  { value: "NY", label: "New York" },
  { value: "NC", label: "North Carolina" },
  { value: "ND", label: "North Dakota" },
  { value: "OH", label: "Ohio" },
  { value: "OK", label: "Oklahoma" },
  { value: "OR", label: "Oregon" },
  { value: "PA", label: "Pennsylvania" },
  { value: "RI", label: "Rhode Island" },
  { value: "SC", label: "South Carolina" },
  { value: "SD", label: "South Dakota" },
  { value: "TN", label: "Tennessee" },
  { value: "TX", label: "Texas" },
  { value: "UT", label: "Utah" },
  { value: "VT", label: "Vermont" },
  { value: "VA", label: "Virginia" },
  { value: "WA", label: "Washington" },
  { value: "WV", label: "West Virginia" },
  { value: "WI", label: "Wisconsin" },
  { value: "WY", label: "Wyoming" },
  { value: "DC", label: "District of Columbia" },
];

const COUNTRY_OPTIONS = ["United States", "Canada", "Mexico"];

const LATEST_ASSESSMENT_STATUSES = {
  assigned: { label: "Assigned", className: "assigned" },
  in_progress: { label: "In Progress", className: "in-progress" },
  completed: { label: "Completed", className: "completed" },
};

const formatLatestAssessmentCodename = (value) => {
  const normalizedValue = String(value ?? "").trim();
  if (!normalizedValue) return "—";

  return normalizedValue
    .split("_")
    .filter(Boolean)
    .map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
    .join(" ");
};

const getPersonFromRelation = (item) => item.person ?? item.people ?? item.person_data ?? item;

const getPersonTypeDescription = (person) => (
  person?.person_type?.description ??
  person?.person_type_description ??
  person?.person_type_name ??
  "—"
);

const getPersonDisplayName = (person) => {
  const first = person?.first_name ?? "";
  const last = person?.last_name ?? "";
  return `${first} ${last}`.trim() || person?.email || "—";
};

const normalizePhoneDigits = (value) => String(value ?? "").replace(/\D/g, "");

const formatPhoneForInput = (value) => {
  const digits = normalizePhoneDigits(value);

  if (!digits) return "";
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  if (digits.length <= 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)} ${digits.slice(10)}`;
};

const getDateOnlyInputValue = (value) => {
  const normalizedValue = String(value ?? "").trim();
  if (!normalizedValue) return "";

  const isoDateMatch = normalizedValue.match(/^(\d{4}-\d{2}-\d{2})(?:$|T|\s)/);
  if (isoDateMatch) return isoDateMatch[1];

  const parsed = new Date(normalizedValue);
  if (Number.isNaN(parsed.getTime())) return "";

  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const day = String(parsed.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatDateOnly = (value, emptyValue = "") => {
  const dateInputValue = getDateOnlyInputValue(value);
  if (!dateInputValue) return value ? String(value) : emptyValue;

  const [year, month, day] = dateInputValue.split("-").map(Number);
  return new Date(year, month - 1, day).toLocaleDateString();
};

const getResponseErrorMessage = async (response) => {
  try {
    const data = await response.json();
    if (typeof data === "string") return data;
    return JSON.stringify(data);
  } catch {
    return `status ${response.status}`;
  }
};

const readApiResponseBody = async (response) => {
  const contentType = response.headers.get("content-type") || "";
  return contentType.includes("application/json")
    ? await response.json().catch(() => null)
    : await response.text().catch(() => "");
};

const parseCssColorToRgb = (value) => {
  const normalizedValue = String(value || "").trim();
  if (!normalizedValue) return null;

  const hexMatch = normalizedValue.match(/#([0-9a-f]{3}|[0-9a-f]{6})/i);
  if (hexMatch) {
    const hexValue = hexMatch[1];
    const expandedHex =
      hexValue.length === 3
        ? hexValue
            .split("")
            .map((char) => `${char}${char}`)
            .join("")
        : hexValue;

    return {
      r: parseInt(expandedHex.slice(0, 2), 16),
      g: parseInt(expandedHex.slice(2, 4), 16),
      b: parseInt(expandedHex.slice(4, 6), 16),
    };
  }

  const rgbMatch = normalizedValue.match(/rgba?\(([^)]+)\)/i);
  if (rgbMatch) {
    const components = rgbMatch[1]
      .split(",")
      .map((component) => Number.parseFloat(component.trim()))
      .filter((component) => Number.isFinite(component));

    if (components.length >= 3) {
      return {
        r: Math.max(0, Math.min(255, Math.round(components[0]))),
        g: Math.max(0, Math.min(255, Math.round(components[1]))),
        b: Math.max(0, Math.min(255, Math.round(components[2]))),
      };
    }
  }

  return null;
};

const getPdfPageBackgroundRgb = (rootElement) => {
  const ownerDocument = rootElement?.ownerDocument;
  const ownerWindow = ownerDocument?.defaultView;
  if (!rootElement || !ownerDocument || !ownerWindow) {
    return { r: 11, g: 18, b: 32 };
  }

  const backgroundSources = [
    ownerDocument.body,
    ownerDocument.documentElement,
    rootElement,
  ].filter(Boolean);

  for (const sourceElement of backgroundSources) {
    const computedStyle = ownerWindow.getComputedStyle(sourceElement);
    const directBackgroundColor = parseCssColorToRgb(computedStyle.backgroundColor);
    if (
      directBackgroundColor &&
      !/rgba\(0,\s*0,\s*0,\s*0\)|transparent/i.test(String(computedStyle.backgroundColor || ""))
    ) {
      return directBackgroundColor;
    }

    const backgroundImageColor = parseCssColorToRgb(computedStyle.backgroundImage);
    if (backgroundImageColor) {
      return backgroundImageColor;
    }

    const inlineBackgroundColor = parseCssColorToRgb(sourceElement.style?.backgroundColor);
    if (inlineBackgroundColor) {
      return inlineBackgroundColor;
    }

    const inlineBackground = parseCssColorToRgb(sourceElement.style?.background);
    if (inlineBackground) {
      return inlineBackground;
    }
  }

  return { r: 11, g: 18, b: 32 };
};

const getPdfPaginationHints = (rootElement) => {
  if (!rootElement) {
    return {
      forcedBreaks: [],
      avoidRegions: [],
      preferredBreaks: [],
    };
  }

  const ownerDocument = rootElement.ownerDocument;
  const ownerWindow = ownerDocument?.defaultView;
  if (!ownerWindow) {
    return {
      forcedBreaks: [],
      avoidRegions: [],
      preferredBreaks: [],
    };
  }

  const breakMarkerSelector = [
    "[data-page-break-before]",
    "[data-page-break-after]",
    "[data-pdf-page-break-before]",
    "[data-pdf-page-break-after]",
    ".page-break",
    ".page-break-before",
    ".page-break-after",
    ".print-page-break",
    ".print-page-break-before",
    ".print-page-break-after",
    ".pdf-page-break",
    ".pdf-page-break-before",
    ".pdf-page-break-after",
  ].join(",");

  const rootRect = rootElement.getBoundingClientRect();
  const allElements = [rootElement, ...Array.from(rootElement.querySelectorAll("*"))];
  const markedElements = new Set(rootElement.querySelectorAll(breakMarkerSelector));
  const forcedBreaks = [];
  const avoidRegions = [];
  const preferredBreaks = [];

  allElements.forEach((element, index) => {
    const computedStyle = ownerWindow.getComputedStyle(element);
    const rect = element.getBoundingClientRect();
    const top = Math.round(rect.top - rootRect.top);
    const bottom = Math.round(rect.bottom - rootRect.top);
    const height = Math.max(0, bottom - top);

    const breakBefore =
      computedStyle.breakBefore ||
      computedStyle.pageBreakBefore ||
      "";
    const breakAfter =
      computedStyle.breakAfter ||
      computedStyle.pageBreakAfter ||
      "";
    const breakInside =
      computedStyle.breakInside ||
      computedStyle.pageBreakInside ||
      "";
    const normalizedBreakBefore = String(breakBefore || "").trim().toLowerCase();
    const normalizedBreakAfter = String(breakAfter || "").trim().toLowerCase();
    const normalizedBreakInside = String(breakInside || "").trim().toLowerCase();
    const classList = Array.from(element.classList || []);
    const hasPrintCardHint = classList.includes("cx-print-card");
    const hasPrintDescriptionHint = classList.includes("cx-print-description");
    const hasPrintTitleRowHint = classList.includes("cx-print-title-row");
    const hasPrintFollowTitleHint = classList.includes("cx-print-follow-title");

    const hasBeforeMarker =
      markedElements.has(element) &&
      (/before/i.test(element.className || "") ||
        element.hasAttribute("data-page-break-before") ||
        element.hasAttribute("data-pdf-page-break-before"));
    const hasAfterMarker =
      markedElements.has(element) &&
      (/after/i.test(element.className || "") ||
        element.hasAttribute("data-page-break-after") ||
        element.hasAttribute("data-pdf-page-break-after"));
    const hasGenericMarker =
      markedElements.has(element) && !hasBeforeMarker && !hasAfterMarker;

    if (
      ["page", "always", "left", "right"].includes(normalizedBreakBefore) ||
      hasBeforeMarker ||
      hasGenericMarker
    ) {
      if (top > 0) {
        forcedBreaks.push(top);
      }
    }

    if (["page", "always", "left", "right"].includes(normalizedBreakAfter) || hasAfterMarker) {
      if (bottom > 0) {
        forcedBreaks.push(bottom);
      }
    }

    if (
      (normalizedBreakInside.includes("avoid") ||
        hasPrintCardHint ||
        hasPrintDescriptionHint ||
        hasPrintTitleRowHint ||
        hasPrintFollowTitleHint) &&
      top > 0 &&
      height > 0
    ) {
      avoidRegions.push({
        top,
        bottom,
        height,
        preferTopBreak: hasPrintTitleRowHint,
      });
    }

    if ((normalizedBreakAfter.includes("avoid") || hasPrintTitleRowHint) && top > 0) {
      const nextSiblingElement = element.nextElementSibling;
      const pairedElement = nextSiblingElement ?? allElements[index + 1] ?? null;

      if (pairedElement) {
        const pairedRect = pairedElement.getBoundingClientRect();
        const pairedBottom = Math.round(pairedRect.bottom - rootRect.top);
        if (pairedBottom > top) {
          avoidRegions.push({
            top,
            bottom: pairedBottom,
            height: Math.max(0, pairedBottom - top),
            preferTopBreak: true,
          });
        }
      } else if (bottom > top) {
        avoidRegions.push({ top, bottom, height, preferTopBreak: true });
      }
    }

    if (hasPrintFollowTitleHint && top > 0 && bottom > top) {
      avoidRegions.push({ top, bottom, height, preferTopBreak: false });
    }

    if ((hasPrintCardHint || hasPrintTitleRowHint || hasPrintFollowTitleHint) && top > 0) {
      preferredBreaks.push(top);
    }
  });

  return {
    forcedBreaks: Array.from(new Set(forcedBreaks))
      .filter((value) => Number.isFinite(value) && value > 0)
      .sort((left, right) => left - right),
    avoidRegions: avoidRegions
      .filter((region) =>
        Number.isFinite(region.top) &&
        Number.isFinite(region.bottom) &&
        region.top > 0 &&
        region.bottom > region.top
      )
      .sort((left, right) => left.top - right.top),
    preferredBreaks: Array.from(new Set(preferredBreaks))
      .filter((value) => Number.isFinite(value) && value > 0)
      .sort((left, right) => left - right),
  };
};

const createPdfBlobFromHtml = async (html) => {
  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed";
  iframe.style.left = "-10000px";
  iframe.style.top = "0";
  iframe.style.width = "1024px";
  iframe.style.height = "1px";
  iframe.style.border = "0";
  iframe.setAttribute("aria-hidden", "true");
  document.body.appendChild(iframe);

  try {
    await new Promise((resolve, reject) => {
      iframe.onload = resolve;
      iframe.onerror = () => reject(new Error("Failed to render generated report HTML."));
      iframe.srcdoc = html;
    });

    const reportDocument = iframe.contentDocument;
    const reportElement = reportDocument?.body?.firstElementChild ?? reportDocument?.body;
    if (!reportElement) {
      throw new Error("Generated report HTML did not render any content.");
    }

    await reportDocument.fonts?.ready;
    await Promise.all(Array.from(reportDocument.images || []).map((image) => {
      if (image.complete) return Promise.resolve();
      return new Promise((resolve) => {
        image.addEventListener("load", resolve, { once: true });
        image.addEventListener("error", resolve, { once: true });
      });
    }));

    const captureWidth = Math.ceil(Math.max(reportElement.scrollWidth, reportElement.clientWidth, 1));
    const captureHeight = Math.ceil(Math.max(reportElement.scrollHeight, reportElement.clientHeight, 1));
    iframe.style.height = `${captureHeight}px`;
    const pageBackgroundRgb = getPdfPageBackgroundRgb(reportElement);
    const reportBackgroundColor = `rgb(${pageBackgroundRgb.r}, ${pageBackgroundRgb.g}, ${pageBackgroundRgb.b})`;
    const backgroundLuminance =
      0.2126 * pageBackgroundRgb.r +
      0.7152 * pageBackgroundRgb.g +
      0.0722 * pageBackgroundRgb.b;
    const footerTextRgb = backgroundLuminance < 145
      ? { r: 203, g: 213, b: 225 }
      : { r: 71, g: 85, b: 105 };

    const canvas = await html2canvas(reportElement, {
      scale: 1.5,
      useCORS: true,
      backgroundColor: reportBackgroundColor,
      logging: false,
      width: captureWidth,
      height: captureHeight,
      windowWidth: captureWidth,
      windowHeight: captureHeight,
    });

    const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "letter" });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 12.7;
    const contentWidth = pageWidth - margin * 2;
    const contentHeight = pageHeight - margin * 2;
    const pageHeightPx = Math.floor((canvas.width * contentHeight) / contentWidth);
    const totalPages = Math.ceil(canvas.height / pageHeightPx);
    const footerDisclaimer = "This report contains patient self-reported symptom data only. It does not constitute a medical diagnosis or treatment recommendation. All data submitted for licensed physician review.";

    for (let pageTop = 0, pageIndex = 0; pageTop < canvas.height; pageTop += pageHeightPx, pageIndex += 1) {
      const sliceHeight = Math.min(pageHeightPx, canvas.height - pageTop);
      const pageCanvas = document.createElement("canvas");
      pageCanvas.width = canvas.width;
      pageCanvas.height = sliceHeight;
      const pageContext = pageCanvas.getContext("2d");
      if (!pageContext) throw new Error("Failed to create PDF page canvas.");

      pageContext.fillStyle = reportBackgroundColor;
      pageContext.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
      pageContext.drawImage(
        canvas,
        0,
        pageTop,
        canvas.width,
        sliceHeight,
        0,
        0,
        canvas.width,
        sliceHeight
      );

      if (pageIndex > 0) pdf.addPage();
  pdf.setFillColor(pageBackgroundRgb.r, pageBackgroundRgb.g, pageBackgroundRgb.b);
  pdf.rect(0, 0, pageWidth, pageHeight, "F");
      pdf.addImage(
        pageCanvas.toDataURL("image/jpeg", 0.92),
        "JPEG",
        margin,
        margin,
        contentWidth,
        (sliceHeight / canvas.width) * contentWidth
      );

      pdf.setTextColor(footerTextRgb.r, footerTextRgb.g, footerTextRgb.b);
      pdf.setFontSize(5.5);
      const footerLines = pdf.splitTextToSize(footerDisclaimer, contentWidth);
      pdf.text(footerLines, pageWidth / 2, pageHeight - margin + 1, {
        align: "center",
        baseline: "top",
      });
      pdf.setFontSize(6);
      pdf.text(`Page ${pageIndex + 1} of ${totalPages}`, pageWidth / 2, pageHeight - 3.2, {
        align: "center",
      });
    }

    return pdf.output("blob");
  } finally {
    iframe.remove();
  }
};

const getPhoneRowKey = (phone) =>
  phone?.patient_phone_id ??
  phone?.id ??
  phone?.phone_id ??
  null;

const getPersistedPhoneId = (phone) =>
  phone?._isNew
    ? null
    : phone?.patient_phone_id ??
      phone?.id ??
      null;

const getAddressRowKey = (address) =>
  address?.patient_address_id ??
  address?.id ??
  null;

const getPersistedAddressId = (address) =>
  address?._isNew
    ? null
    : address?.patient_address_id ??
      address?.id ??
      null;

const getPatientRelationCandidates = (patientId) => {
  const normalizedPatientId = Number(patientId ?? 0);
  if (normalizedPatientId <= 0) return [];

  return [
    { patient_id: normalizedPatientId },
    { patient: normalizedPatientId },
  ];
};

const getCompanyIdFromCompanyPerson = (cp) => Number(
  cp.company_id ?? cp.company?.company_id ?? cp.company?.id ?? 0
);

const getPersonIdFromCompanyPerson = (cp) => {
  const person = getPersonFromRelation(cp);
  return Number(cp.person_id ?? person?.person_id ?? person?.id ?? 0);
};

const getPersonIdFromPatientPerson = (pp) => {
  const person = getPersonFromRelation(pp);
  return Number(pp.person_id ?? person?.person_id ?? person?.id ?? 0);
};

const getPatientPersonId = (pp) => (
  pp.patient_person_id ??
  pp.patient_people_id ??
  pp.patient_person?.patient_person_id ??
  null
);

const getPatientEventId = (patientEvent) => (
  patientEvent.patient_event_id ??
  patientEvent.patient_events_id ??
  patientEvent.id ??
  null
);

const getInjuryEventTypeDescription = (eventItem) => (
  eventItem?.injury_event_type?.description ??
  eventItem?.injury_event_type_description ??
  "—"
);

const getInjuryEventTypeId = (eventItem) => (
  Number(
    eventItem?.injury_event_type?.injury_event_type_id ??
    eventItem?.injury_event_type_id ??
    0
  ) || ""
);

const getAttemptPatientId = (attempt) => Number(
  attempt.patient_id ?? attempt.patient?.patient_id ?? attempt.patient?.id ?? 0
);

const getAttemptAssessmentId = (attempt) => Number(
  attempt.assessment_id ?? attempt.assessment?.assessment_id ?? attempt.assessment?.id ?? 0
);

const getAttemptPatientEventId = (attempt) => Number(
  attempt.patient_event_id ??
  attempt.patient_event?.patient_event_id ??
  attempt.patient_event?.id ??
  0
);

const getAttemptId = (attempt) => (
  attempt.id ??
  attempt.patient_assessment_attempt_id ??
  attempt.patient_assessment_attempts_id ??
  attempt.patient_assessment_id ??
  attempt.patient_assessment_attempt?.patient_assessment_attempt_id ??
  attempt.assessment_attempt_id ??
  null
);

const getAttemptStatus = (attempt) =>
  attempt.status ?? attempt.attempt_status ?? "assigned";

const getAttemptTokenValue = (attempt) =>
  String(
    attempt?.token?.token ??
    attempt?.token?.value ??
    attempt?.token ??
    attempt?.patient_token?.token ??
    attempt?.patient_token?.value ??
    attempt?.patient_token ??
    attempt?.assessment_token ??
    ""
  ).trim();

const getAttemptTokenExpiration = (attempt) =>
  attempt?.token_expires_at ??
  attempt?.token_expiration ??
  attempt?.token_expires_on ??
  attempt?.token_expiry ??
  attempt?.expires_at ??
  attempt?.patient_token?.expires_at ??
  attempt?.patient_token?.expires_on ??
  attempt?.token_details?.expires_at ??
  "";

const getAssessmentClassificationPillStyle = (classification) => {
  const normalizedClassification = String(classification || "").trim().toLowerCase();

  if (normalizedClassification === "improbable tbi") {
    return {
      background: "rgba(20, 83, 45, 0.12)",
      border: "1px solid rgba(22, 163, 74, 0.35)",
      color: "#166534",
    };
  }

  if (normalizedClassification === "probable tbi") {
    return {
      background: "rgba(245, 158, 11, 0.14)",
      border: "1px solid rgba(245, 158, 11, 0.4)",
      color: "#b45309",
    };
  }

  if (normalizedClassification === "likely tbi") {
    return {
      background: "rgba(239, 68, 68, 0.12)",
      border: "1px solid rgba(239, 68, 68, 0.35)",
      color: "#b91c1c",
    };
  }

  return {
    background: "#f8fafc",
    border: "1px solid #cbd5e1",
    color: "#475569",
  };
};

const normalizeSupplementOrderTypeOptions = (optionJson) => {
  const orderTypeEntries = Array.isArray(optionJson?.order_type) ? optionJson.order_type : [];

  return orderTypeEntries.flatMap((entry) => {
    if (!entry || typeof entry !== "object") return [];

    return Object.values(entry)
      .filter((value) => value && typeof value === "object")
      .map((value) => ({
        value: String(value?.value || "").trim(),
        description: String(value?.description || "").trim() || String(value?.value || "").trim(),
        cost: String(value?.cost ?? "").trim(),
      }))
      .filter((value) => value.value);
  });
};

const getCountryCodeFromValue = (countryValue) => {
  const normalizedCountry = String(countryValue || "").trim().toUpperCase();

  if (!normalizedCountry) return "";
  if (normalizedCountry.length === 2) return normalizedCountry;

  const countryCodeMap = {
    "UNITED STATES": "US",
    USA: "US",
    CANADA: "CA",
    MEXICO: "MX",
  };

  return countryCodeMap[normalizedCountry] || normalizedCountry.slice(0, 2);
};

const getAttemptProgressAttemptId = (progressItem) => Number(
  progressItem?.patient_assessment_attempt_id ??
  progressItem?.patient_assessment_attempt?.patient_assessment_attempt_id ??
  progressItem?.patient_assessment_attempt ??
  progressItem?.attempt_id ??
  progressItem?.id ??
  0
);

const normalizeApiRows = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
};

const normalizeStoredAnswerValue = (value) => {
  if (typeof value !== "string") return value;

  const trimmed = value.trim();
  if (!trimmed) return value;

  const looksJson =
    (trimmed.startsWith("{") && trimmed.endsWith("}")) ||
    (trimmed.startsWith("[") && trimmed.endsWith("]"));

  if (!looksJson) return value;

  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
};

const getPatientResponseId = (responseItem) => Number(
  responseItem?.patient_response_id ??
    responseItem?.patient_response?.patient_response_id ??
    responseItem?.response_id ??
    responseItem?.id ??
    0
) || 0;

const getResponseQuestionId = (responseItem) => Number(
  responseItem?.question?.question_id ??
    responseItem?.question_id ??
    responseItem?.question ??
    0
) || 0;

const formatAnswerKeyLabel = (key) =>
  String(key ?? "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const formatAnswerValueForTable = (value) => {
  if (value === null || value === undefined || value === "") return "—";

  if (typeof value === "boolean") return value ? "Yes" : "No";

  if (Array.isArray(value)) {
    if (!value.length) return "—";

    const formattedItems = value
      .map((item) => formatAnswerValueForTable(item))
      .flatMap((item) => Array.isArray(item) ? item : [item])
      .filter((item) => item && item !== "—");

    return formattedItems.length ? formattedItems : "—";
  }

  if (typeof value === "object") {
    const hasOption = Object.prototype.hasOwnProperty.call(value, "option");
    const hasValue = Object.prototype.hasOwnProperty.call(value, "value");

    if (hasOption || hasValue) {
      const optionText = formatAnswerValueForTable(value.option);
      const valueText = formatAnswerValueForTable(value.value);
      const primaryText = optionText !== "—" ? optionText : valueText;
      const subAnswerOptionText = shouldShowStoredSubAnswer(value)
        ? formatAnswerValueForTable(value?.sub_answer?.option)
        : "—";

      if (primaryText === "—") {
        return subAnswerOptionText !== "—" ? `(${subAnswerOptionText})` : "—";
      }

      return subAnswerOptionText !== "—"
        ? `${primaryText} (${subAnswerOptionText})`
        : primaryText;
    }

    const formattedEntries = Object.entries(value)
      .map(([key, nestedValue]) => {
        const formattedValue = formatAnswerValueForTable(nestedValue);
        return formattedValue && formattedValue !== "—"
          ? `${formatAnswerKeyLabel(key)}: ${formattedValue}`
          : null;
      })
      .filter(Boolean);

    return formattedEntries.length ? formattedEntries.join("; ") : "—";
  }

  return String(value);
};

const hasSubAnswerValue = (value) => {
  if (value === null || value === undefined) return false;

  if (Array.isArray(value)) {
    return value.some((item) => hasSubAnswerValue(item));
  }

  if (typeof value === "object") {
    if (value?.sub_answer && typeof value.sub_answer === "object") {
      const subAnswerOption = String(value.sub_answer?.option ?? "").trim();
      if (subAnswerOption) return true;
    }

    return Object.values(value).some((nestedValue) => hasSubAnswerValue(nestedValue));
  }

  return false;
};

const shouldShowStoredSubAnswer = (value) => {
  if (!value || typeof value !== "object") return false;

  const rawMainValue = value?.value;
  const numericMainValue =
    typeof rawMainValue === "number"
      ? (Number.isFinite(rawMainValue) ? rawMainValue : null)
      : typeof rawMainValue === "string"
        ? (() => {
            const trimmed = rawMainValue.trim();
            if (!trimmed) return null;
            const parsed = Number(trimmed);
            return Number.isFinite(parsed) ? parsed : null;
          })()
        : null;

  if (numericMainValue === null || numericMainValue <= 0) {
    return false;
  }

  return String(value?.sub_answer?.option ?? "").trim().length > 0;
};

const getAnswerUrl = (value) => {
  const normalizedValue = String(value ?? "").trim();
  if (!normalizedValue) return "";

  try {
    const parsed = new URL(normalizedValue);
    if (parsed.protocol === "http:" || parsed.protocol === "https:") {
      return parsed.toString();
    }
  } catch {
    return "";
  }

  return "";
};

const renderAnswerItem = (answer, options = {}) => {
  if (answer && typeof answer === "object" && !Array.isArray(answer)) {
    if (answer.kind === "labeled_answer_group") {
      const items = Array.isArray(answer.items) ? answer.items : [];
      return (
        <div className="patient-answers-labeled-group">
          {items.map((item, index) => (
            <div
              key={`${String(item?.label ?? "item")}-${index}`}
              className="patient-answers-labeled-row"
            >
              <span className="patient-answers-labeled-key">{item?.label}:</span>{" "}
              <span className="patient-answers-labeled-value">
                {renderAnswerItem(item?.value, options)}
              </span>
            </div>
          ))}
        </div>
      );
    }

    if (answer.kind === "patient_document_link") {
      const patientDocumentId = Number(answer.patientDocumentId) || 0;
      const isLoading = Number(options.loadingDocumentId) === patientDocumentId;
      return (
        <button
          type="button"
          className="patient-answers-link-button"
          onClick={() => options.onOpenPatientDocument?.(patientDocumentId)}
          disabled={!patientDocumentId || isLoading || !options.onOpenPatientDocument}
        >
          {isLoading ? "Opening..." : "View document"}
        </button>
      );
    }
  }

  const answerUrl = getAnswerUrl(answer);
  if (answerUrl) {
    const linkLabel = /\.pdf(?:\?|#|$)/i.test(answerUrl) ? "Open document" : "Open link";
    return (
      <a
        href={answerUrl}
        target="_blank"
        rel="noreferrer"
        className="patient-answers-link"
      >
        {linkLabel}
      </a>
    );
  }

  return answer;
};

const renderAnswerContent = (answer, options = {}) => {
  if (Array.isArray(answer)) {
    return (
      <ul className="patient-answers-list">
        {answer.map((item, index) => (
          <li key={`${String(item)}-${index}`}>{renderAnswerItem(item, options)}</li>
        ))}
      </ul>
    );
  }

  return renderAnswerItem(answer, options);
};

const isBlankAnswerValue = (value) => {
  if (value === null || value === undefined) return true;

  if (typeof value === "string") {
    return value.trim() === "";
  }

  if (Array.isArray(value)) {
    return value.length === 0 || value.every((item) => isBlankAnswerValue(item));
  }

  if (typeof value === "object") {
    const entries = Object.entries(value);
    return entries.length === 0 || entries.every(([, nestedValue]) => isBlankAnswerValue(nestedValue));
  }

  return false;
};

const getUserTypeId = (user) =>
  Number(user?.user_type_id ?? user?.user_type?.user_type_id ?? user?.user_type?.id ?? 0);

const getPatientCompanyIds = (patient) =>
  (patient?.companies ?? [])
    .map((entry) => Number(entry?.company?.company_id ?? entry?.company_id ?? entry?.company?.id ?? 0))
    .filter((id) => Number.isFinite(id) && id > 0);

const hasPopulatedQuestionControls = (value) => {
  if (Array.isArray(value)) return value.length > 0;
  if (value && typeof value === "object") return Object.keys(value).length > 0;
  return value !== null && value !== undefined && String(value).trim().length > 0;
};

const getQuestionControlItems = (value) => {
  if (Array.isArray(value)) return value;
  if (value && typeof value === "object") return [value];

  if (typeof value === "string" && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : parsed && typeof parsed === "object" ? [parsed] : [];
    } catch {
      return [];
    }
  }

  return [];
};

const questionControlsApplyToAssessment = (value, assessmentId) =>
  getQuestionControlItems(value).some(
    (control) => Number(control?.assessment_id) === Number(assessmentId)
  );

const escapeHtml = (value) => String(value ?? "")
  .replace(/&/g, "&amp;")
  .replace(/</g, "&lt;")
  .replace(/>/g, "&gt;")
  .replace(/"/g, "&quot;")
  .replace(/'/g, "&#39;");

const buildAssessmentLinkEmailHtml = ({
  firstName,
  companyName,
  assessmentLink,
  languageCode = "en",
}) => {
  const isSpanish = String(languageCode || "en").trim().toLowerCase() === "es";
  const safeFirstName = escapeHtml(firstName || (isSpanish ? "paciente" : "there"));
  const safeCompanyName = escapeHtml(
    companyName || (isSpanish ? "su equipo de atención" : "your care team")
  );
  const isValhallaHealthCompany = String(companyName || "")
    .trim()
    .toLowerCase() === "valhalla health";
  const safeAssessmentLink = escapeHtml(assessmentLink || "");
  const copy = isSpanish
    ? {
        documentLanguage: "es",
        heading: "Su evaluación CogniTrackX está lista",
        actionRequired: "Acción requerida",
        greeting: `Hola ${safeFirstName},`,
        ready: "Su evaluación CogniTrackX está lista.",
        description: "CogniTrackX es una herramienta estandarizada de detección cognitiva utilizada por Valhalla Health para establecer una línea de base objetiva de su función cognitiva y neurológica. Este es un paso importante para documentar el impacto total de su lesión: los resultados pasan a formar parte de su expediente médico y se ponen a disposición de su equipo de atención y de su abogado.",
        completeHere: "Puede completar su evaluación aquí:",
        startAssessment: "Iniciar su evaluación",
        remindersHeading: "Algunas cosas que debe tener en cuenta antes de comenzar:",
        reminders: [
          "Busque un lugar tranquilo y sin distracciones",
          "Complete la evaluación en una sola sesión",
          "Responda con sinceridad; no hay respuestas correctas o incorrectas",
          "La evaluación tarda aproximadamente entre 5 y 8 minutos",
        ],
        results: "Sus resultados se procesarán y se incorporarán a su expediente a la brevedad. Si tiene alguna pregunta antes o después de completar su evaluación, nuestro equipo está disponible para ayudarle.",
        regards: "Saludos cordiales",
        association: "en asociación con",
      }
    : {
        documentLanguage: "en",
        heading: "Your CogniTrackX Assessment Is Ready",
        actionRequired: "Action Required",
        greeting: `Hi ${safeFirstName},`,
        ready: "Your CogniTrackX assessment is ready.",
        description: "CogniTrackX is a standardized cognitive screening tool used by Valhalla Health to establish an objective baseline of your cognitive and neurological function. This is an important step in documenting the full impact of your injury - the results become part of your medical record and are made available to your care team and your attorney.",
        completeHere: "You can complete your assessment here:",
        startAssessment: "Start Your Assessment",
        remindersHeading: "A few things to keep in mind before you begin:",
        reminders: [
          "Find a quiet space with no distractions",
          "Complete the assessment in one sitting",
          "Answer honestly - there are no right or wrong responses",
          "The assessment takes approximately 5-8 minutes",
        ],
        results: "Your results will be processed and delivered to your file promptly. If you have any questions before or after completing your assessment, our team is available to assist you.",
        regards: "Warm regards",
        association: "in association with",
      };

  return `<!DOCTYPE html>
<html lang="${copy.documentLanguage}">
  <body style="margin:0; padding:0; background-color:#f4f7fb; font-family:Arial, Helvetica, sans-serif; color:#1f2937;">
    <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background-color:#f4f7fb; margin:0; padding:24px 0;">
      <tr>
        <td align="center">
          <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="max-width:640px; background-color:#ffffff; border-radius:12px; overflow:hidden; box-shadow:0 4px 18px rgba(15, 23, 42, 0.08);">
            <tr>
              <td style="background-color:#0f172a; padding:28px 36px; text-align:center;">
                <h1 style="margin:0; font-size:24px; line-height:1.3; color:#ffffff; font-weight:700;">
                  ${copy.heading}
                </h1>
                <p style="margin:10px 0 0; font-size:14px; color:#cbd5e1;">
                  ${copy.actionRequired}
                </p>
              </td>
            </tr>
            <tr>
              <td style="padding:36px;">
                <p style="margin:0 0 20px; font-size:16px; line-height:1.6;">${copy.greeting}</p>
                <p style="margin:0 0 16px; font-size:16px; line-height:1.7;">${copy.ready}</p>
                <p style="margin:0 0 16px; font-size:16px; line-height:1.7;">${copy.description}</p>
                <p style="margin:0 0 18px; font-size:16px; line-height:1.7;">${copy.completeHere}</p>
                <table role="presentation" cellpadding="0" cellspacing="0" border="0" style="margin:0 0 28px;">
                  <tr>
                    <td align="center" style="border-radius:8px; background-color:#2563eb;">
                      <a href="${safeAssessmentLink}" style="display:inline-block; padding:14px 24px; font-size:16px; font-weight:700; color:#ffffff; text-decoration:none;">${copy.startAssessment}</a>
                    </td>
                  </tr>
                </table>
                <div style="margin:0 0 24px; padding:20px; background-color:#f8fafc; border:1px solid #e2e8f0; border-radius:10px;">
                  <p style="margin:0 0 12px; font-size:16px; font-weight:700; color:#0f172a;">${copy.remindersHeading}</p>
                  <ul style="margin:0; padding-left:20px; font-size:15px; line-height:1.8; color:#334155;">
                    ${copy.reminders.map((reminder) => `<li>${reminder}</li>`).join("")}
                  </ul>
                </div>
                <p style="margin:0 0 16px; font-size:16px; line-height:1.7;">${copy.results}</p>
                <p style="margin:28px 0 0; font-size:16px; line-height:1.7;">${copy.regards},<br />${isValhallaHealthCompany
                  ? "<strong>Valhalla Health</strong>"
                  : `<strong>Valhalla Health</strong> ${copy.association} <strong>${safeCompanyName}</strong>`}</p>
              </td>
            </tr>
          </table>
        </td>
      </tr>
    </table>
  </body>
</html>`;
};

const Patients = () => {
  const { user } = useOutletContext() || {};
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [companyFilterOptions, setCompanyFilterOptions] = useState([]);
  const [companyFilterId, setCompanyFilterId] = useState("");
  const [sortField, setSortField] = useState("patient_id");
  const [sortDirection, setSortDirection] = useState("asc");
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [modalMode, setModalMode] = useState(null); // "view" | "edit"
  const [assessmentPatient, setAssessmentPatient] = useState(null);
  const [patientTableScrollWidth, setPatientTableScrollWidth] = useState(0);
  const patientTableTopScrollRef = useRef(null);
  const patientTableShellRef = useRef(null);

  const userTypeId = getUserTypeId(user);
  const selectedCompany = getSelectedCompany();
  const useClientTerminology = shouldUseClientTerminology(user, selectedCompany);
  const patientLabels = getPatientLabels(useClientTerminology);
  const selectedCompanyId = Number(
    selectedCompany?.company_id ?? selectedCompany?.id ?? selectedCompany?.company?.company_id ?? 0
  );
  const isCorporateAdmin = userTypeId === 3;
  const isCompanyRestrictedUser = userTypeId === 1 || userTypeId === 2;

  const loadPatients = useCallback(async () => {
    setLoading(true);
    try {
      const withSlashRes = await apiRequest(PATIENTS_VIEW_API_URL);
      if (withSlashRes.ok) {
        const data = await withSlashRes.json();
        setPatients(Array.isArray(data) ? data : []);
        return;
      }

      const noSlashRes = await apiRequest(PATIENTS_VIEW_API_URL.replace(/\/$/, ""));
      if (!noSlashRes.ok) {
        throw new Error(`Patients view request failed (${noSlashRes.status})`);
      }

      const fallbackData = await noSlashRes.json();
      setPatients(Array.isArray(fallbackData) ? fallbackData : []);
    } catch (error) {
      console.error("Failed to load patients view", error);
      setPatients([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPatients();
  }, [loadPatients]);

  useEffect(() => {
    const tableShell = patientTableShellRef.current;
    if (!tableShell) return undefined;

    const updateScrollWidth = () => {
      setPatientTableScrollWidth(tableShell.scrollWidth);
    };

    updateScrollWidth();
    const resizeObserver = new ResizeObserver(updateScrollWidth);
    resizeObserver.observe(tableShell);
    const table = tableShell.querySelector("table");
    if (table) resizeObserver.observe(table);

    return () => resizeObserver.disconnect();
  }, []);

  const syncPatientTableFromTop = (event) => {
    if (patientTableShellRef.current) {
      patientTableShellRef.current.scrollLeft = event.currentTarget.scrollLeft;
    }
  };

  const syncPatientTableFromBottom = (event) => {
    if (patientTableTopScrollRef.current) {
      patientTableTopScrollRef.current.scrollLeft = event.currentTarget.scrollLeft;
    }
  };

  useEffect(() => {
    if (!isCorporateAdmin) {
      setCompanyFilterOptions([]);
      setCompanyFilterId("");
      return;
    }

    const loadCompanyFilterOptions = async () => {
      try {
        const response = await apiRequest(COMPANIES_API);
        if (!response.ok) {
          throw new Error(`Companies request failed (${response.status})`);
        }

        const companyRows = await response.json();
        setCompanyFilterOptions(
          (Array.isArray(companyRows) ? companyRows : [])
            .filter((company) => Number(company?.company_id ?? company?.id ?? 0) > 0)
            .sort((left, right) =>
              String(left?.company_name ?? left?.name ?? "").localeCompare(
                String(right?.company_name ?? right?.name ?? ""),
                undefined,
                { sensitivity: "base" }
              )
            )
        );
      } catch (error) {
        console.error("Failed to load company filter options", error);
        setCompanyFilterOptions([]);
      }
    };

    loadCompanyFilterOptions();
  }, [isCorporateAdmin]);

  /* ----------------------------------
     Helpers
  ---------------------------------- */

  const getCompanyNames = useCallback(
    (p) =>
      p.companies
        ?.map((c) => c?.company?.company_name ?? c?.company_name ?? "")
        .filter(Boolean)
        .join(", ") || "",
    []
  );

  const matchesSearch = useCallback(
    (p) => {
      const normalizeSearchableText = (value) => String(value ?? "").trim().toLowerCase();
      const searchText = normalizeSearchableText(search);

      return (
        String(p?.patient_id ?? "").includes(searchText) ||
        normalizeSearchableText(p?.company_patient_id).includes(searchText) ||
        normalizeSearchableText(p?.first_name).includes(searchText) ||
        normalizeSearchableText(p?.last_name).includes(searchText) ||
        normalizeSearchableText(p?.email).includes(searchText) ||
        normalizeSearchableText(getCompanyNames(p)).includes(searchText) ||
        normalizeSearchableText(p?.created_on).includes(searchText)
      );
    },
    [search, getCompanyNames]
  );

  const sortedPatients = useMemo(() => {
    return [...patients]
      .filter((patient) => {
        const patientCompanyIds = getPatientCompanyIds(patient);

        if (isCompanyRestrictedUser) {
          if (!selectedCompanyId) return false;
          return patientCompanyIds.includes(selectedCompanyId);
        }

        const selectedFilterId = Number(companyFilterId);
        if (isCorporateAdmin && selectedFilterId > 0) {
          return patientCompanyIds.includes(selectedFilterId);
        }

        return true;
      })
      .filter(matchesSearch)
      .sort((a, b) => {
        const normalizeText = (value) => String(value ?? "").trim().toLowerCase();
        const compareValues = (leftValue, rightValue) => {
          if (leftValue < rightValue) return sortDirection === "asc" ? -1 : 1;
          if (leftValue > rightValue) return sortDirection === "asc" ? 1 : -1;
          return 0;
        };

        if (sortField === "company") {
          return compareValues(
            normalizeText(getCompanyNames(a)),
            normalizeText(getCompanyNames(b))
          );
        }

        if (sortField === "created_on") {
          return compareValues(
            new Date(a.created_on).getTime() || 0,
            new Date(b.created_on).getTime() || 0
          );
        }

        if (sortField === "latest_assessment_status") {
          return compareValues(
            normalizeText(a.latest_assessment_status),
            normalizeText(b.latest_assessment_status)
          );
        }

        if (sortField === "company_patient_id") {
          const comparison = normalizeText(a.company_patient_id).localeCompare(
            normalizeText(b.company_patient_id),
            undefined,
            { numeric: true, sensitivity: "base" }
          );
          return sortDirection === "asc" ? comparison : -comparison;
        }

        if (sortField === "last_name") {
          const primaryResult = compareValues(
            normalizeText(a.last_name),
            normalizeText(b.last_name)
          );
          if (primaryResult !== 0) return primaryResult;

          return compareValues(
            normalizeText(a.first_name),
            normalizeText(b.first_name)
          );
        }

        if (sortField === "first_name") {
          const primaryResult = compareValues(
            normalizeText(a.first_name),
            normalizeText(b.first_name)
          );
          if (primaryResult !== 0) return primaryResult;

          return compareValues(
            normalizeText(a.last_name),
            normalizeText(b.last_name)
          );
        }

        const valA = a[sortField];
        const valB = b[sortField];

        if (valA < valB) return sortDirection === "asc" ? -1 : 1;
        if (valA > valB) return sortDirection === "asc" ? 1 : -1;
        return 0;
      });
  }, [
    patients,
    sortField,
    sortDirection,
    matchesSearch,
    getCompanyNames,
    companyFilterId,
    isCorporateAdmin,
    isCompanyRestrictedUser,
    selectedCompanyId,
  ]);

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const renderSortCaret = (field) => {
    const isActive = sortField === field;
    if (!isActive) return null;

    return (
      <span className="patients-sort-caret is-active" aria-hidden="true">
        {sortDirection === "asc" ? "▲" : "▼"}
      </span>
    );
  };

  /* ----------------------------------
     Render
  ---------------------------------- */

  return (
    <div className="patients-page">
      <header className="portal-page-header">
        <div>
          <p className="portal-page-kicker">Patient operations</p>
          <h1 className="portal-page-title">
            {patientLabels.plural}
            <span className="portal-page-count">{sortedPatients.length}</span>
          </h1>
          <p className="portal-page-subtitle">Manage patient records and assessment activity.</p>
        </div>
        <div className="portal-page-actions">
          <button
            className="assessments-action-btn portal-primary-action"
            onClick={() => {
              setSelectedPatient(null);
              setModalMode("add");
            }}
          >
            + Add New {patientLabels.singular}
          </button>
        </div>
      </header>

        <div className="patients-toolbar portal-command-bar">
          <label className="portal-search-field" htmlFor="patients-search">
            <span>Search</span>
            <input
                id="patients-search"
                className="search-bar portal-search-input"
                placeholder={`Search ${patientLabels.pluralLower}...`}
                aria-label={`Search ${patientLabels.pluralLower}`}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
            />
          </label>
            {isCorporateAdmin && (
              <div className="patients-company-filter-group">
                <label className="patients-company-filter">
                  <span>Filter by Company</span>
                  <select
                    value={companyFilterId}
                    onChange={(event) => setCompanyFilterId(event.target.value)}
                  >
                    <option value="">All</option>
                    {companyFilterOptions.map((company) => {
                      const companyId = company?.company_id ?? company?.id;
                      return (
                        <option key={companyId} value={companyId}>
                          {company?.company_name ?? company?.name ?? `Company ${companyId}`}
                        </option>
                      );
                    })}
                  </select>
                </label>
                <button
                  type="button"
                  className="patients-refresh-btn"
                  onClick={loadPatients}
                  disabled={loading}
                  aria-label={`Refresh ${patientLabels.pluralLower}`}
                  title={`Refresh ${patientLabels.pluralLower}`}
                >
                  <FaSyncAlt className={loading ? "spin" : ""} aria-hidden="true" />
                </button>
              </div>
            )}
        </div>

      <div
        ref={patientTableTopScrollRef}
        className="patients-table-top-scroll"
        onScroll={syncPatientTableFromTop}
        role="region"
        aria-label={`Scroll ${patientLabels.pluralLower} table horizontally`}
        tabIndex={0}
      >
        <div style={{ width: patientTableScrollWidth, height: 1 }} aria-hidden="true" />
      </div>
      <div
        ref={patientTableShellRef}
        className="portal-table-shell"
        onScroll={syncPatientTableFromBottom}
      >
      <table className="patients-table">
        <thead>
          <tr>
            <th onClick={() => toggleSort("patient_id")}>
              <span className="patients-sort-header">
                <span>VH ID</span>
                {renderSortCaret("patient_id")}
              </span>
            </th>
            <th onClick={() => toggleSort("company_patient_id")}>
              <span className="patients-sort-header">
                <span>Company ID</span>
                {renderSortCaret("company_patient_id")}
              </span>
            </th>
            <th onClick={() => toggleSort("first_name")}>
              <span className="patients-sort-header">
                <span>First Name</span>
                {renderSortCaret("first_name")}
              </span>
            </th>
            <th onClick={() => toggleSort("last_name")}>
              <span className="patients-sort-header">
                <span>Last Name</span>
                {renderSortCaret("last_name")}
              </span>
            </th>
            <th>Email</th>
            <th onClick={() => toggleSort("company")}>
              <span className="patients-sort-header">
                <span>Company</span>
                {renderSortCaret("company")}
              </span>
            </th>
            <th onClick={() => toggleSort("created_on")}>
              <span className="patients-sort-header">
                <span>Created</span>
                {renderSortCaret("created_on")}
              </span>
            </th>
            <th>Assessments</th>
            <th>Latest Assessment</th>
            <th onClick={() => toggleSort("latest_assessment_status")}>
              <span className="patients-sort-header">
                <span>Status</span>
                {renderSortCaret("latest_assessment_status")}
              </span>
            </th>
            <th>{patientLabels.singular} Info</th>
          </tr>
        </thead>

        <tbody>
          {loading ? (
            Array.from({ length: 6 }).map((_, index) => (
              <tr key={`patient-skeleton-${index}`} className="patients-table-skeleton-row" aria-hidden="true">
                <td><div className="skeleton-line patients-skeleton-id" /></td>
                <td><div className="skeleton-line patients-skeleton-id" /></td>
                <td><div className="skeleton-line patients-skeleton-name" /></td>
                <td><div className="skeleton-line patients-skeleton-name" /></td>
                <td><div className="skeleton-line patients-skeleton-email" /></td>
                <td><div className="skeleton-line patients-skeleton-company" /></td>
                <td><div className="skeleton-line patients-skeleton-date" /></td>
                <td><div className="skeleton-line patients-skeleton-manage" /></td>
                <td><div className="skeleton-line patients-skeleton-name" /></td>
                <td><div className="skeleton-line patients-skeleton-date" /></td>
                <td><div className="skeleton-line patients-skeleton-actions" /></td>
              </tr>
            ))
          ) : sortedPatients.length === 0 ? (
            <tr>
              <td colSpan={11}>
                <div className="people-empty">No {patientLabels.pluralLower} found.</div>
              </td>
            </tr>
          ) : (
            sortedPatients.map((p) => {
              const latestAssessmentStatus = LATEST_ASSESSMENT_STATUSES[
                String(p.latest_assessment_status ?? "").toLowerCase()
              ];

              return (
                <tr
                  key={p.patient_id}
                  onClick={() => {
                    setSelectedPatient(p);
                    setModalMode("view");
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <td>{p.patient_id}</td>
                  <td>
                    <span className="patient-company-id-with-test-badge">
                      <span>{p.company_patient_id || "—"}</span>
                      {p.test_mode === true && (
                        <span className="patient-test-mode-badge" title="Test patient">
                          DEV
                        </span>
                      )}
                    </span>
                  </td>
                  <td>{p.first_name}</td>
                  <td>{p.last_name}</td>
                  <td>{p.email}</td>
                  <td>{getCompanyNames(p)}</td>
                  <td>{new Date(p.created_on).toLocaleDateString()}</td>
                  <td>
                    <button
                      className="assessments-action-btn"
                      title="Assessments"
                      onClick={(event) => {
                        event.stopPropagation();
                        setAssessmentPatient(p);
                      }}
                    >
                      Manage
                    </button>
                  </td>
                  <td>{formatLatestAssessmentCodename(p.latest_assessment_codename)}</td>
                  <td>
                    {latestAssessmentStatus ? (
                      <span
                        role="button"
                        tabIndex={0}
                        className={`latest-assessment-status-pill ${latestAssessmentStatus.className}`}
                        title="Open assessments"
                        onClick={(event) => {
                          event.stopPropagation();
                          setAssessmentPatient(p);
                        }}
                        onKeyDown={(event) => {
                          if (event.key !== "Enter" && event.key !== " ") return;
                          event.preventDefault();
                          event.stopPropagation();
                          setAssessmentPatient(p);
                        }}
                      >
                        {latestAssessmentStatus.label}
                      </span>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="actions">
                    <button
                      type="button"
                      className="patient-row-icon-btn patient-row-view-icon-btn"
                      title="View Details"
                      aria-label="View details"
                      onClick={(event) => {
                        event.stopPropagation();
                        setSelectedPatient(p);
                        setModalMode("view");
                      }}
                    >
                      <Eye strokeWidth={1.75} />
                    </button>
                    <button
                      type="button"
                      className="patient-row-icon-btn patient-row-edit-icon-btn"
                      title={`Edit ${patientLabels.singular}`}
                      aria-label={`Edit ${patientLabels.singularLower}`}
                      onClick={(event) => {
                        event.stopPropagation();
                        setSelectedPatient(p);
                        setModalMode("edit");
                      }}
                    >
                      <Pencil strokeWidth={1.75} />
                    </button>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
      </div>

      {modalMode && (
        modalMode === "add" ? (
            <AddPatientModal
            restrictedCompanyId={isCompanyRestrictedUser ? selectedCompanyId : null}
            canManageTestMode={userTypeId > 1}
            onClose={() => setModalMode(null)}
            onCreated={(newPatient) => {
                setPatients((prev) => [...prev, newPatient]);
                setModalMode(null);
            }}
            />
        ) : (
            <PatientModal
            patient={selectedPatient}
            mode={modalMode}
            canManageTestMode={userTypeId > 1}
            onClose={() => setModalMode(null)}
            onUpdated={(updatedPatient) => {
              setPatients((prev) =>
                prev.map((p) =>
                  p.patient_id === updatedPatient.patient_id ? updatedPatient : p
                )
              );
              setSelectedPatient(updatedPatient);
              setModalMode(null);
            }}
            />
        )
        )}

      {assessmentPatient && (
        <PatientAssessmentsModal
          patient={assessmentPatient}
          userTypeId={userTypeId}
          onClose={() => setAssessmentPatient(null)}
        />
      )}
    </div>
  );
};

export default Patients;

/* ----------------------------------
   Modal Component
---------------------------------- */

const PatientModal = ({ patient, mode, canManageTestMode, onClose, onUpdated }) => {
  const isEdit = mode === "edit";

  const [firstName, setFirstName] = useState(patient?.first_name || "");
  const [middleName, setMiddleName] = useState(patient?.middle_name || "");
  const [lastName, setLastName] = useState(patient?.last_name || "");
  const [email, setEmail] = useState(patient?.email || "");
  const [dob, setDob] = useState(patient?.dob || "");
  const [patientTypeId, setPatientTypeId] = useState(
    patient?.patient_type?.patient_type_id || ""
  );
  const [patientTypes, setPatientTypes] = useState([]);
  const [isActive, setIsActive] = useState(
    patient ? patient.is_active : false
  );
  const [testMode, setTestMode] = useState(Boolean(patient?.test_mode));
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState(
    String(
      patient?.companies?.[0]?.company?.company_id ??
      patient?.companies?.[0]?.company_id ??
      ""
    )
  );
  const [companyPatientId, setCompanyPatientId] = useState(
    patient?.company_patient_id ?? ""
  );
  const [languages, setLanguages] = useState([]);
  const [preferredLanguageCode, setPreferredLanguageCode] = useState(
    patient?.preferred_language?.code ?? ""
  );
  const [patientPeople, setPatientPeople] = useState([]);
  const [peopleLoading, setPeopleLoading] = useState(false);
  const [showPersonModal, setShowPersonModal] = useState(false);
  const [removingLinkId, setRemovingLinkId] = useState(null);
  const [patientEvents, setPatientEvents] = useState([]);
  const [patientOrders, setPatientOrders] = useState([]);
  const [injuryEventTypes, setInjuryEventTypes] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [showEventModal, setShowEventModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [savingEvent, setSavingEvent] = useState(false);
  const [isDeleteEventOpen, setIsDeleteEventOpen] = useState(false);
  const [deletingEvent, setDeletingEvent] = useState(null);
  const [deletingEventInProgress, setDeletingEventInProgress] = useState(false);
  const [saving, setSaving] = useState(false);
  const [phones, setPhones] = useState(patient?.phones || []);
  const [initialPersistedPhoneIds, setInitialPersistedPhoneIds] = useState([]);
  const [addresses, setAddresses] = useState(patient?.addresses || []);
  const [phoneTypes, setPhoneTypes] = useState([]);
  const [addressTypes, setAddressTypes] = useState([]);
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [editingPhoneKey, setEditingPhoneKey] = useState(null);
  const [isDeletePhoneOpen, setIsDeletePhoneOpen] = useState(false);
  const [deletingPhoneKey, setDeletingPhoneKey] = useState(null);
  const [phoneError, setPhoneError] = useState("");
  const [newPhoneData, setNewPhoneData] = useState({
    phone: "",
    phone_type_id: "",
  });
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [editingAddressKey, setEditingAddressKey] = useState(null);
  const [isDeleteAddressOpen, setIsDeleteAddressOpen] = useState(false);
  const [deletingAddressKey, setDeletingAddressKey] = useState(null);
  const [addressError, setAddressError] = useState("");
  const [showSupplementOrderModal, setShowSupplementOrderModal] = useState(false);
  const [selectedShippingAddressKey, setSelectedShippingAddressKey] = useState("");
  const [selectedShippingPhoneKey, setSelectedShippingPhoneKey] = useState("");
  const [supplementProductOptions, setSupplementProductOptions] = useState([]);
  const [supplementOrderTypeOptions, setSupplementOrderTypeOptions] = useState([]);
  const [supplementOptionsLoading, setSupplementOptionsLoading] = useState(false);
  const [supplementOptionsError, setSupplementOptionsError] = useState("");
  const [selectedSupplementProduct, setSelectedSupplementProduct] = useState("");
  const [selectedSupplementOrderType, setSelectedSupplementOrderType] = useState("");
  const [selectedSupplementOrderTypeCost, setSelectedSupplementOrderTypeCost] = useState("");
  const [supplementOrderSaveError, setSupplementOrderSaveError] = useState("");
  const [supplementOrderSubmitting, setSupplementOrderSubmitting] = useState(false);
  const [showSupplementOrderPayloadModal, setShowSupplementOrderPayloadModal] = useState(false);
  const [supplementOrderPayloadPreview, setSupplementOrderPayloadPreview] = useState("");
  const [isPartnersSectionOpen, setIsPartnersSectionOpen] = useState(false);
  const [isEventsSectionOpen, setIsEventsSectionOpen] = useState(false);
  const [isAddressesSectionOpen, setIsAddressesSectionOpen] = useState(false);
  const [isOrdersSectionOpen, setIsOrdersSectionOpen] = useState(false);
  const [isPhonesSectionOpen, setIsPhonesSectionOpen] = useState(true);
  const [newAddressData, setNewAddressData] = useState({
    street_1: "",
    street_2: "",
    city: "",
    st: "",
    zip: "",
    country: "United States",
    address_type_id: "",
  });

  const patientCompanyId = Number(
    patient?.companies?.[0]?.company?.company_id ??
    patient?.companies?.[0]?.company_id ??
    0
  );

  useEffect(() => {
    if (patient) {
      setFirstName(patient.first_name || "");
      setMiddleName(patient.middle_name || "");
      setLastName(patient.last_name || "");
      setEmail(patient.email || "");
      setDob(patient.dob || "");
      setPatientTypeId(patient.patient_type?.patient_type_id || "");
      setIsActive(patient.is_active);
      setTestMode(Boolean(patient.test_mode));
      setPhones(Array.isArray(patient.phones) ? patient.phones : []);
      setInitialPersistedPhoneIds(
        Array.from(
          new Set(
            (Array.isArray(patient.phones) ? patient.phones : [])
              .map(getPersistedPhoneId)
              .filter((id) => Number.isFinite(Number(id)) && Number(id) > 0)
              .map((id) => Number(id))
          )
        )
      );
      setAddresses(Array.isArray(patient.addresses) ? patient.addresses : []);
      setCompanyId(String(patientCompanyId || ""));
      setCompanyPatientId(patient?.company_patient_id ?? "");
      setPreferredLanguageCode(patient?.preferred_language?.code ?? "");
    }
  }, [patient, patientCompanyId]);

  useEffect(() => {
    const firstAddressKey = getAddressRowKey(addresses[0]);
    setSelectedShippingAddressKey(firstAddressKey ? String(firstAddressKey) : "");
  }, [addresses]);

  useEffect(() => {
    const firstPhoneKey = getPhoneRowKey(phones[0]);
    setSelectedShippingPhoneKey(firstPhoneKey ? String(firstPhoneKey) : "");
  }, [phones]);

  useEffect(() => {
    if (!showSupplementOrderModal) return;

    let cancelled = false;

    const loadSupplementOptions = async () => {
      setSupplementOptionsLoading(true);
      setSupplementOptionsError("");

      try {
        const response = await apiRequest(`${INTEGRATION_OPTIONS_API}?integration_type_id=2`);
        if (!response.ok) {
          throw new Error(`Integration options request failed with status ${response.status}`);
        }

        const payload = await response.json();
        const options = Array.isArray(payload) ? payload : [];

        if (cancelled) return;

        setSupplementProductOptions(options);

        if (options.length === 1) {
          const selectedOptionValue = String(options[0]?.option_value || "").trim();
          const orderTypeOptions = normalizeSupplementOrderTypeOptions(options[0]?.option_json);
          setSelectedSupplementProduct(selectedOptionValue);
          setSupplementOrderTypeOptions(orderTypeOptions);
          setSelectedSupplementOrderType(orderTypeOptions[0]?.value || "");
          setSelectedSupplementOrderTypeCost(orderTypeOptions[0]?.cost || "");
        } else {
          setSelectedSupplementProduct("");
          setSupplementOrderTypeOptions([]);
          setSelectedSupplementOrderType("");
          setSelectedSupplementOrderTypeCost("");
        }
      } catch (error) {
        if (cancelled) return;
        setSupplementProductOptions([]);
        setSupplementOrderTypeOptions([]);
        setSelectedSupplementProduct("");
        setSelectedSupplementOrderType("");
        setSelectedSupplementOrderTypeCost("");
        setSupplementOptionsError(error?.message || "Failed to load supplement options.");
      } finally {
        if (!cancelled) {
          setSupplementOptionsLoading(false);
        }
      }
    };

    loadSupplementOptions();

    return () => {
      cancelled = true;
    };
  }, [showSupplementOrderModal]);

  useEffect(() => {
    const selectedOption = supplementProductOptions.find(
      (option) => String(option?.option_value || "").trim() === selectedSupplementProduct
    );

    const nextOrderTypeOptions = normalizeSupplementOrderTypeOptions(selectedOption?.option_json);
    setSupplementOrderTypeOptions(nextOrderTypeOptions);
    setSelectedSupplementOrderType((currentValue) => {
      if (nextOrderTypeOptions.some((option) => option.value === currentValue)) {
        return currentValue;
      }
      return nextOrderTypeOptions[0]?.value || "";
    });
  }, [selectedSupplementProduct, supplementProductOptions]);

  useEffect(() => {
    const selectedOrderTypeOption = supplementOrderTypeOptions.find(
      (option) => option.value === selectedSupplementOrderType
    );

    setSelectedSupplementOrderTypeCost(selectedOrderTypeOption?.cost || "");
  }, [selectedSupplementOrderType, supplementOrderTypeOptions]);

  useEffect(() => {
    setIsPhonesSectionOpen(true);
    setIsPartnersSectionOpen(false);
    setIsEventsSectionOpen(false);
    setIsAddressesSectionOpen(false);
    setIsOrdersSectionOpen(false);
  }, [patient?.patient_id, mode]);

  useEffect(() => {
    Promise.all([
      apiRequest(PATIENT_TYPES_API).then((res) => res.json()),
      apiRequest(PHONE_TYPES_API).then((res) => res.json()),
      apiRequest(ADDRESS_TYPES_API).then((res) => res.json()),
      apiRequest(INJURY_EVENT_TYPES_API).then((res) => res.json()),
      apiRequest(COMPANIES_API).then((res) => res.json()),
    ])
      .then(([patientTypeRows, phoneTypeRows, addressTypeRows, injuryEventTypeRows, companyRows]) => {
        setPatientTypes(patientTypeRows);
        setPhoneTypes(phoneTypeRows);
        setAddressTypes(addressTypeRows);
        setInjuryEventTypes(injuryEventTypeRows);
        setCompanies(Array.isArray(companyRows) ? companyRows : []);
      })
      .catch(console.error);

    apiRequest(LANGUAGES_API)
      .then((res) => res.json())
      .then((languageRows) => setLanguages(Array.isArray(languageRows) ? languageRows : []))
      .catch((error) => {
        console.error("Load languages failed", error);
        setLanguages([]);
      });
  }, []);

  const loadPatientPeople = useCallback(async () => {
    if (!patient?.patient_id) return;

    setPeopleLoading(true);
    try {
      const res = await apiRequest(
        `${PATIENT_PEOPLE_API}?patient=${encodeURIComponent(patient.patient_id)}`
      );
      const payload = await res.json();
      const linksForPatient = (Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.results)
          ? payload.results
          : [])
        .map((pp) => {
          const person = getPersonFromRelation(pp);
          return {
            patient_person_id: getPatientPersonId(pp),
            person_id: getPersonIdFromPatientPerson(pp),
            first_name: person?.first_name || "",
            last_name: person?.last_name || "",
            email: person?.email || "",
            phone: person?.phone || "",
            person_type_description: getPersonTypeDescription(person),
          };
        });

      setPatientPeople(linksForPatient);
    } catch (err) {
      console.error("Load patient people failed", err);
      setPatientPeople([]);
    } finally {
      setPeopleLoading(false);
    }
  }, [patient]);

  useEffect(() => {
    loadPatientPeople();
  }, [loadPatientPeople]);

  const loadPatientEvents = useCallback(async () => {
    if (!patient?.patient_id) return;

    setEventsLoading(true);
    try {
      const res = await apiRequest(
        `${PATIENT_EVENTS_API}?patient=${encodeURIComponent(patient.patient_id)}`
      );
      const payload = await res.json();
      const eventsForPatient = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.results)
          ? payload.results
          : [];

      setPatientEvents(eventsForPatient);
    } catch (err) {
      console.error("Load patient events failed", err);
      setPatientEvents([]);
    } finally {
      setEventsLoading(false);
    }
  }, [patient]);

  useEffect(() => {
    loadPatientEvents();
  }, [loadPatientEvents]);

  const loadPatientOrders = useCallback(async () => {
    if (!patient?.patient_id) return;

    setOrdersLoading(true);
    try {
      const res = await apiRequest(
        `${PATIENT_ORDERS_API}?patient=${encodeURIComponent(patient.patient_id)}`
      );
      const payload = await res.json();
      const ordersForPatient = (Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.results)
          ? payload.results
          : [])
        .slice()
        .sort((left, right) => {
          const leftTime = new Date(left?.order_date || 0).getTime();
          const rightTime = new Date(right?.order_date || 0).getTime();
          return rightTime - leftTime;
        });

      setPatientOrders(ordersForPatient);
    } catch (err) {
      console.error("Load patient orders failed", err);
      setPatientOrders([]);
    } finally {
      setOrdersLoading(false);
    }
  }, [patient]);

  useEffect(() => {
    loadPatientOrders();
  }, [loadPatientOrders]);

  const loadPatientPhonesAndAddresses = useCallback(async () => {
    if (!patient?.patient_id) return;

    try {
      const patientQuery = `?patient=${encodeURIComponent(patient.patient_id)}`;
      const [phoneRes, addressRes] = await Promise.all([
        apiRequest(`${PATIENT_PHONES_API}${patientQuery}`),
        apiRequest(`${PATIENT_ADDRESSES_API}${patientQuery}`),
      ]);

      const phonePayload = await phoneRes.json();
      const addressPayload = await addressRes.json();

      const phoneRows = Array.isArray(phonePayload)
        ? phonePayload
        : Array.isArray(phonePayload?.results)
          ? phonePayload.results
          : [];

      const addressRows = Array.isArray(addressPayload)
        ? addressPayload
        : Array.isArray(addressPayload?.results)
          ? addressPayload.results
          : [];

      const filteredPhones = phoneRows
        .map((phone) => ({
          ...phone,
          phone: phone?.phone ?? "",
          phone_type: phone?.phone_type ?? {
            phone_type_id: phone?.phone_type_id ?? "",
            description: "",
          },
        }));

      const filteredAddresses = addressRows
        .map((address) => ({
          ...address,
          address_type: address?.address_type ?? {
            address_type_id: address?.address_type_id ?? "",
            description: "",
          },
        }));

      setPhones(filteredPhones);
      setInitialPersistedPhoneIds(
        Array.from(
          new Set(
            filteredPhones
              .map(getPersistedPhoneId)
              .filter((id) => Number.isFinite(Number(id)) && Number(id) > 0)
              .map((id) => Number(id))
          )
        )
      );
      setAddresses(filteredAddresses);
    } catch (error) {
      console.error("Load patient phones/addresses failed", error);
    }
  }, [patient]);

  useEffect(() => {
    loadPatientPhonesAndAddresses();
  }, [loadPatientPhonesAndAddresses]);

  const postPatientLinkedRecord = async (baseUrl, basePayload, patientId) => {
    const relationCandidates = getPatientRelationCandidates(patientId);
    if (relationCandidates.length === 0) {
      throw new Error("Patient ID is required.");
    }

    let lastErrorMessage = "Unknown error";

    for (const relationPayload of relationCandidates) {
      const response = await apiRequest(baseUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...basePayload,
          ...relationPayload,
        }),
      });

      if (response.ok) {
        return response;
      }

      lastErrorMessage = await getResponseErrorMessage(response);
    }

    throw new Error(lastErrorMessage);
  };

  const openAddPhoneModal = () => {
    setEditingPhoneKey(null);
    setNewPhoneData({ phone: "", phone_type_id: "" });
    setPhoneError("");
    setShowPhoneModal(true);
  };

  const openEditPhoneModal = (phone) => {
    setEditingPhoneKey(getPhoneRowKey(phone));
    setNewPhoneData({
      phone: phone?.phone || "",
      phone_type_id: String(phone?.phone_type?.phone_type_id ?? phone?.phone_type_id ?? ""),
    });
    setPhoneError("");
    setShowPhoneModal(true);
  };

  const handleSavePhoneFromModal = () => {
    const phoneDigits = normalizePhoneDigits(newPhoneData.phone);
    const phoneTypeId = Number(newPhoneData.phone_type_id);

    if (!phoneDigits || Number.isNaN(phoneTypeId) || phoneTypeId <= 0) {
      setPhoneError("Phone number and Phone Type are required.");
      return;
    }

    const selectedPhoneType = phoneTypes.find(
      (type) => Number(type.phone_type_id) === phoneTypeId
    );

    const normalizedPhone = {
      phone: phoneDigits,
      phone_type: selectedPhoneType || {
        phone_type_id: phoneTypeId,
        description: "",
      },
    };

    if (editingPhoneKey !== null) {
      setPhones((prev) =>
        prev.map((phone) =>
          getPhoneRowKey(phone) === editingPhoneKey
            ? { ...phone, ...normalizedPhone }
            : phone
        )
      );
    } else {
      setPhones((prev) => [
        ...prev,
        {
          id: Date.now(),
          ...normalizedPhone,
          _isNew: true,
        },
      ]);
    }

    setShowPhoneModal(false);
    setEditingPhoneKey(null);
    setPhoneError("");
  };

  const handleRequestDeletePhone = (phoneKey) => {
    setDeletingPhoneKey(phoneKey);
    setIsDeletePhoneOpen(true);
  };

  const handleConfirmDeletePhone = () => {
    if (deletingPhoneKey === null) return;

    setPhones((prev) => prev.filter((phone) => getPhoneRowKey(phone) !== deletingPhoneKey));
    setDeletingPhoneKey(null);
    setIsDeletePhoneOpen(false);
  };

  const openAddAddressModal = () => {
    setEditingAddressKey(null);
    setNewAddressData({
      street_1: "",
      street_2: "",
      city: "",
      st: "",
      zip: "",
      country: "United States",
      address_type_id: "",
    });
    setAddressError("");
    setShowAddressModal(true);
  };

  const openEditAddressModal = (address) => {
    setEditingAddressKey(getAddressRowKey(address));
    setNewAddressData({
      street_1: address?.street_1 || "",
      street_2: address?.street_2 || "",
      city: address?.city || "",
      st: address?.st || "",
      zip: address?.zip || "",
      country: address?.country || "United States",
      address_type_id: String(
        address?.address_type?.address_type_id ??
        address?.address_type_id ??
        ""
      ),
    });
    setAddressError("");
    setShowAddressModal(true);
  };

  const handleSaveAddressFromModal = async () => {
    const addressTypeId = Number(newAddressData.address_type_id);
    if (!newAddressData.street_1.trim() || Number.isNaN(addressTypeId) || addressTypeId <= 0) {
      setAddressError("Address line 1 and Address Type are required.");
      return;
    }

    const selectedAddressType = addressTypes.find(
      (type) => Number(type.address_type_id) === addressTypeId
    );

    const normalizedAddress = {
      street_1: newAddressData.street_1.trim(),
      street_2: newAddressData.street_2.trim(),
      city: newAddressData.city.trim(),
      st: newAddressData.st.trim(),
      zip: newAddressData.zip.trim(),
      country: newAddressData.country.trim(),
      address_type: selectedAddressType || {
        address_type_id: addressTypeId,
        description: "",
      },
    };

    try {
      if (editingAddressKey !== null) {
        const targetAddress = addresses.find(
          (address) => getAddressRowKey(address) === editingAddressKey
        );
        const persistedAddressId = getPersistedAddressId(targetAddress);

        if (!persistedAddressId) {
          throw new Error("Address ID is missing for update.");
        }

        const relationCandidates = getPatientRelationCandidates(patient.patient_id);
        let updateAddressRes = null;
        let lastAddressUpdateError = "Unknown error";

        for (const relationPayload of relationCandidates) {
          const updatePayload = {
            ...normalizedAddress,
            address_type_id: addressTypeId,
            ...relationPayload,
          };

          updateAddressRes = await apiRequest(
            `${PATIENT_ADDRESSES_API}${persistedAddressId}/`,
            {
              method: "PATCH",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(updatePayload),
            }
          );

          if (!updateAddressRes.ok && updateAddressRes.status === 405) {
            updateAddressRes = await apiRequest(
              `${PATIENT_ADDRESSES_API}${persistedAddressId}/`,
              {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(updatePayload),
              }
            );
          }

          if (updateAddressRes.ok) {
            break;
          }

          lastAddressUpdateError = await getResponseErrorMessage(updateAddressRes);
        }

        if (!updateAddressRes || !updateAddressRes.ok) {
          throw new Error(`Address update failed: ${lastAddressUpdateError}`);
        }

        await loadPatientPhonesAndAddresses();
      } else {
        await postPatientLinkedRecord(
          PATIENT_ADDRESSES_API,
          {
            ...normalizedAddress,
            address_type_id: addressTypeId,
          },
          patient.patient_id
        );

        await loadPatientPhonesAndAddresses();
      }

      setShowAddressModal(false);
      setEditingAddressKey(null);
      setAddressError("");
    } catch (error) {
      setAddressError(error?.message || "Failed to save address");
    }
  };

  const handleRequestDeleteAddress = (addressKey) => {
    setDeletingAddressKey(addressKey);
    setIsDeleteAddressOpen(true);
  };

  const handleConfirmDeleteAddress = async () => {
    if (deletingAddressKey === null) return;

    const targetAddress = addresses.find(
      (address) => getAddressRowKey(address) === deletingAddressKey
    );
    const persistedAddressId = getPersistedAddressId(targetAddress);

    if (persistedAddressId) {
      try {
        const deleteAddressRes = await apiRequest(
          `${PATIENT_ADDRESSES_API}${persistedAddressId}/`,
          { method: "DELETE" }
        );

        if (!deleteAddressRes.ok && deleteAddressRes.status !== 204) {
          const errorMessage = await getResponseErrorMessage(deleteAddressRes);
          throw new Error(`Address delete failed: ${errorMessage}`);
        }
      } catch (error) {
        setAddressError(error?.message || "Failed to delete address");
        setDeletingAddressKey(null);
        setIsDeleteAddressOpen(false);
        return;
      }
    }

    await loadPatientPhonesAndAddresses();
    setDeletingAddressKey(null);
    setIsDeleteAddressOpen(false);
  };

  if (!patient) return null;

  const useClientTerminology = shouldUseClientTerminology();
  const patientLabels = getPatientLabels(useClientTerminology);

  const companyNames =
    patient.companies
      ?.map((c) => c?.company?.company_name ?? c?.company_name ?? "")
      .filter(Boolean)
      .join(", ") || "—";

  const selectedPatientTypeDescription =
    patientTypes.find((pt) => Number(pt.patient_type_id) === Number(patientTypeId))?.description ||
    patient?.patient_type?.description ||
    "—";

  const dateOfBirthDisplay = patient?.dob ? new Date(patient.dob).toLocaleDateString() : "—";

  const linkedPersonIds = patientPeople.map((pp) => pp.person_id);

  const handleRemovePerson = async (patientPersonId) => {
    if (!patientPersonId) return;

    setRemovingLinkId(patientPersonId);
    try {
      await apiRequest(`${PATIENT_PEOPLE_API}${patientPersonId}/`, {
        method: "DELETE",
      });

      setPatientPeople((prev) =>
        prev.filter((pp) => pp.patient_person_id !== patientPersonId)
      );
    } catch (err) {
      console.error("Remove person from patient failed", err);
    } finally {
      setRemovingLinkId(null);
    }
  };

  const handleSavePatient = async () => {
    if (!patient?.patient_id) return;

    setSaving(true);
    try {
      const payload = {
        first_name: firstName,
        middle_name: middleName,
        last_name: lastName,
        email,
        dob,
        patient_type_id: patientTypeId ? Number(patientTypeId) : null,
        is_active: isActive,
        test_mode: testMode,
        company_patient_id: companyPatientId || null,
        preferred_language_code: preferredLanguageCode || null,
      };

      let response = await apiRequest(`${PATIENTS_API_URL}${patient.patient_id}/`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok && response.status === 405) {
        response = await apiRequest(`${PATIENTS_API_URL}${patient.patient_id}/`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
      }

      if (!response.ok) {
        throw new Error(`Update failed with status ${response.status}`);
      }

      const updatedPatientResponse = await response.json();
      const updatedPatient = {
        ...updatedPatientResponse,
        middle_name: updatedPatientResponse?.middle_name ?? middleName,
        test_mode: Boolean(updatedPatientResponse?.test_mode),
      };

      const relationCandidates = getPatientRelationCandidates(patient.patient_id);

      const currentPersistedPhoneIds = new Set(
        phones
          .map(getPersistedPhoneId)
          .filter((id) => Number.isFinite(Number(id)) && Number(id) > 0)
          .map((id) => Number(id))
      );

      const phoneIdsToDelete = initialPersistedPhoneIds.filter(
        (id) => !currentPersistedPhoneIds.has(Number(id))
      );

      for (const phoneId of phoneIdsToDelete) {
        const deletePhoneRes = await apiRequest(`${PATIENT_PHONES_API}${phoneId}/`, {
          method: "DELETE",
        });

        if (!deletePhoneRes.ok && deletePhoneRes.status !== 204) {
          const errorMessage = await getResponseErrorMessage(deletePhoneRes);
          throw new Error(`Phone delete failed: ${errorMessage}`);
        }
      }

      for (const phoneRow of phones) {
        const phoneDigits = normalizePhoneDigits(phoneRow.phone);
        const phoneTypeId = Number(phoneRow?.phone_type?.phone_type_id ?? phoneRow?.phone_type_id);

        if (!phoneDigits || Number.isNaN(phoneTypeId) || phoneTypeId <= 0) {
          throw new Error("Each phone requires a phone number and phone type.");
        }

        const persistedPhoneId = Number(getPersistedPhoneId(phoneRow));

        if (Number.isFinite(persistedPhoneId) && persistedPhoneId > 0) {
          let updatePhoneRes = null;
          let lastPhoneUpdateError = "Unknown error";

          for (const relationPayload of relationCandidates) {
            const updatePayload = {
              phone: phoneDigits,
              phone_type_id: phoneTypeId,
              ...relationPayload,
            };

            updatePhoneRes = await apiRequest(
              `${PATIENT_PHONES_API}${persistedPhoneId}/`,
              {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(updatePayload),
              }
            );

            if (!updatePhoneRes.ok && updatePhoneRes.status === 405) {
              updatePhoneRes = await apiRequest(
                `${PATIENT_PHONES_API}${persistedPhoneId}/`,
                {
                  method: "PUT",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify(updatePayload),
                }
              );
            }

            if (updatePhoneRes.ok) {
              break;
            }

            lastPhoneUpdateError = await getResponseErrorMessage(updatePhoneRes);
          }

          if (!updatePhoneRes || !updatePhoneRes.ok) {
            throw new Error(`Phone update failed: ${lastPhoneUpdateError}`);
          }
        } else {
          await postPatientLinkedRecord(
            PATIENT_PHONES_API,
            {
              phone: phoneDigits,
              phone_type_id: phoneTypeId,
            },
            patient.patient_id
          );
        }
      }

      await loadPatientPhonesAndAddresses();

      // Try to persist the patient <-> company link (company assignment only;
      // company_patient_id lives on the main patient record and is saved above).
      try {
        const relation = patient?.companies?.[0] ?? null;
        const existingRelationId = relation?.company_patient_id ?? null;
        const selectedCompanyId = Number(companyId || (relation?.company_id ?? 0)) || null;

        if (existingRelationId) {
          // PATCH existing relation
          await apiRequest(`${COMPANY_PATIENTS_API}${existingRelationId}/`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              company_id: selectedCompanyId,
              patient_id: patient.patient_id,
            }),
          }).catch(() => {});
        } else if (selectedCompanyId) {
          // Create a new company-patient link if none exists
          await apiRequest(COMPANY_PATIENTS_API, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              company_id: selectedCompanyId,
              patient_id: patient.patient_id,
            }),
          }).catch(() => {});
        }
      } catch (err) {
        console.error("Persist company-patient link failed", err);
      }

      if (onUpdated) {
        onUpdated(updatedPatient);
      } else {
        onClose();
      }
    } catch (err) {
      console.error("Update patient failed", err);
    } finally {
      setSaving(false);
    }
  };

  const handleOpenAddEvent = () => {
    setEditingEvent(null);
    setShowEventModal(true);
  };

  const handleOpenEditEvent = (eventItem) => {
    setEditingEvent(eventItem);
    setShowEventModal(true);
  };

  const handleOpenDeleteEvent = (eventItem) => {
    setDeletingEvent(eventItem);
    setIsDeleteEventOpen(true);
  };

  const handleDeleteEvent = async () => {
    const eventId = deletingEvent ? getPatientEventId(deletingEvent) : null;
    if (!eventId) return;

    setDeletingEventInProgress(true);
    try {
      const response = await apiRequest(`${PATIENT_EVENTS_API}${eventId}/`, {
        method: "DELETE",
      });

      if (!response.ok && response.status !== 204) {
        throw new Error(`Delete event failed with status ${response.status}`);
      }

      setPatientEvents((prev) =>
        prev.filter((item) => getPatientEventId(item) !== eventId)
      );
      setIsDeleteEventOpen(false);
      setDeletingEvent(null);
    } catch (err) {
      console.error("Delete patient event failed", err);
    } finally {
      setDeletingEventInProgress(false);
    }
  };

  const handleSavePatientEvent = async ({ event, eventDate, injuryEventTypeId }) => {
    if (!patient?.patient_id) return;

    const payload = {
      patient_id: patient.patient_id,
      event,
      event_date: eventDate,
      injury_event_type_id: Number(injuryEventTypeId),
    };

    setSavingEvent(true);
    try {
      const existingEventId = editingEvent ? getPatientEventId(editingEvent) : null;

      if (existingEventId) {
        let response = await apiRequest(`${PATIENT_EVENTS_API}${existingEventId}/`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        if (!response.ok && response.status === 405) {
          response = await apiRequest(`${PATIENT_EVENTS_API}${existingEventId}/`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          });
        }

        if (!response.ok) {
          throw new Error(`Update event failed with status ${response.status}`);
        }
      } else {
        const response = await apiRequest(PATIENT_EVENTS_API, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          throw new Error(`Create event failed with status ${response.status}`);
        }
      }

      setShowEventModal(false);
      setEditingEvent(null);
      await loadPatientEvents();
    } catch (err) {
      console.error("Save patient event failed", err);
    } finally {
      setSavingEvent(false);
    }
  };

  const formatEventDate = (value) => {
    return formatDateOnly(value, "—");
  };

  const formatOrderAddress = (address) => {
    if (!address) return "—";

    const lineOne = String(address?.street_1 || "").trim();
    const lineTwo = String(address?.street_2 || "").trim();
    const city = String(address?.city || "").trim();
    const state = String(address?.st || "").trim();
    const zip = String(address?.zip || "").trim();
    const locality = [city, state].filter(Boolean).join(", ");

    return [lineOne, lineTwo, [locality, zip].filter(Boolean).join(" ")]
      .filter(Boolean)
      .join(" | ") || "—";
  };

  const patientDisplayName = `${firstName || ""} ${lastName || ""}`.trim() || patientLabels.singular;

  const formatShippingAddressLabel = (address) => {
    if (!address) return "";

    const lineOne = String(address?.street_1 || "").trim();
    const lineTwo = String(address?.street_2 || "").trim();
    const city = String(address?.city || "").trim();
    const state = String(address?.st || "").trim();
    const zip = String(address?.zip || "").trim();

    const locality = [city, state].filter(Boolean).join(", ");
    const tail = [locality, zip].filter(Boolean).join(" ");

    return [lineOne, lineTwo, tail].filter(Boolean).join(" | ") || "Address";
  };

  const formatSupplementPhoneLabel = (phoneRow) => {
    const phoneTypeDescription =
      phoneRow?.phone_type?.description ||
      phoneTypes.find(
        (phoneType) =>
          Number(phoneType.phone_type_id) ===
          Number(phoneRow?.phone_type?.phone_type_id ?? phoneRow?.phone_type_id)
      )?.description ||
      "Phone";

    return `${phoneTypeDescription}: ${formatPhoneForInput(phoneRow?.phone) || "—"}`;
  };

  const handleOrderSupplementNow = async () => {
    setSupplementOrderSaveError("");

    const orderingCompany = companies.find(
      (company) => Number(company?.company_id ?? company?.id ?? 0) === Number(companyId)
    );

    if (orderingCompany?.enable_ordering !== true) {
      setSupplementOrderSaveError(
        "Ordering is disabled for this company. Please contact a Valhalla administrator to enable it."
      );
      return;
    }

    const selectedAddress = addresses.find(
      (address) => String(getAddressRowKey(address) || "") === selectedShippingAddressKey
    );
    const selectedPhone = phones.find(
      (phoneRow) => String(getPhoneRowKey(phoneRow) || "") === selectedShippingPhoneKey
    );
    const selectedOrderTypeOption = supplementOrderTypeOptions.find(
      (option) => option.value === selectedSupplementOrderType
    );

    const phoneDigits = normalizePhoneDigits(selectedPhone?.phone);
    const numericCost = Number.parseFloat(selectedOrderTypeOption?.cost ?? selectedSupplementOrderTypeCost ?? "0");
    const formattedCost = Number.isFinite(numericCost) ? numericCost.toFixed(2) : "0.00";
    const persistedAddressId = getPersistedAddressId(selectedAddress);
    const persistedPhoneId = getPersistedPhoneId(selectedPhone);
    const orderedByUserId = Number(getSessionUserId());

    if (!persistedAddressId || !persistedPhoneId || !Number.isFinite(orderedByUserId) || orderedByUserId <= 0) {
      setSupplementOrderSaveError("A saved address, saved phone, and logged-in user are required before ordering.");
      return;
    }

    const orderPayload = {
      order: {
        email: email || "",
        phone_number: phoneDigits,
        financial_status: "paid",
        send_receipt: false,
        send_fulfillment_receipt: false,
        line_items: [
          {
              "variant_id": 51938545991997,
              "quantity": 1
          },
        ],
        customer: {
          first_name: firstName || "",
          last_name: lastName || "",
          email: email || "",
          phone_number: phoneDigits,
        },
        shipping_address: {
          first_name: firstName || "",
          last_name: lastName || "",
          address1: selectedAddress?.street_1 || "",
          address2: selectedAddress?.street_2 || "",
          city: selectedAddress?.city || "",
          province_code: selectedAddress?.st || "",
          country_code: getCountryCodeFromValue(selectedAddress?.country),
          zip: selectedAddress?.zip || "",
          phone_number: phoneDigits,
        },
        tags: "valhalla",
        note_attributes: [
          {
            name: "patient_id",
            value: patient?.patient_id ?? "",
          },
          {
            name: "subscription_type",
            value: selectedSupplementOrderType,
          },
          {
            name: "source",
            value: "valhalla",
          },
        ],
      },
      meta_data: {
        product: selectedSupplementProduct,
        order_type: selectedSupplementOrderType,
        order_date: new Date().toISOString(),
        cost: Number(formattedCost),
        comments: "",
        result: {},
        ordered_by: orderedByUserId,
        patient: patient?.patient_id ?? null,
        patient_address: persistedAddressId,
        patient_phone: persistedPhoneId,
      },
    };

    const requestHeaders = {
      "Content-Type": "application/json",
    };

    setSupplementOrderSubmitting(true);

    try {
      const response = await apiRequest(BODYIQ_SEND_ORDER_API, {
        method: "POST",
        headers: requestHeaders,
        body: JSON.stringify(orderPayload),
      });

      const responseBody = await readApiResponseBody(response);

      setSupplementOrderPayloadPreview(
        JSON.stringify(
          {
            request_url: BODYIQ_SEND_ORDER_API,
            headers: requestHeaders,
            request_body: orderPayload,
            response_status_code: response.status,
            response_body: responseBody,
          },
          null,
          2
        )
      );
      setShowSupplementOrderPayloadModal(true);

      if (!response.ok) {
        setSupplementOrderSaveError(`Supplement order request failed with status ${response.status}`);
      }
    } catch (error) {
      setSupplementOrderSaveError(error?.message || "Failed to submit supplement order.");
      setSupplementOrderPayloadPreview(
        JSON.stringify(
          {
            request_url: BODYIQ_SEND_ORDER_API,
            headers: requestHeaders,
            request_body: orderPayload,
            error: error?.message || "Failed to submit supplement order.",
          },
          null,
          2
        )
      );
      setShowSupplementOrderPayloadModal(true);
    } finally {
      setSupplementOrderSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal modern patient-modal">
        <div className="modal-header">
          <h3>
            {isEdit
              ? `EDIT ${patientLabels.singular.toUpperCase()}`
              : `${patientLabels.singular.toUpperCase()} DETAILS`}
          </h3>
          <button className="icon-close" onClick={onClose}>✕</button>
        </div>

        <div className="patient-profile-layout">
          <div className="patient-profile-left">
            <div className="patient-profile-identity-card">
              <div className="patient-profile-avatar">
                {`${firstName?.[0] ?? ""}${lastName?.[0] ?? ""}`.toUpperCase() || "P"}
              </div>
              <div className="patient-profile-name">
                {[firstName, middleName, lastName].filter(Boolean).join(" ") || patientLabels.singular}
              </div>
            </div>

            <div className="patient-profile-fields-card">
              <div className="patient-profile-field-row">
                <span className="patient-profile-field-label">First Name</span>
                <div className="patient-profile-field-value">
                  {isEdit ? (
                    <input value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                  ) : (
                    firstName || "—"
                  )}
                </div>
              </div>

              <div className="patient-profile-field-row">
                <span className="patient-profile-field-label">Middle Name</span>
                <div className="patient-profile-field-value">
                  {isEdit ? (
                    <input value={middleName} onChange={(e) => setMiddleName(e.target.value)} />
                  ) : (
                    middleName || "—"
                  )}
                </div>
              </div>

              <div className="patient-profile-field-row">
                <span className="patient-profile-field-label">Last Name</span>
                <div className="patient-profile-field-value">
                  {isEdit ? (
                    <input value={lastName} onChange={(e) => setLastName(e.target.value)} />
                  ) : (
                    lastName || "—"
                  )}
                </div>
              </div>

              <div className="patient-profile-field-row">
                <span className="patient-profile-field-label">Email</span>
                <div className="patient-profile-field-value">
                  {isEdit ? (
                    <input value={email} onChange={(e) => setEmail(e.target.value)} />
                  ) : (
                    email || "—"
                  )}
                </div>
              </div>

              <div className="patient-profile-field-row">
                <span className="patient-profile-field-label">Date of Birth</span>
                <div className="patient-profile-field-value">
                  {isEdit ? (
                    <input type="date" value={dob} onChange={(e) => setDob(e.target.value)} />
                  ) : (
                    dateOfBirthDisplay
                  )}
                </div>
              </div>

              <div className="patient-profile-field-row">
                <span className="patient-profile-field-label">{patientLabels.singular} Type</span>
                <div className="patient-profile-field-value">
                  {isEdit ? (
                    <select value={patientTypeId} onChange={(e) => setPatientTypeId(e.target.value)}>
                      <option value="">Select type</option>
                      {patientTypes.map((pt) => (
                        <option key={pt.patient_type_id} value={pt.patient_type_id}>
                          {pt.description}
                        </option>
                      ))}
                    </select>
                  ) : (
                    selectedPatientTypeDescription
                  )}
                </div>
              </div>

              <div className="patient-profile-field-row">
                <span className="patient-profile-field-label">Company</span>
                <div className="patient-profile-field-value">
                  {isEdit ? (
                    <select value={companyId} onChange={(e) => setCompanyId(e.target.value)}>
                      <option value="">Select company</option>
                      {companies.map((company) => (
                        <option key={company.company_id} value={company.company_id}>
                          {company.company_name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    companyNames
                  )}
                </div>
              </div>

              <div className="patient-profile-field-row">
                <span className="patient-profile-field-label">Company Patient ID</span>
                <div className="patient-profile-field-value">
                  {isEdit ? (
                    <input
                      value={companyPatientId || ""}
                      onChange={(e) => setCompanyPatientId(e.target.value)}
                    />
                  ) : (
                    companyPatientId || "—"
                  )}
                </div>
              </div>

              <div className="patient-profile-field-row">
                <span className="patient-profile-field-label">Status</span>
                <div className="patient-profile-field-value">
                  {isEdit ? (
                    <label className="checkbox-row">
                      <input
                        type="checkbox"
                        checked={isActive}
                        onChange={(e) => setIsActive(e.target.checked)}
                      />
                      <span>{isActive ? "Active" : "Inactive"}</span>
                    </label>
                  ) : (
                    <span className={`status-pill ${isActive ? "active" : "inactive"}`}>
                      {isActive ? "Active" : "Inactive"}
                    </span>
                  )}
                </div>
              </div>

              <div className="patient-profile-field-row">
                <span className="patient-profile-field-label">Preferred Language</span>
                <div className="patient-profile-field-value">
                  {isEdit ? (
                    <select
                      value={preferredLanguageCode}
                      onChange={(e) => setPreferredLanguageCode(e.target.value)}
                    >
                      <option value="">Select language</option>
                      {languages.map((language) => (
                        <option key={language.code} value={language.code}>
                          {language.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    patient?.preferred_language?.name || "—"
                  )}
                </div>
              </div>

              <div className="patient-profile-order-row">
                <button
                  type="button"
                  className="user-address-add-btn"
                  onClick={() => setShowSupplementOrderModal(true)}
                >
                  Order Valhalla Supplements
                </button>
              </div>
            </div>
          </div>

          <div className="patient-profile-right">
            <div className="patient-data-card">
              <button
                type="button"
                className="patient-data-card-header patient-data-card-header-toggle"
                onClick={() => setIsPhonesSectionOpen((prev) => !prev)}
                aria-expanded={isPhonesSectionOpen}
              >
                <span>Phones</span>
                <span className="patient-card-chevron">{isPhonesSectionOpen ? "▾" : "▸"}</span>
              </button>

              {isPhonesSectionOpen && (
                <div className="patient-data-card-body">
                  {phones.length === 0 ? (
                    <p className="people-empty">No phones added.</p>
                  ) : (
                    <div className="user-phones-grid">
                      {phones.map((phoneRow) => (
                        <div key={getPhoneRowKey(phoneRow) ?? `phone-${phoneRow.phone ?? ""}`} className="user-phone-card">
                          <div className="user-phone-card-top">
                            <strong>
                              {phoneRow?.phone_type?.description ||
                                phoneTypes.find(
                                  (phoneType) =>
                                    Number(phoneType.phone_type_id) ===
                                    Number(phoneRow?.phone_type?.phone_type_id ?? phoneRow?.phone_type_id)
                                )?.description ||
                                "Phone"}
                            </strong>

                            {isEdit && (
                              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                <button
                                  type="button"
                                  className="icon-edit"
                                  title="Edit phone"
                                  onClick={() => openEditPhoneModal(phoneRow)}
                                >
                                  ✏️
                                </button>
                                <FaTrash
                                  className="icon-button"
                                  style={{ color: "#dc2626" }}
                                  onClick={() => handleRequestDeletePhone(getPhoneRowKey(phoneRow))}
                                />
                              </div>
                            )}
                          </div>

                          <div className="user-phone-card-body">
                            <p>{formatPhoneForInput(phoneRow.phone) || "—"}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {isEdit && (
                    <button type="button" className="user-address-add-btn" onClick={openAddPhoneModal}>
                      + Add Phone
                    </button>
                  )}
                </div>
              )}
            </div>

            <div className="patient-data-card people-section">
              <div className="collapsible-section-header patient-data-card-header-row">
                <button
                  type="button"
                  className="collapsible-section-toggle"
                  aria-expanded={isPartnersSectionOpen}
                  onClick={() => setIsPartnersSectionOpen((prev) => !prev)}
                >
                  <span>Associated Partners</span>
                  <span className="patient-card-chevron">{isPartnersSectionOpen ? "▾" : "▸"}</span>
                </button>
              </div>

              {isPartnersSectionOpen && (
                <div className="patient-data-card-body">
                  {peopleLoading ? (
                    <div className="people-empty">Loading people...</div>
                  ) : patientPeople.length === 0 ? (
                    <div className="people-empty">
                      No people associated with this {patientLabels.singularLower}.
                    </div>
                  ) : (
                    <div className="people-list">
                      {patientPeople.map((pp) => (
                        <div className="people-row" key={`${pp.person_id}-${pp.patient_person_id ?? "temp"}`}>
                          <div>
                            <div className="people-name">{getPersonDisplayName(pp)}</div>
                            <div className="people-type">{pp.person_type_description}</div>
                          </div>
                          {isEdit && (
                            <button
                              type="button"
                              className="icon-remove"
                              title="Remove person"
                              style={{ color: "#dc2626" }}
                              disabled={removingLinkId === pp.patient_person_id || !pp.patient_person_id}
                              onClick={() => handleRemovePerson(pp.patient_person_id)}
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {isEdit && (
                    <button
                      type="button"
                      className="user-address-add-btn"
                      onClick={() => setShowPersonModal(true)}
                    >
                      + Add Partner
                    </button>
                  )}
                </div>
              )}
            </div>

            <div className="patient-data-card order-history-section">
              <div className="collapsible-section-header patient-data-card-header-row">
                <button
                  type="button"
                  className="collapsible-section-toggle"
                  aria-expanded={isOrdersSectionOpen}
                  onClick={() => setIsOrdersSectionOpen((prev) => !prev)}
                >
                  <span>Order History</span>
                  <span className="patient-card-chevron">{isOrdersSectionOpen ? "▾" : "▸"}</span>
                </button>
              </div>

              {isOrdersSectionOpen && (
                <div className="patient-data-card-body">
                  {ordersLoading ? (
                    <div className="people-empty">Loading order history...</div>
                  ) : patientOrders.length === 0 ? (
                    <div className="people-empty">No orders found for this patient.</div>
                  ) : (
                    <div className="user-addresses-grid order-history-grid">
                      {patientOrders.map((order) => {
                        const orderId = order?.patient_order_id ?? order?.id;
                        const orderedByFirstName = String(order?.ordered_by?.first_name || "").trim();
                        const orderedByLastName = String(order?.ordered_by?.last_name || "").trim();

                        return (
                          <div key={orderId ?? `${order?.order_date ?? "order"}-${order?.product ?? ""}`} className="user-address-card order-history-card">
                            <div className="user-address-card-top">
                              <strong>
                                {`${order?.product || "—"} - ${order?.result?.bodyiq_response?.order?.line_items?.[0]?.name || "—"}`}
                              </strong>
                              <span className="order-history-date">{formatEventDate(order?.order_date)}</span>
                            </div>

                            <div className="user-address-card-body">
                              <p>
                                {`${order?.patient?.first_name || ""} ${order?.patient?.last_name || ""}`.trim() || "—"}
                              </p>
                              <p>{formatOrderAddress(order?.patient_address)}</p>
                              <p>Order ID: {order?.result?.bodyiq_response?.order?.id || "—"}</p>
                              <p>Order Name: {order?.result?.bodyiq_response?.order?.name || "—"}</p>
                              <p>Order Type: {order?.order_type || "—"}</p>
                              <p>
                                Ordered By: {[orderedByFirstName, orderedByLastName].filter(Boolean).join(" ") || "—"}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="patient-data-card user-addresses-section">
              <div className="collapsible-section-header patient-data-card-header-row">
                <button
                  type="button"
                  className="collapsible-section-toggle"
                  aria-expanded={isAddressesSectionOpen}
                  onClick={() => setIsAddressesSectionOpen((prev) => !prev)}
                >
                  <span>Addresses</span>
                  <span className="patient-card-chevron">{isAddressesSectionOpen ? "▾" : "▸"}</span>
                </button>
              </div>

              {isAddressesSectionOpen && (
                <div className="patient-data-card-body">
                  {addresses.length === 0 ? (
                    <p className="user-addresses-empty">No addresses added.</p>
                  ) : (
                    <div className="user-addresses-grid">
                      {addresses.map((address) => (
                        <div key={getAddressRowKey(address)} className="user-address-card">
                          <div className="user-address-card-top">
                            <strong>{address?.address_type?.description || "Address"}</strong>
                            {isEdit && (
                              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                <button
                                  type="button"
                                  className="icon-edit"
                                  title="Edit address"
                                  onClick={() => openEditAddressModal(address)}
                                >
                                  ✏️
                                </button>
                                <FaTrash
                                  className="icon-button"
                                  style={{ color: "#dc2626" }}
                                  onClick={() => handleRequestDeleteAddress(getAddressRowKey(address))}
                                />
                              </div>
                            )}
                          </div>

                          <div className="user-address-card-body">
                            <p>{address?.street_1 || "—"}</p>
                            {address?.street_2?.trim() && <p>{address.street_2}</p>}
                            <p>
                              {address?.city || ""}
                              {address?.city && address?.st ? ", " : ""}
                              {address?.st || ""}
                              {(address?.city || address?.st) && address?.zip ? " " : ""}
                              {address?.zip || (!(address?.city || address?.st) ? "—" : "")}
                            </p>
                            <p>{address?.country || "—"}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {isEdit && (
                    <button type="button" className="user-address-add-btn" onClick={openAddAddressModal}>
                      + Add Address
                    </button>
                  )}
                </div>
              )}
            </div>

          </div>

          <div className="patient-data-card events-section events-section-full-width">
              <div className="collapsible-section-header patient-data-card-header-row">
                <button
                  type="button"
                  className="collapsible-section-toggle"
                  aria-expanded={isEventsSectionOpen}
                  onClick={() => setIsEventsSectionOpen((prev) => !prev)}
                >
                  <span>Injury Events</span>
                  <span className="patient-card-chevron">{isEventsSectionOpen ? "▾" : "▸"}</span>
                </button>
              </div>

              {isEventsSectionOpen && (
                <div className="patient-data-card-body">
                  {eventsLoading ? (
                    <div className="people-empty">Loading events...</div>
                  ) : patientEvents.length === 0 ? (
                    <div className="people-empty">
                      No injury events associated with this {patientLabels.singularLower}.
                    </div>
                  ) : (
                    <div className="events-table-wrap">
                      <table className="events-table">
                        <colgroup>
                          <col className="events-col-event" />
                          <col className="events-col-type" />
                          <col className="events-col-date" />
                          {isEdit && <col className="events-col-actions" />}
                        </colgroup>
                        <thead>
                          <tr>
                            <th>Event</th>
                            <th>Event Type</th>
                            <th>Event Date</th>
                            {isEdit && <th>Actions</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {patientEvents.map((eventItem, index) => (
                            <tr key={getPatientEventId(eventItem) ?? `${eventItem.event}-${index}`}>
                              <td>{eventItem.event || "—"}</td>
                              <td>{getInjuryEventTypeDescription(eventItem)}</td>
                              <td>{formatEventDate(eventItem.event_date)}</td>
                              {isEdit && (
                                <td className="event-actions-cell">
                                  <div className="event-actions">
                                    <button
                                      type="button"
                                      className="icon-edit"
                                      title="Edit injury event"
                                      onClick={() => handleOpenEditEvent(eventItem)}
                                    >
                                      ✏️
                                    </button>
                                    <button
                                      type="button"
                                      className="icon-remove"
                                      title="Delete injury event"
                                      onClick={() => handleOpenDeleteEvent(eventItem)}
                                    >
                                      <FaTrash className="icon-button" style={{ color: "#dc2626" }} />
                                    </button>
                                  </div>
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {isEdit && (
                    <button type="button" className="user-address-add-btn" onClick={handleOpenAddEvent}>
                      + Add Injury Event
                    </button>
                  )}
                </div>
              )}
          </div>

          {canManageTestMode && (
            <div className="patient-test-mode-field">
              <label htmlFor={`patient-test-mode-${patient.patient_id}`}>
                <span>Point this patient to the DEV Environment</span>
                <input
                  id={`patient-test-mode-${patient.patient_id}`}
                  type="checkbox"
                  checked={testMode}
                  disabled={!isEdit}
                  onChange={(event) => setTestMode(event.target.checked)}
                />
              </label>
            </div>
          )}
        </div>

        <div className="modal-actions">
          <button onClick={onClose}>Close</button>
          {isEdit && (
            <button className="primary" onClick={handleSavePatient} disabled={saving}>
              {saving ? "Saving..." : "Save Changes"}
            </button>
          )}
        </div>

        {showPersonModal && (
          <PatientPersonModal
            companyId={patientCompanyId}
            patientId={patient.patient_id}
            linkedPersonIds={linkedPersonIds}
            onClose={() => setShowPersonModal(false)}
            onPersonAdded={() => {
              setShowPersonModal(false);
              loadPatientPeople();
            }}
          />
        )}

        {showEventModal && (
          <PatientEventModal
            title={editingEvent ? "Edit Injury Event" : "Add Injury Event"}
            initialEvent={editingEvent}
            saving={savingEvent}
            onCancel={() => {
              if (savingEvent) return;
              setShowEventModal(false);
              setEditingEvent(null);
            }}
            onSave={handleSavePatientEvent}
            injuryEventTypes={injuryEventTypes}
          />
        )}

        {isDeleteEventOpen && deletingEvent && (
          <ConfirmActionModal
            title="Delete Injury Event"
            message="Are you sure you want to delete this injury event?"
            confirmLabel={deletingEventInProgress ? "Deleting..." : "Yes, Delete"}
            disabled={deletingEventInProgress}
            onCancel={() => {
              if (deletingEventInProgress) return;
              setIsDeleteEventOpen(false);
              setDeletingEvent(null);
            }}
            onConfirm={handleDeleteEvent}
          />
        )}

        {showPhoneModal && (
          <div className="modal-overlay" style={{ zIndex: 1300 }}>
            <div className="modal modern" style={{ width: "520px" }}>
              <div className="modal-header">
                <h3>{editingPhoneKey !== null ? "Edit Phone" : "Add Phone"}</h3>
                <button className="icon-close" onClick={() => setShowPhoneModal(false)}>✕</button>
              </div>

              <div className="details-grid">
                <Detail label="Phone">
                  <input
                    inputMode="numeric"
                    value={formatPhoneForInput(newPhoneData.phone)}
                    onChange={(e) =>
                      setNewPhoneData((prev) => ({
                        ...prev,
                        phone: normalizePhoneDigits(e.target.value),
                      }))
                    }
                  />
                </Detail>
                <Detail label="Phone Type">
                  <select
                    value={newPhoneData.phone_type_id}
                    onChange={(e) =>
                      setNewPhoneData((prev) => ({ ...prev, phone_type_id: e.target.value }))
                    }
                  >
                    <option value="">Select Type</option>
                    {phoneTypes.map((phoneType) => (
                      <option key={phoneType.phone_type_id} value={phoneType.phone_type_id}>
                        {phoneType.description}
                      </option>
                    ))}
                  </select>
                </Detail>
              </div>

              <div className="modal-actions">
                {phoneError && <p style={{ color: "#dc2626", marginRight: "auto" }}>{phoneError}</p>}
                <button onClick={() => setShowPhoneModal(false)}>Cancel</button>
                <button className="primary" onClick={handleSavePhoneFromModal}>
                  {editingPhoneKey !== null ? "Save Phone" : "Add Phone"}
                </button>
              </div>
            </div>
          </div>
        )}

        {isDeletePhoneOpen && (
          <div className="modal-overlay" style={{ zIndex: 1350 }}>
            <div className="modal modern" style={{ width: "460px" }}>
              <div className="modal-header">
                <h3>Delete Phone</h3>
                <button
                  className="icon-close"
                  onClick={() => {
                    setIsDeletePhoneOpen(false);
                    setDeletingPhoneKey(null);
                  }}
                >
                  ✕
                </button>
              </div>

              <p style={{ marginBottom: "18px", color: "#444" }}>
                Are you sure you want to delete this phone?
              </p>

              <div className="modal-actions">
                <button
                  onClick={() => {
                    setIsDeletePhoneOpen(false);
                    setDeletingPhoneKey(null);
                  }}
                >
                  Cancel
                </button>
                <button className="primary" onClick={handleConfirmDeletePhone}>
                  Yes, Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {showAddressModal && (
          <div className="modal-overlay" style={{ zIndex: 1300 }}>
            <div className="modal modern" style={{ width: "560px" }}>
              <div className="modal-header">
                <h3>{editingAddressKey !== null ? "Edit Address" : "Add Address"}</h3>
                <button className="icon-close" onClick={() => setShowAddressModal(false)}>✕</button>
              </div>

              <div className="details-grid">
                <Detail label="Street 1">
                  <input
                    value={newAddressData.street_1}
                    onChange={(e) => setNewAddressData((prev) => ({ ...prev, street_1: e.target.value }))}
                  />
                </Detail>
                <Detail label="Street 2">
                  <input
                    value={newAddressData.street_2}
                    onChange={(e) => setNewAddressData((prev) => ({ ...prev, street_2: e.target.value }))}
                  />
                </Detail>
                <Detail label="City">
                  <input
                    value={newAddressData.city}
                    onChange={(e) => setNewAddressData((prev) => ({ ...prev, city: e.target.value }))}
                  />
                </Detail>
                <Detail label="State">
                  <select
                    value={newAddressData.st}
                    onChange={(e) => setNewAddressData((prev) => ({ ...prev, st: e.target.value }))}
                  >
                    <option value="">Select State</option>
                    {US_STATES.map((stateOption) => (
                      <option key={stateOption.value} value={stateOption.value}>
                        {stateOption.label}
                      </option>
                    ))}
                  </select>
                </Detail>
                <Detail label="Zip">
                  <input
                    value={newAddressData.zip}
                    onChange={(e) => setNewAddressData((prev) => ({ ...prev, zip: e.target.value }))}
                  />
                </Detail>
                <Detail label="Country">
                  <select
                    value={newAddressData.country}
                    onChange={(e) => setNewAddressData((prev) => ({ ...prev, country: e.target.value }))}
                  >
                    {COUNTRY_OPTIONS.map((country) => (
                      <option key={country} value={country}>
                        {country}
                      </option>
                    ))}
                  </select>
                </Detail>
                <Detail label="Address Type">
                  <select
                    value={newAddressData.address_type_id}
                    onChange={(e) => setNewAddressData((prev) => ({ ...prev, address_type_id: e.target.value }))}
                  >
                    <option value="">Select Type</option>
                    {addressTypes.map((addressType) => (
                      <option key={addressType.address_type_id} value={addressType.address_type_id}>
                        {addressType.description}
                      </option>
                    ))}
                  </select>
                </Detail>
              </div>

              <div className="modal-actions">
                {addressError && <p style={{ color: "#dc2626", marginRight: "auto" }}>{addressError}</p>}
                <button onClick={() => setShowAddressModal(false)}>Cancel</button>
                <button className="primary" onClick={handleSaveAddressFromModal}>
                  {editingAddressKey !== null ? "Save Address" : "Add Address"}
                </button>
              </div>
            </div>
          </div>
        )}

        {isDeleteAddressOpen && (
          <div className="modal-overlay" style={{ zIndex: 1350 }}>
            <div className="modal modern" style={{ width: "460px" }}>
              <div className="modal-header">
                <h3>Delete Address</h3>
                <button
                  className="icon-close"
                  onClick={() => {
                    setIsDeleteAddressOpen(false);
                    setDeletingAddressKey(null);
                  }}
                >
                  ✕
                </button>
              </div>

              <p style={{ marginBottom: "18px", color: "#444" }}>
                Are you sure you want to delete this address?
              </p>

              <div className="modal-actions">
                <button
                  onClick={() => {
                    setIsDeleteAddressOpen(false);
                    setDeletingAddressKey(null);
                  }}
                >
                  Cancel
                </button>
                <button className="primary" onClick={handleConfirmDeleteAddress}>
                  Yes, Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {showSupplementOrderModal && (
          <div className="modal-overlay" style={{ zIndex: 1360 }}>
            <div className="modal modern supplement-order-modal">
              <div className="modal-header supplement-order-modal-header">
                <div className="supplement-order-title-block">
                  <span className="supplement-order-title-eyebrow">Valhalla Supplements</span>
                  <h3>{`Order Supplements for ${patientDisplayName}`}</h3>
                </div>
                <button className="icon-close" onClick={() => setShowSupplementOrderModal(false)}>✕</button>
              </div>

              <div className="supplement-order-layout">
                <section className="supplement-order-card supplement-order-card-patient">
                  <div className="supplement-order-card-header">
                    <h4>Patient Information</h4>
                    <p>Confirm the shipping contact details for this order.</p>
                  </div>

                  <div className="supplement-order-card-fields">
                    <div className="supplement-order-field supplement-order-field-full">
                      <Detail label="Shipping Address">
                        <select
                          value={selectedShippingAddressKey}
                          onChange={(e) => setSelectedShippingAddressKey(e.target.value)}
                        >
                          {addresses.length === 0 ? (
                            <option value="">No addresses available</option>
                          ) : (
                            addresses.map((address) => {
                              const addressKey = getAddressRowKey(address);
                              if (!addressKey) return null;

                              return (
                                <option key={addressKey} value={String(addressKey)}>
                                  {formatShippingAddressLabel(address)}
                                </option>
                              );
                            })
                          )}
                        </select>
                      </Detail>
                    </div>

                    <div className="supplement-order-field supplement-order-field-full">
                      <Detail label="Phone">
                        <select
                          value={selectedShippingPhoneKey}
                          onChange={(e) => setSelectedShippingPhoneKey(e.target.value)}
                        >
                          {phones.length === 0 ? (
                            <option value="">No phone numbers available</option>
                          ) : (
                            phones.map((phoneRow) => {
                              const phoneKey = getPhoneRowKey(phoneRow);
                              if (!phoneKey) return null;

                              return (
                                <option key={phoneKey} value={String(phoneKey)}>
                                  {formatSupplementPhoneLabel(phoneRow)}
                                </option>
                              );
                            })
                          )}
                        </select>
                      </Detail>
                    </div>
                  </div>
                </section>

                <section className="supplement-order-card supplement-order-card-product">
                  <div className="supplement-order-card-header">
                    <h4>Product Information</h4>
                    <p>Choose the suppliment product and order type.</p>
                  </div>

                  <div className="supplement-order-card-fields">
                    <div className="supplement-order-field supplement-order-field-full">
                      <Detail label="Product">
                        <select
                          value={selectedSupplementProduct}
                          onChange={(e) => setSelectedSupplementProduct(e.target.value)}
                          disabled={supplementOptionsLoading || supplementProductOptions.length === 0}
                        >
                          <option value="">
                            {supplementOptionsLoading
                              ? "Loading products..."
                              : supplementProductOptions.length === 0
                                ? "No products available"
                                : "Select product"}
                          </option>
                          {supplementProductOptions.map((option) => {
                            const optionValue = String(option?.option_value || "").trim();
                            if (!optionValue) return null;

                            return (
                              <option key={optionValue} value={optionValue}>
                                {String(option?.option || "").trim() || optionValue}
                              </option>
                            );
                          })}
                        </select>
                      </Detail>
                    </div>

                    <div className="supplement-order-field supplement-order-field-full">
                      <Detail label="Order Type">
                        <select
                          value={selectedSupplementOrderType}
                          onChange={(e) => setSelectedSupplementOrderType(e.target.value)}
                          disabled={
                            supplementOptionsLoading ||
                            !selectedSupplementProduct ||
                            supplementOrderTypeOptions.length === 0
                          }
                        >
                          <option value="">
                            {selectedSupplementProduct
                              ? supplementOrderTypeOptions.length === 0
                                ? "No order types available"
                                : "Select order type"
                              : "Select product first"}
                          </option>
                          {supplementOrderTypeOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.description}
                            </option>
                          ))}
                        </select>
                      </Detail>
                    </div>
                  </div>
                </section>
              </div>

              <div className="modal-actions">
                {(supplementOptionsError || supplementOrderSaveError) && (
                  <p className="supplement-order-error" style={{ color: "#dc2626", marginRight: "auto" }}>
                    {supplementOptionsError || supplementOrderSaveError}
                  </p>
                )}
                <button onClick={() => setShowSupplementOrderModal(false)}>Cancel</button>
                <button
                  className="primary"
                  onClick={handleOrderSupplementNow}
                  disabled={
                    supplementOrderSubmitting ||
                    addresses.length === 0 ||
                    !selectedShippingAddressKey ||
                    phones.length === 0 ||
                    !selectedShippingPhoneKey ||
                    !selectedSupplementProduct ||
                    !selectedSupplementOrderType
                  }
                >
                  {supplementOrderSubmitting ? "Ordering..." : "Order Now"}
                </button>
              </div>
            </div>
          </div>
        )}

        {showSupplementOrderPayloadModal && (
          <div className="modal-overlay" style={{ zIndex: 1370 }}>
            <div className="modal modern supplement-order-payload-modal" style={{ width: "min(900px, 92vw)" }}>
              <div className="modal-header">
                <h3>Supplement Order Request / Response</h3>
                <button
                  className="icon-close"
                  onClick={() => {
                    setShowSupplementOrderPayloadModal(false);
                    setShowSupplementOrderModal(false);
                  }}
                >
                  ✕
                </button>
              </div>

              <div
                className="supplement-order-payload-content"
                style={{
                  maxHeight: "70vh",
                  overflow: "auto",
                  background: "#0f172a",
                  color: "#e2e8f0",
                  borderRadius: "14px",
                  padding: "18px",
                  border: "1px solid rgba(148, 163, 184, 0.25)",
                }}
              >
                <pre
                  style={{
                    margin: 0,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    fontSize: "13px",
                    lineHeight: 1.55,
                    fontFamily: '"SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace',
                  }}
                >
                  {supplementOrderPayloadPreview}
                </pre>
              </div>

              <div className="modal-actions">
                <button
                  onClick={() => {
                    setShowSupplementOrderPayloadModal(false);
                    setShowSupplementOrderModal(false);
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const AddPatientModal = ({
  onClose,
  onCreated,
  restrictedCompanyId = null,
  canManageTestMode,
}) => {
  const [firstName, setFirstName] = useState("");
  const [middleName, setMiddleName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [dob, setDob] = useState("");
  const [patientTypeId, setPatientTypeId] = useState("");
  const [companyId, setCompanyId] = useState("");
  const [companyPatientId, setCompanyPatientId] = useState("");
  const [languages, setLanguages] = useState([]);
  const [preferredLanguageCode, setPreferredLanguageCode] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [testMode, setTestMode] = useState(false);

  const useClientTerminology = shouldUseClientTerminology();
  const patientLabels = getPatientLabels(useClientTerminology);

  const [companies, setCompanies] = useState([]);
  const [patientTypes, setPatientTypes] = useState([]);
  const [stagedPeople, setStagedPeople] = useState([]);
  const [showPersonModal, setShowPersonModal] = useState(false);
  const [stagedEvents, setStagedEvents] = useState([]);
  const [injuryEventTypes, setInjuryEventTypes] = useState([]);
  const [showEventModal, setShowEventModal] = useState(false);
  const [editingEventIndex, setEditingEventIndex] = useState(null);
  const [deletingEventIndex, setDeletingEventIndex] = useState(null);
  const [isDeleteEventOpen, setIsDeleteEventOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [phones, setPhones] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [phoneTypes, setPhoneTypes] = useState([]);
  const [addressTypes, setAddressTypes] = useState([]);
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [editingPhoneKey, setEditingPhoneKey] = useState(null);
  const [isDeletePhoneOpen, setIsDeletePhoneOpen] = useState(false);
  const [deletingPhoneKey, setDeletingPhoneKey] = useState(null);
  const [phoneError, setPhoneError] = useState("");
  const [newPhoneData, setNewPhoneData] = useState({
    phone: "",
    phone_type_id: "",
  });
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [editingAddressKey, setEditingAddressKey] = useState(null);
  const [isDeleteAddressOpen, setIsDeleteAddressOpen] = useState(false);
  const [deletingAddressKey, setDeletingAddressKey] = useState(null);
  const [addressError, setAddressError] = useState("");
  const [isPartnersSectionOpen, setIsPartnersSectionOpen] = useState(false);
  const [isEventsSectionOpen, setIsEventsSectionOpen] = useState(false);
  const [isAddressesSectionOpen, setIsAddressesSectionOpen] = useState(false);
  const [isPhonesSectionOpen, setIsPhonesSectionOpen] = useState(true);
  const [newAddressData, setNewAddressData] = useState({
    street_1: "",
    street_2: "",
    city: "",
    st: "",
    zip: "",
    country: "United States",
    address_type_id: "",
  });

  useEffect(() => {
    Promise.all([
      apiRequest(COMPANIES_API).then(r => r.json()),
      apiRequest(PATIENT_TYPES_API).then(r => r.json()),
      apiRequest(PHONE_TYPES_API).then(r => r.json()),
      apiRequest(ADDRESS_TYPES_API).then(r => r.json()),
      apiRequest(INJURY_EVENT_TYPES_API).then(r => r.json()),
    ]).then(([companies, patientTypes, phoneTypeRows, addressTypeRows, injuryEventTypeRows]) => {
      const restrictedId = Number(restrictedCompanyId);
      const filteredCompanies = restrictedId
        ? companies.filter((company) => Number(company.company_id) === restrictedId)
        : companies;

      setCompanies(filteredCompanies);
      if (restrictedId && filteredCompanies.length > 0) {
        setCompanyId(String(restrictedId));
      }
      setPatientTypes(patientTypes);
      setPhoneTypes(phoneTypeRows);
      setAddressTypes(addressTypeRows);
      setInjuryEventTypes(Array.isArray(injuryEventTypeRows) ? injuryEventTypeRows : []);
    }).catch(console.error);

    apiRequest(LANGUAGES_API)
      .then((res) => res.json())
      .then((languageRows) => setLanguages(Array.isArray(languageRows) ? languageRows : []))
      .catch((error) => {
        console.error("Load languages failed", error);
        setLanguages([]);
      });
  }, [restrictedCompanyId]);

  useEffect(() => {
    setStagedPeople([]);
  }, [companyId]);

  const formatEventDate = (value) => {
    return formatDateOnly(value, "—");
  };

  const handleRemoveStagedPerson = (personId) => {
    setStagedPeople((prev) => prev.filter((p) => p.person_id !== personId));
  };

  const handleOpenAddEvent = () => {
    setEditingEventIndex(null);
    setShowEventModal(true);
  };

  const handleOpenEditEvent = (index) => {
    setEditingEventIndex(index);
    setShowEventModal(true);
  };

  const handleOpenDeleteEvent = (index) => {
    setDeletingEventIndex(index);
    setIsDeleteEventOpen(true);
  };

  const handleDeleteStagedEvent = () => {
    if (deletingEventIndex === null) return;

    setStagedEvents((prev) => prev.filter((_, index) => index !== deletingEventIndex));
    setDeletingEventIndex(null);
    setIsDeleteEventOpen(false);
  };

  const handleSaveStagedEvent = ({ event, eventDate, injuryEventTypeId }) => {
    const parsedInjuryEventTypeId = Number(injuryEventTypeId);
    const selectedInjuryEventType = injuryEventTypes.find(
      (eventType) => Number(eventType?.injury_event_type_id) === parsedInjuryEventTypeId
    );

    const newEvent = {
      event,
      event_date: eventDate,
      injury_event_type_id: parsedInjuryEventTypeId,
      injury_event_type: selectedInjuryEventType
        ? {
            injury_event_type_id: selectedInjuryEventType.injury_event_type_id,
            description: selectedInjuryEventType.description,
          }
        : null,
    };

    setStagedEvents((prev) => {
      if (editingEventIndex === null) {
        return [...prev, newEvent];
      }

      return prev.map((item, index) => (index === editingEventIndex ? newEvent : item));
    });

    setShowEventModal(false);
    setEditingEventIndex(null);
  };

  const postPatientLinkedRecord = async (baseUrl, basePayload, patientId) => {
    const relationCandidates = getPatientRelationCandidates(patientId);
    if (relationCandidates.length === 0) {
      throw new Error("Patient ID is required.");
    }

    let lastErrorMessage = "Unknown error";

    for (const relationPayload of relationCandidates) {
      const response = await apiRequest(baseUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...basePayload,
          ...relationPayload,
        }),
      });

      if (response.ok) {
        return response;
      }

      lastErrorMessage = await getResponseErrorMessage(response);
    }

    throw new Error(lastErrorMessage);
  };

  const openAddPhoneModal = () => {
    setEditingPhoneKey(null);
    setNewPhoneData({ phone: "", phone_type_id: "" });
    setPhoneError("");
    setShowPhoneModal(true);
  };

  const openEditPhoneModal = (phone) => {
    setEditingPhoneKey(getPhoneRowKey(phone));
    setNewPhoneData({
      phone: phone?.phone || "",
      phone_type_id: String(phone?.phone_type?.phone_type_id ?? phone?.phone_type_id ?? ""),
    });
    setPhoneError("");
    setShowPhoneModal(true);
  };

  const handleSavePhoneFromModal = () => {
    const phoneDigits = normalizePhoneDigits(newPhoneData.phone);
    const phoneTypeId = Number(newPhoneData.phone_type_id);

    if (!phoneDigits || Number.isNaN(phoneTypeId) || phoneTypeId <= 0) {
      setPhoneError("Phone number and Phone Type are required.");
      return;
    }

    const selectedPhoneType = phoneTypes.find(
      (type) => Number(type.phone_type_id) === phoneTypeId
    );

    const normalizedPhone = {
      phone: phoneDigits,
      phone_type: selectedPhoneType || {
        phone_type_id: phoneTypeId,
        description: "",
      },
      _isNew: true,
    };

    if (editingPhoneKey !== null) {
      setPhones((prev) =>
        prev.map((phone) =>
          getPhoneRowKey(phone) === editingPhoneKey
            ? { ...phone, ...normalizedPhone }
            : phone
        )
      );
    } else {
      setPhones((prev) => [
        ...prev,
        {
          id: Date.now(),
          ...normalizedPhone,
        },
      ]);
    }

    setShowPhoneModal(false);
    setEditingPhoneKey(null);
    setPhoneError("");
  };

  const handleRequestDeletePhone = (phoneKey) => {
    setDeletingPhoneKey(phoneKey);
    setIsDeletePhoneOpen(true);
  };

  const handleConfirmDeletePhone = () => {
    if (deletingPhoneKey === null) return;

    setPhones((prev) => prev.filter((phone) => getPhoneRowKey(phone) !== deletingPhoneKey));
    setDeletingPhoneKey(null);
    setIsDeletePhoneOpen(false);
  };

  const openAddAddressModal = () => {
    setEditingAddressKey(null);
    setNewAddressData({
      street_1: "",
      street_2: "",
      city: "",
      st: "",
      zip: "",
      country: "United States",
      address_type_id: "",
    });
    setAddressError("");
    setShowAddressModal(true);
  };

  const openEditAddressModal = (address) => {
    setEditingAddressKey(getAddressRowKey(address));
    setNewAddressData({
      street_1: address?.street_1 || "",
      street_2: address?.street_2 || "",
      city: address?.city || "",
      st: address?.st || "",
      zip: address?.zip || "",
      country: address?.country || "United States",
      address_type_id: String(
        address?.address_type?.address_type_id ??
        address?.address_type_id ??
        ""
      ),
    });
    setAddressError("");
    setShowAddressModal(true);
  };

  const handleSaveAddressFromModal = () => {
    const addressTypeId = Number(newAddressData.address_type_id);
    if (!newAddressData.street_1.trim() || Number.isNaN(addressTypeId) || addressTypeId <= 0) {
      setAddressError("Address line 1 and Address Type are required.");
      return;
    }

    const selectedAddressType = addressTypes.find(
      (type) => Number(type.address_type_id) === addressTypeId
    );

    const normalizedAddress = {
      street_1: newAddressData.street_1.trim(),
      street_2: newAddressData.street_2.trim(),
      city: newAddressData.city.trim(),
      st: newAddressData.st.trim(),
      zip: newAddressData.zip.trim(),
      country: newAddressData.country.trim(),
      address_type: selectedAddressType || {
        address_type_id: addressTypeId,
        description: "",
      },
      _isNew: true,
    };

    if (editingAddressKey !== null) {
      setAddresses((prev) =>
        prev.map((address) =>
          getAddressRowKey(address) === editingAddressKey
            ? { ...address, ...normalizedAddress }
            : address
        )
      );
    } else {
      setAddresses((prev) => [
        ...prev,
        {
          id: Date.now(),
          ...normalizedAddress,
        },
      ]);
    }

    setShowAddressModal(false);
    setEditingAddressKey(null);
    setAddressError("");
  };

  const handleRequestDeleteAddress = (addressKey) => {
    setDeletingAddressKey(addressKey);
    setIsDeleteAddressOpen(true);
  };

  const handleConfirmDeleteAddress = () => {
    if (deletingAddressKey === null) return;
    setAddresses((prev) => prev.filter((address) => getAddressRowKey(address) !== deletingAddressKey));
    setDeletingAddressKey(null);
    setIsDeleteAddressOpen(false);
  };

  const handleSave = async () => {
    setSaving(true);

    try {
      // 1️⃣ Create patient
      const patientRes = await apiRequest(
        PATIENTS_API_URL,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            first_name: firstName,
            middle_name: middleName,
            last_name: lastName,
            email,
            dob,
            patient_type_id: patientTypeId,
            is_active: isActive,
            test_mode: testMode,
            company_patient_id: companyPatientId || null,
            preferred_language_code: preferredLanguageCode || null,
          }),
        }
      );

      const patient = await patientRes.json();

      // 2️⃣ Link patient to company
      await apiRequest(
        COMPANY_PATIENTS_API,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            company_id: companyId,
            patient_id: patient.patient_id,
          }),
        }
      );

      for (const person of stagedPeople) {
        try {
          await apiRequest(PATIENT_PEOPLE_API, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              patient_id: patient.patient_id,
              person_id: person.person_id,
            }),
          });
        } catch (linkErr) {
          console.error("Link person to patient failed", linkErr);
        }
      }

      for (const stagedEvent of stagedEvents) {
        try {
          await apiRequest(PATIENT_EVENTS_API, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              patient_id: patient.patient_id,
              event: stagedEvent.event,
              event_date: stagedEvent.event_date,
              injury_event_type_id: Number(stagedEvent.injury_event_type_id),
            }),
          });
        } catch (eventErr) {
          console.error("Create patient event failed", eventErr);
        }
      }

      for (const phoneRow of phones) {
        const phoneDigits = normalizePhoneDigits(phoneRow.phone);
        const phoneTypeId = Number(phoneRow?.phone_type?.phone_type_id ?? phoneRow?.phone_type_id);

        if (!phoneDigits || Number.isNaN(phoneTypeId) || phoneTypeId <= 0) {
          continue;
        }

        try {
          await postPatientLinkedRecord(
            PATIENT_PHONES_API,
            {
              phone: phoneDigits,
              phone_type_id: phoneTypeId,
            },
            patient.patient_id
          );
        } catch (phoneErr) {
          console.error("Create patient phone failed", phoneErr);
        }
      }

      for (const address of addresses) {
        const addressTypeId = Number(address?.address_type?.address_type_id ?? address?.address_type_id);
        if (Number.isNaN(addressTypeId) || addressTypeId <= 0) {
          continue;
        }

        try {
          await postPatientLinkedRecord(
            PATIENT_ADDRESSES_API,
            {
              street_1: address?.street_1 ?? "",
              street_2: address?.street_2 ?? "",
              city: address?.city ?? "",
              st: address?.st ?? "",
              zip: address?.zip ?? "",
              country: address?.country ?? "",
              address_type_id: addressTypeId,
            },
            patient.patient_id
          );
        } catch (addressErr) {
          console.error("Create patient address failed", addressErr);
        }
      }

      // 3️⃣ Attach company locally for UI
      const company = companies.find(c => c.company_id === Number(companyId));

      const patientWithCompany = {
        ...patient,
        middle_name: patient?.middle_name ?? middleName,
        test_mode: Boolean(patient?.test_mode),
        phones,
        addresses,
        companies: company
          ? [{
              company_patient_id: Date.now(), // temp UI id
              company,
              is_active: true,
            }]
          : [],
      };

      onCreated(patientWithCompany);
    } catch (err) {
      console.error("Create patient failed", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal modern patient-modal">
        <div className="modal-header">
          <h3>ADD NEW {patientLabels.singular.toUpperCase()}</h3>
          <button className="icon-close" onClick={onClose}>✕</button>
        </div>

        <div className="patient-profile-layout">
          <div className="patient-profile-left">
            <div className="patient-profile-identity-card">
              <div className="patient-profile-avatar">
                {`${firstName?.[0] ?? ""}${lastName?.[0] ?? ""}`.toUpperCase() || "P"}
              </div>
              <div className="patient-profile-name">
                {[firstName, middleName, lastName].filter(Boolean).join(" ") || `New ${patientLabels.singular}`}
              </div>
            </div>

            <div className="patient-profile-fields-card">
              <div className="patient-profile-field-row">
                <span className="patient-profile-field-label">First Name</span>
                <div className="patient-profile-field-value">
                  <input value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                </div>
              </div>

              <div className="patient-profile-field-row">
                <span className="patient-profile-field-label">Middle Name</span>
                <div className="patient-profile-field-value">
                  <input value={middleName} onChange={(e) => setMiddleName(e.target.value)} />
                </div>
              </div>

              <div className="patient-profile-field-row">
                <span className="patient-profile-field-label">Last Name</span>
                <div className="patient-profile-field-value">
                  <input value={lastName} onChange={(e) => setLastName(e.target.value)} />
                </div>
              </div>

              <div className="patient-profile-field-row">
                <span className="patient-profile-field-label">Email</span>
                <div className="patient-profile-field-value">
                  <input value={email} onChange={(e) => setEmail(e.target.value)} />
                </div>
              </div>

              <div className="patient-profile-field-row">
                <span className="patient-profile-field-label">Date of Birth</span>
                <div className="patient-profile-field-value">
                  <input type="date" value={dob} onChange={(e) => setDob(e.target.value)} />
                </div>
              </div>

              <div className="patient-profile-field-row">
                <span className="patient-profile-field-label">{patientLabels.singular} Type</span>
                <div className="patient-profile-field-value">
                  <select value={patientTypeId} onChange={(e) => setPatientTypeId(e.target.value)}>
                    <option value="">Select type</option>
                    {patientTypes.map((pt) => (
                      <option key={pt.patient_type_id} value={pt.patient_type_id}>
                        {pt.description}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="patient-profile-field-row">
                <span className="patient-profile-field-label">Company</span>
                <div className="patient-profile-field-value">
                  <select
                    value={companyId}
                    onChange={(e) => setCompanyId(e.target.value)}
                    disabled={Number(restrictedCompanyId) > 0}
                  >
                    <option value="">Select company</option>
                    {companies.map((company) => (
                      <option key={company.company_id} value={company.company_id}>
                        {company.company_name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="patient-profile-field-row">
                <span className="patient-profile-field-label">Company Patient ID</span>
                <div className="patient-profile-field-value">
                  <input value={companyPatientId} onChange={(e) => setCompanyPatientId(e.target.value)} />
                </div>
              </div>

              <div className="patient-profile-field-row">
                <span className="patient-profile-field-label">Status</span>
                <div className="patient-profile-field-value">
                  <label className="checkbox-row">
                    <input
                      type="checkbox"
                      checked={isActive}
                      onChange={(e) => setIsActive(e.target.checked)}
                    />
                    <span>{isActive ? "Active" : "Inactive"}</span>
                  </label>
                </div>
              </div>

              <div className="patient-profile-field-row">
                <span className="patient-profile-field-label">Preferred Language</span>
                <div className="patient-profile-field-value">
                  <select
                    value={preferredLanguageCode}
                    onChange={(e) => setPreferredLanguageCode(e.target.value)}
                  >
                    <option value="">Select language</option>
                    {languages.map((language) => (
                      <option key={language.code} value={language.code}>
                        {language.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div className="patient-profile-right">
            <div className="patient-data-card">
              <button
                type="button"
                className="patient-data-card-header patient-data-card-header-toggle"
                onClick={() => setIsPhonesSectionOpen((prev) => !prev)}
                aria-expanded={isPhonesSectionOpen}
              >
                <span>Phones</span>
                <span className="patient-card-chevron">{isPhonesSectionOpen ? "▾" : "▸"}</span>
              </button>

              {isPhonesSectionOpen && (
                <div className="patient-data-card-body">
                  {phones.length === 0 ? (
                    <p className="people-empty">No phones added.</p>
                  ) : (
                    <div className="user-phones-grid">
                      {phones.map((phoneRow) => (
                        <div key={getPhoneRowKey(phoneRow) ?? `phone-${phoneRow.phone ?? ""}`} className="user-phone-card">
                          <div className="user-phone-card-top">
                            <strong>
                              {phoneRow?.phone_type?.description ||
                                phoneTypes.find(
                                  (phoneType) =>
                                    Number(phoneType.phone_type_id) ===
                                    Number(phoneRow?.phone_type?.phone_type_id ?? phoneRow?.phone_type_id)
                                )?.description ||
                                "Phone"}
                            </strong>

                            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                              <button
                                type="button"
                                className="icon-edit"
                                title="Edit phone"
                                onClick={() => openEditPhoneModal(phoneRow)}
                              >
                                ✏️
                              </button>
                              <FaTrash
                                className="icon-button"
                                style={{ color: "#dc2626" }}
                                onClick={() => handleRequestDeletePhone(getPhoneRowKey(phoneRow))}
                              />
                            </div>
                          </div>

                          <div className="user-phone-card-body">
                            <p>{formatPhoneForInput(phoneRow.phone) || "—"}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <button type="button" className="user-address-add-btn" onClick={openAddPhoneModal}>
                    + Add Phone
                  </button>
                </div>
              )}
            </div>

            <div className="patient-data-card people-section">
              <div className="collapsible-section-header patient-data-card-header-row">
                <button
                  type="button"
                  className="collapsible-section-toggle"
                  aria-expanded={isPartnersSectionOpen}
                  onClick={() => setIsPartnersSectionOpen((prev) => !prev)}
                >
                  <span>Associated Partners</span>
                  <span className="patient-card-chevron">{isPartnersSectionOpen ? "▾" : "▸"}</span>
                </button>
              </div>

              {isPartnersSectionOpen && (
                <div className="patient-data-card-body">
                  {stagedPeople.length === 0 ? (
                    <div className="people-empty">No people added yet.</div>
                  ) : (
                    <div className="people-list">
                      {stagedPeople.map((person) => (
                        <div className="people-row" key={person.person_id}>
                          <div>
                            <div className="people-name">{getPersonDisplayName(person)}</div>
                            <div className="people-type">{getPersonTypeDescription(person)}</div>
                          </div>
                          <button
                            type="button"
                            className="icon-remove"
                            title="Remove person"
                            style={{ color: "#dc2626" }}
                            onClick={() => handleRemoveStagedPerson(person.person_id)}
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <button
                    type="button"
                    className="user-address-add-btn"
                    onClick={() => setShowPersonModal(true)}
                    disabled={!companyId}
                    title={!companyId ? "Select company first" : "Add partner"}
                  >
                    + Add Partner
                  </button>
                </div>
              )}
            </div>

            <div className="patient-data-card user-addresses-section">
              <div className="collapsible-section-header patient-data-card-header-row">
                <button
                  type="button"
                  className="collapsible-section-toggle"
                  aria-expanded={isAddressesSectionOpen}
                  onClick={() => setIsAddressesSectionOpen((prev) => !prev)}
                >
                  <span>Addresses</span>
                  <span className="patient-card-chevron">{isAddressesSectionOpen ? "▾" : "▸"}</span>
                </button>
              </div>

              {isAddressesSectionOpen && (
                <div className="patient-data-card-body">
                  {addresses.length === 0 ? (
                    <p className="user-addresses-empty">No addresses added.</p>
                  ) : (
                    <div className="user-addresses-grid">
                      {addresses.map((address) => (
                        <div key={getAddressRowKey(address)} className="user-address-card">
                          <div className="user-address-card-top">
                            <strong>{address?.address_type?.description || "Address"}</strong>
                            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                              <button
                                type="button"
                                className="icon-edit"
                                title="Edit address"
                                onClick={() => openEditAddressModal(address)}
                              >
                                ✏️
                              </button>
                              <FaTrash
                                className="icon-button"
                                style={{ color: "#dc2626" }}
                                onClick={() => handleRequestDeleteAddress(getAddressRowKey(address))}
                              />
                            </div>
                          </div>

                          <div className="user-address-card-body">
                            <p>{address?.street_1 || "—"}</p>
                            {address?.street_2?.trim() && <p>{address.street_2}</p>}
                            <p>
                              {address?.city || ""}
                              {address?.city && address?.st ? ", " : ""}
                              {address?.st || ""}
                              {(address?.city || address?.st) && address?.zip ? " " : ""}
                              {address?.zip || (!(address?.city || address?.st) ? "—" : "")}
                            </p>
                            <p>{address?.country || "—"}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <button type="button" className="user-address-add-btn" onClick={openAddAddressModal}>
                    + Add Address
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="patient-data-card events-section events-section-full-width">
              <div className="collapsible-section-header patient-data-card-header-row">
                <button
                  type="button"
                  className="collapsible-section-toggle"
                  aria-expanded={isEventsSectionOpen}
                  onClick={() => setIsEventsSectionOpen((prev) => !prev)}
                >
                  <span>Injury Events</span>
                  <span className="patient-card-chevron">{isEventsSectionOpen ? "▾" : "▸"}</span>
                </button>
              </div>

              {isEventsSectionOpen && (
                <div className="patient-data-card-body">
                  {stagedEvents.length === 0 ? (
                    <div className="people-empty">No injury events added yet.</div>
                  ) : (
                    <div className="events-table-wrap">
                      <table className="events-table">
                        <colgroup>
                          <col className="events-col-event" />
                          <col className="events-col-type" />
                          <col className="events-col-date" />
                          <col className="events-col-actions" />
                        </colgroup>
                        <thead>
                          <tr>
                            <th>Event</th>
                            <th>Event Type</th>
                            <th>Event Date</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {stagedEvents.map((eventItem, index) => (
                            <tr key={`${eventItem.event}-${eventItem.event_date}-${index}`}>
                              <td>{eventItem.event || "—"}</td>
                              <td>{getInjuryEventTypeDescription(eventItem)}</td>
                              <td>{formatEventDate(eventItem.event_date)}</td>
                              <td className="event-actions-cell">
                                <div className="event-actions">
                                  <button
                                    type="button"
                                    className="icon-edit"
                                    title="Edit injury event"
                                    onClick={() => handleOpenEditEvent(index)}
                                  >
                                    ✏️
                                  </button>
                                  <button
                                    type="button"
                                    className="icon-remove"
                                    title="Delete injury event"
                                    onClick={() => handleOpenDeleteEvent(index)}
                                  >
                                    <FaTrash className="icon-button" style={{ color: "#dc2626" }} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  <button type="button" className="user-address-add-btn" onClick={handleOpenAddEvent}>
                    + Add Injury Event
                  </button>
                </div>
              )}
          </div>

          {canManageTestMode && (
            <div className="patient-test-mode-field">
              <label htmlFor="new-patient-test-mode">
                <span>Point this patient to the DEV Environment</span>
                <input
                  id="new-patient-test-mode"
                  type="checkbox"
                  checked={testMode}
                  onChange={(event) => setTestMode(event.target.checked)}
                />
              </label>
            </div>
          )}
        </div>

        <div className="modal-actions">
          <button onClick={onClose}>Cancel</button>
          <button className="primary" disabled={saving} onClick={handleSave}>
            {saving ? "Saving..." : `Create ${patientLabels.singular}`}
          </button>
        </div>

        {showPersonModal && (
          <PatientPersonModal
            companyId={Number(companyId)}
            linkedPersonIds={stagedPeople.map((p) => p.person_id)}
            onClose={() => setShowPersonModal(false)}
            onPersonAdded={({ person }) => {
              if (!person) {
                setShowPersonModal(false);
                return;
              }

              setStagedPeople((prev) => {
                if (prev.some((p) => p.person_id === person.person_id)) {
                  return prev;
                }
                return [...prev, person];
              });

              setShowPersonModal(false);
            }}
          />
        )}

        {showEventModal && (
          <PatientEventModal
            title={editingEventIndex !== null ? "Edit Injury Event" : "Add Injury Event"}
            initialEvent={editingEventIndex !== null ? stagedEvents[editingEventIndex] : null}
            saving={saving}
            onCancel={() => {
              if (saving) return;
              setShowEventModal(false);
              setEditingEventIndex(null);
            }}
            onSave={handleSaveStagedEvent}
            injuryEventTypes={injuryEventTypes}
          />
        )}

        {isDeleteEventOpen && deletingEventIndex !== null && (
          <ConfirmActionModal
            title="Delete Injury Event"
            message="Are you sure you want to delete this injury event?"
            confirmLabel="Yes, Delete"
            onCancel={() => {
              setIsDeleteEventOpen(false);
              setDeletingEventIndex(null);
            }}
            onConfirm={handleDeleteStagedEvent}
          />
        )}

        {showPhoneModal && (
          <div className="modal-overlay" style={{ zIndex: 1300 }}>
            <div className="modal modern" style={{ width: "520px" }}>
              <div className="modal-header">
                <h3>{editingPhoneKey !== null ? "Edit Phone" : "Add Phone"}</h3>
                <button className="icon-close" onClick={() => setShowPhoneModal(false)}>✕</button>
              </div>

              <div className="details-grid">
                <Detail label="Phone">
                  <input
                    inputMode="numeric"
                    value={formatPhoneForInput(newPhoneData.phone)}
                    onChange={(e) =>
                      setNewPhoneData((prev) => ({
                        ...prev,
                        phone: normalizePhoneDigits(e.target.value),
                      }))
                    }
                  />
                </Detail>
                <Detail label="Phone Type">
                  <select
                    value={newPhoneData.phone_type_id}
                    onChange={(e) =>
                      setNewPhoneData((prev) => ({ ...prev, phone_type_id: e.target.value }))
                    }
                  >
                    <option value="">Select Type</option>
                    {phoneTypes.map((phoneType) => (
                      <option key={phoneType.phone_type_id} value={phoneType.phone_type_id}>
                        {phoneType.description}
                      </option>
                    ))}
                  </select>
                </Detail>
              </div>

              <div className="modal-actions">
                {phoneError && <p style={{ color: "#dc2626", marginRight: "auto" }}>{phoneError}</p>}
                <button onClick={() => setShowPhoneModal(false)}>Cancel</button>
                <button className="primary" onClick={handleSavePhoneFromModal}>
                  {editingPhoneKey !== null ? "Save Phone" : "Add Phone"}
                </button>
              </div>
            </div>
          </div>
        )}

        {isDeletePhoneOpen && (
          <div className="modal-overlay" style={{ zIndex: 1350 }}>
            <div className="modal modern" style={{ width: "460px" }}>
              <div className="modal-header">
                <h3>Delete Phone</h3>
                <button
                  className="icon-close"
                  onClick={() => {
                    setIsDeletePhoneOpen(false);
                    setDeletingPhoneKey(null);
                  }}
                >
                  ✕
                </button>
              </div>

              <p style={{ marginBottom: "18px", color: "#444" }}>
                Are you sure you want to delete this phone?
              </p>

              <div className="modal-actions">
                <button
                  onClick={() => {
                    setIsDeletePhoneOpen(false);
                    setDeletingPhoneKey(null);
                  }}
                >
                  Cancel
                </button>
                <button className="primary" onClick={handleConfirmDeletePhone}>
                  Yes, Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {showAddressModal && (
          <div className="modal-overlay" style={{ zIndex: 1300 }}>
            <div className="modal modern" style={{ width: "560px" }}>
              <div className="modal-header">
                <h3>{editingAddressKey !== null ? "Edit Address" : "Add Address"}</h3>
                <button className="icon-close" onClick={() => setShowAddressModal(false)}>✕</button>
              </div>

              <div className="details-grid">
                <Detail label="Street 1">
                  <input
                    value={newAddressData.street_1}
                    onChange={(e) => setNewAddressData((prev) => ({ ...prev, street_1: e.target.value }))}
                  />
                </Detail>
                <Detail label="Street 2">
                  <input
                    value={newAddressData.street_2}
                    onChange={(e) => setNewAddressData((prev) => ({ ...prev, street_2: e.target.value }))}
                  />
                </Detail>
                <Detail label="City">
                  <input
                    value={newAddressData.city}
                    onChange={(e) => setNewAddressData((prev) => ({ ...prev, city: e.target.value }))}
                  />
                </Detail>
                <Detail label="State">
                  <select
                    value={newAddressData.st}
                    onChange={(e) => setNewAddressData((prev) => ({ ...prev, st: e.target.value }))}
                  >
                    <option value="">Select State</option>
                    {US_STATES.map((stateOption) => (
                      <option key={stateOption.value} value={stateOption.value}>
                        {stateOption.label}
                      </option>
                    ))}
                  </select>
                </Detail>
                <Detail label="Zip">
                  <input
                    value={newAddressData.zip}
                    onChange={(e) => setNewAddressData((prev) => ({ ...prev, zip: e.target.value }))}
                  />
                </Detail>
                <Detail label="Country">
                  <select
                    value={newAddressData.country}
                    onChange={(e) => setNewAddressData((prev) => ({ ...prev, country: e.target.value }))}
                  >
                    {COUNTRY_OPTIONS.map((country) => (
                      <option key={country} value={country}>
                        {country}
                      </option>
                    ))}
                  </select>
                </Detail>
                <Detail label="Address Type">
                  <select
                    value={newAddressData.address_type_id}
                    onChange={(e) => setNewAddressData((prev) => ({ ...prev, address_type_id: e.target.value }))}
                  >
                    <option value="">Select Type</option>
                    {addressTypes.map((addressType) => (
                      <option key={addressType.address_type_id} value={addressType.address_type_id}>
                        {addressType.description}
                      </option>
                    ))}
                  </select>
                </Detail>
              </div>

              <div className="modal-actions">
                {addressError && <p style={{ color: "#dc2626", marginRight: "auto" }}>{addressError}</p>}
                <button onClick={() => setShowAddressModal(false)}>Cancel</button>
                <button className="primary" onClick={handleSaveAddressFromModal}>
                  {editingAddressKey !== null ? "Save Address" : "Add Address"}
                </button>
              </div>
            </div>
          </div>
        )}

        {isDeleteAddressOpen && (
          <div className="modal-overlay" style={{ zIndex: 1350 }}>
            <div className="modal modern" style={{ width: "460px" }}>
              <div className="modal-header">
                <h3>Delete Address</h3>
                <button
                  className="icon-close"
                  onClick={() => {
                    setIsDeleteAddressOpen(false);
                    setDeletingAddressKey(null);
                  }}
                >
                  ✕
                </button>
              </div>

              <p style={{ marginBottom: "18px", color: "#444" }}>
                Are you sure you want to delete this address?
              </p>

              <div className="modal-actions">
                <button
                  onClick={() => {
                    setIsDeleteAddressOpen(false);
                    setDeletingAddressKey(null);
                  }}
                >
                  Cancel
                </button>
                <button className="primary" onClick={handleConfirmDeleteAddress}>
                  Yes, Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const PatientEventModal = ({
  title,
  initialEvent,
  saving,
  onCancel,
  onSave,
  injuryEventTypes = [],
}) => {
  const [eventText, setEventText] = useState(initialEvent?.event || "");
  const [eventDate, setEventDate] = useState(getDateOnlyInputValue(initialEvent?.event_date));
  const [injuryEventTypeId, setInjuryEventTypeId] = useState(
    String(getInjuryEventTypeId(initialEvent))
  );

  useEffect(() => {
    setEventText(initialEvent?.event || "");
    setEventDate(getDateOnlyInputValue(initialEvent?.event_date));
    setInjuryEventTypeId(String(getInjuryEventTypeId(initialEvent)));
  }, [initialEvent]);

  const handleSubmit = () => {
    if (!eventText || !eventDate || !injuryEventTypeId) return;
    onSave({ event: eventText, eventDate, injuryEventTypeId });
  };

  return (
    <div className="modal-overlay">
      <div className="modal modern event-modal">
        <div className="modal-header">
          <h3>{title}</h3>
          <button className="icon-close" onClick={onCancel}>✕</button>
        </div>

        <div className="details-grid">
          <Detail label="Event Date">
            <input
              type="date"
              value={eventDate}
              onChange={(e) => setEventDate(e.target.value)}
            />
          </Detail>

          <Detail label="Injury Event Type">
            <select
              value={injuryEventTypeId}
              onChange={(e) => setInjuryEventTypeId(e.target.value)}
            >
              <option value="">Select Type</option>
              {injuryEventTypes.map((eventType) => (
                <option
                  key={eventType.injury_event_type_id}
                  value={eventType.injury_event_type_id}
                >
                  {eventType.description}
                </option>
              ))}
            </select>
          </Detail>

          <Detail label="Injury Event Description" style={{ gridColumn: "1 / -1" }}>
            <textarea
              value={eventText}
              onChange={(e) => setEventText(e.target.value)}
              rows={4}
            />
          </Detail>
        </div>

        <div className="modal-actions">
          <button type="button" onClick={onCancel} disabled={saving}>Cancel</button>
          <button
            className="primary"
            type="button"
            disabled={saving || !eventText || !eventDate || !injuryEventTypeId}
            onClick={handleSubmit}
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

const ConfirmActionModal = ({
  title,
  message,
  confirmLabel,
  onCancel,
  onConfirm,
  disabled = false,
}) => (
  <div
    style={{
      position: "fixed",
      inset: 0,
      backgroundColor: "rgba(0,0,0,0.4)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 1300,
    }}
  >
    <div
      style={{
        background: "#fff",
        padding: "24px",
        width: "440px",
        borderRadius: "8px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
      }}
    >
      <h3 style={{ marginBottom: "12px", color: "#dc3545" }}>{title}</h3>

      <p style={{ marginBottom: "20px", color: "#444" }}>{message}</p>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
        <button onClick={onCancel} disabled={disabled}>Cancel</button>

        <button
          style={{
            background: "#dc3545",
            color: "#fff",
            border: "none",
            padding: "6px 12px",
            borderRadius: "4px",
            cursor: disabled ? "not-allowed" : "pointer",
            opacity: disabled ? 0.7 : 1,
          }}
          onClick={onConfirm}
          disabled={disabled}
        >
          {confirmLabel}
        </button>
      </div>
    </div>
  </div>
);

const PatientPersonModal = ({ companyId, patientId, linkedPersonIds, onClose, onPersonAdded }) => {
  const [people, setPeople] = useState([]);
  const [personTypes, setPersonTypes] = useState([]);
  const [companyPeopleLinks, setCompanyPeopleLinks] = useState([]);
  const [selectedPersonId, setSelectedPersonId] = useState("");

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [personTypeId, setPersonTypeId] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      apiRequest(PEOPLE_API).then((r) => r.json()),
      apiRequest(PERSON_TYPES_API).then((r) => r.json()),
      apiRequest(COMPANY_PEOPLE_API).then((r) => r.json()),
    ])
      .then(([peopleData, personTypeData, companyPeopleData]) => {
        setPeople(peopleData);
        setPersonTypes(personTypeData);
        setCompanyPeopleLinks(companyPeopleData);
      })
      .catch(console.error);
  }, []);

  const companyPersonIds = companyPeopleLinks
    .filter((cp) => getCompanyIdFromCompanyPerson(cp) === Number(companyId))
    .map((cp) => getPersonIdFromCompanyPerson(cp));

  const availablePeople = people.filter((person) => {
    const personId = Number(person.person_id);
    return companyPersonIds.includes(personId) && !linkedPersonIds.includes(personId);
  });

  const ensureCompanyPersonLink = async (personId) => {
    if (!companyId || !personId) return;
    if (companyPersonIds.includes(Number(personId))) return;

    await apiRequest(COMPANY_PEOPLE_API, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        company_id: companyId,
        person_id: personId,
      }),
    });
  };

  const handleAddPerson = async () => {
    setSaving(true);

    try {
      let personToAdd = null;
      let personIdToUse = selectedPersonId ? Number(selectedPersonId) : null;

      if (!personIdToUse) {
        const createPersonRes = await apiRequest(PEOPLE_API, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            first_name: firstName,
            last_name: lastName,
            email,
            phone,
            person_type_id: personTypeId ? Number(personTypeId) : null,
          }),
        });

        personToAdd = await createPersonRes.json();
        personIdToUse = Number(personToAdd.person_id);
      } else {
        personToAdd = people.find((p) => Number(p.person_id) === personIdToUse) || null;
      }

      await ensureCompanyPersonLink(personIdToUse);

      if (patientId && personIdToUse) {
        await apiRequest(PATIENT_PEOPLE_API, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            patient_id: patientId,
            person_id: personIdToUse,
          }),
        });
      }

      if (personToAdd && personTypeId && !personToAdd.person_type) {
        const typeMatch = personTypes.find(
          (type) => Number(type.person_type_id) === Number(personTypeId)
        );
        if (typeMatch) {
          personToAdd = {
            ...personToAdd,
            person_type: typeMatch,
          };
        }
      }

      onPersonAdded({ person: personToAdd, personId: personIdToUse });
    } catch (err) {
      console.error("Add person failed", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal modern person-modal">
        <div className="modal-header">
          <h3>Partner</h3>
          <button className="icon-close" onClick={onClose}>✕</button>
        </div>

        <div className="details-grid">
          <Detail label="Existing Partner">
            <select value={selectedPersonId} onChange={(e) => setSelectedPersonId(e.target.value)}>
              <option value="">Select partner</option>
              {availablePeople.map((person) => (
                <option key={person.person_id} value={person.person_id}>
                  {getPersonDisplayName(person)} ({getPersonTypeDescription(person)})
                </option>
              ))}
            </select>
          </Detail>

          <Detail label="First Name">
            <input
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              disabled={!!selectedPersonId}
            />
          </Detail>

          <Detail label="Last Name">
            <input
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              disabled={!!selectedPersonId}
            />
          </Detail>

          <Detail label="Email">
            <input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={!!selectedPersonId}
            />
          </Detail>

          <Detail label="Phone">
            <input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              disabled={!!selectedPersonId}
            />
          </Detail>

          <Detail label="Partner Type">
            <select
              value={personTypeId}
              onChange={(e) => setPersonTypeId(e.target.value)}
              disabled={!!selectedPersonId}
            >
              <option value="">Select type</option>
              {personTypes.map((pt) => (
                <option key={pt.person_type_id} value={pt.person_type_id}>
                  {pt.description}
                </option>
              ))}
            </select>
          </Detail>
        </div>

        <div className="modal-actions">
          <button onClick={onClose}>Cancel</button>
          <button className="primary" disabled={saving || !companyId} onClick={handleAddPerson}>
            {saving ? "Saving..." : "Add Partner"}
          </button>
        </div>
      </div>
    </div>
  );
};

const PatientAssessmentsModal = ({ patient, userTypeId, onClose }) => {
  const [attempts, setAttempts] = useState([]);
  const [tokenExpirationByAttemptId, setTokenExpirationByAttemptId] = useState({});
  const [attemptProgressByAttemptId, setAttemptProgressByAttemptId] = useState({});
  const [refreshingAttemptByKey, setRefreshingAttemptByKey] = useState({});
  const [linkFeedbackByAttemptKey, setLinkFeedbackByAttemptKey] = useState({});
  const [assessments, setAssessments] = useState([]);
  const [patientEvents, setPatientEvents] = useState([]);
  const [selectedPatientEventId, setSelectedPatientEventId] = useState("");
  const [isEventDropdownOpen, setIsEventDropdownOpen] = useState(false);
  const eventDropdownRef = useRef(null);
  const linkFeedbackTimeoutsRef = useRef({});
  const [selectedAssessmentId, setSelectedAssessmentId] = useState("");
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [showRemoved, setShowRemoved] = useState(false);
  const [isDeleteAssessmentOpen, setIsDeleteAssessmentOpen] = useState(false);
  const [deletingAssessmentAttempt, setDeletingAssessmentAttempt] = useState(null);
  const [deletingAssessmentInProgress, setDeletingAssessmentInProgress] = useState(false);
  const [showAnswersModal, setShowAnswersModal] = useState(false);
  const [selectedAnswersAttempt, setSelectedAnswersAttempt] = useState(null);
  const [answersLoading, setAnswersLoading] = useState(false);
  const [answersError, setAnswersError] = useState("");
  const [answerRows, setAnswerRows] = useState([]);
  const [answerDocumentLoadingId, setAnswerDocumentLoadingId] = useState(null);
  const [answerDocumentError, setAnswerDocumentError] = useState("");
  const [loadingAnswersAttemptId, setLoadingAnswersAttemptId] = useState(null);
  const [showAttemptLogsModal, setShowAttemptLogsModal] = useState(false);
  const [selectedLogsAttempt, setSelectedLogsAttempt] = useState(null);
  const [attemptLogs, setAttemptLogs] = useState([]);
  const [attemptLogsLoading, setAttemptLogsLoading] = useState(false);
  const [attemptLogsError, setAttemptLogsError] = useState("");
  const [expandedAttemptLogRows, setExpandedAttemptLogRows] = useState(() => new Set());
  const [rotatingTokenAttemptId, setRotatingTokenAttemptId] = useState(null);
  const [showGeneratedReportModal, setShowGeneratedReportModal] = useState(false);
  const [selectedReportAttempt, setSelectedReportAttempt] = useState(null);
  const [selectedReportFields, setSelectedReportFields] = useState(null);
  const [selectedReportHtml, setSelectedReportHtml] = useState("");
  const [selectedReportDocumentUrl, setSelectedReportDocumentUrl] = useState("");
  const [selectedReportDocumentName, setSelectedReportDocumentName] = useState("");
  const [loadingGeneratedPreview, setLoadingGeneratedPreview] = useState(null);
  const [selectedGeneratedPreviewType, setSelectedGeneratedPreviewType] = useState("report");
  const [isDownloadingGeneratedReport, setIsDownloadingGeneratedReport] = useState(false);
  const generatedReportRef = useRef(null);
  const generatedReportIframeRef = useRef(null);

  const useClientTerminology = shouldUseClientTerminology();
  const patientLabels = getPatientLabels(useClientTerminology);
  const primaryCompanyId = Number(
    patient?.companies?.[0]?.company?.company_id ??
    patient?.companies?.[0]?.company_id ??
    patient?.companies?.[0]?.company?.id ??
    0
  );
  const primaryCompanyName =
    String(
      patient?.companies?.[0]?.company?.company_name ??
      patient?.companies?.[0]?.company_name ??
      ""
    ).trim() || "your care team";
  const assessmentAppBaseUrl = useMemo(() => {
    const configuredBase = String(process.env.REACT_APP_APP_URL_BASE || "").trim();
    const fallbackBase = String(window?.location?.origin || "http://localhost:3000").trim();
    return (configuredBase || fallbackBase).replace(/\/$/, "");
  }, []);

  const buildAssessmentLink = useCallback((tokenValue) => {
    const sanitizedToken = String(tokenValue || "").trim();
    return `${assessmentAppBaseUrl}/take-assessment/${sanitizedToken}`;
  }, [assessmentAppBaseUrl]);

  const handleDownloadGeneratedReportPdf = useCallback(async () => {
    const isInvoicePreview = selectedGeneratedPreviewType === "invoice";

    if (isDownloadingGeneratedReport) return;

    if (!isInvoicePreview && selectedReportDocumentUrl) {
      setIsDownloadingGeneratedReport(true);

      try {
        const response = await fetch(selectedReportDocumentUrl);
        if (!response.ok) {
          throw new Error(`Stored report download failed (${response.status}).`);
        }

        const reportBlob = await response.blob();
        const blobUrl = URL.createObjectURL(reportBlob);
        const downloadLink = document.createElement("a");
        downloadLink.href = blobUrl;
        downloadLink.download = selectedReportDocumentName || "assessment-report.pdf";
        document.body.appendChild(downloadLink);
        downloadLink.click();
        downloadLink.remove();
        URL.revokeObjectURL(blobUrl);
      } catch (error) {
        console.error("Failed to download stored report PDF", error);
        const downloadLink = document.createElement("a");
        downloadLink.href = selectedReportDocumentUrl;
        downloadLink.target = "_blank";
        downloadLink.rel = "noopener noreferrer";
        downloadLink.download = selectedReportDocumentName || "assessment-report.pdf";
        document.body.appendChild(downloadLink);
        downloadLink.click();
        downloadLink.remove();
      } finally {
        setIsDownloadingGeneratedReport(false);
      }

      return;
    }

    const reportDocument = generatedReportIframeRef.current?.contentDocument;
    const reportElement = isInvoicePreview
      ? generatedReportRef.current
      : reportDocument?.body?.firstElementChild ?? reportDocument?.body;

    if (!reportElement) return;

    setIsDownloadingGeneratedReport(true);

    try {
      const pageBackgroundRgb = getPdfPageBackgroundRgb(reportElement);
      const reportBackgroundColor = `rgb(${pageBackgroundRgb.r}, ${pageBackgroundRgb.g}, ${pageBackgroundRgb.b})`;
      const captureWidth = Math.ceil(
        Math.max(
          reportElement.scrollWidth || 0,
          reportElement.clientWidth || 0,
          reportElement.getBoundingClientRect().width || 0
        )
      );
      const captureHeight = Math.ceil(
        Math.max(
          reportElement.scrollHeight || 0,
          reportElement.clientHeight || 0,
          reportElement.getBoundingClientRect().height || 0
        )
      );

      const canvas = await html2canvas(reportElement, {
        scale: 1.5,
        useCORS: true,
        backgroundColor: reportBackgroundColor,
        logging: false,
        width: captureWidth,
        height: captureHeight,
        windowWidth: captureWidth,
        windowHeight: captureHeight,
      });

      const pageMarginMm = 12.7;
      const bottomPageMarginMm = 12.7;
      const topPageMarginMm = pageMarginMm;
      const sidePageMarginMm = pageMarginMm;

      const paginationHints = isInvoicePreview
        ? { forcedBreaks: [], avoidRegions: [], preferredBreaks: [] }
        : getPdfPaginationHints(reportElement);
      const hintScaleFactor = isInvoicePreview
        ? 1
        : canvas.width / Math.max(1, reportElement.scrollWidth || reportElement.clientWidth || canvas.width);
      const scaledPaginationHints = {
        forcedBreaks: paginationHints.forcedBreaks.map((value) => Math.round(value * hintScaleFactor)),
        avoidRegions: paginationHints.avoidRegions.map((region) => ({
          top: Math.round(region.top * hintScaleFactor),
          bottom: Math.round(region.bottom * hintScaleFactor),
          height: Math.round(region.height * hintScaleFactor),
          preferTopBreak: Boolean(region.preferTopBreak),
        })),
        preferredBreaks: paginationHints.preferredBreaks.map((value) => Math.round(value * hintScaleFactor)),
      };

      let pdf;

      if (!isInvoicePreview) {
        pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "letter" });
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const contentWidth = pageWidth - sidePageMarginMm * 2;
        const pageContentHeight = pageHeight - topPageMarginMm - bottomPageMarginMm;
        const footerDisclaimer = "This report contains patient self-reported symptom data only. It does not constitute a medical diagnosis or treatment recommendation. All data submitted for licensed physician review.";

        const pageSlices = [];
        let currentTop = 0;

        while (currentTop < canvas.height) {
          const currentPageTop = currentTop;
          const minimumBreakTop = currentPageTop + 40;
          const pageContentHeightPx = Math.floor((canvas.width * pageContentHeight) / contentWidth);
          const remainingHeight = canvas.height - currentPageTop;
          if (remainingHeight <= pageContentHeightPx) {
            pageSlices.push({
              top: currentPageTop,
              height: remainingHeight,
              offsetTopMm: topPageMarginMm,
            });
            break;
          }

          const nominalBottom = currentPageTop + pageContentHeightPx;
          const forcedBreak = scaledPaginationHints.forcedBreaks.find(
            (candidate) => candidate > minimumBreakTop && candidate < nominalBottom
          );

          let nextBottom = forcedBreak || nominalBottom;
          const intersectingAvoidRegion = scaledPaginationHints.avoidRegions.find(
            (region) => region.top < nextBottom && region.bottom > nextBottom
          );

          if (intersectingAvoidRegion) {
            const shouldPreferRegionTop =
              intersectingAvoidRegion.preferTopBreak &&
              intersectingAvoidRegion.top > minimumBreakTop;

            const preferredBreakWithinRegion = shouldPreferRegionTop
              ? null
              : scaledPaginationHints.preferredBreaks
                  .filter(
                    (candidate) =>
                      candidate > Math.max(minimumBreakTop, intersectingAvoidRegion.top) &&
                      candidate < nominalBottom
                  )
                  .pop();

            if (shouldPreferRegionTop) {
              nextBottom = intersectingAvoidRegion.top;
            } else if (preferredBreakWithinRegion) {
              nextBottom = preferredBreakWithinRegion;
            }

            const canMoveUp =
              intersectingAvoidRegion.top - currentPageTop > pageContentHeightPx * 0.45;
            if (!shouldPreferRegionTop && !preferredBreakWithinRegion && canMoveUp) {
              nextBottom = intersectingAvoidRegion.top;
            }
          }

          if (nextBottom - currentPageTop < 40) {
            nextBottom = nominalBottom;
          }

          pageSlices.push({
            top: currentPageTop,
            height: nextBottom - currentPageTop,
            offsetTopMm: topPageMarginMm,
          });
          currentTop = nextBottom;
        }

        pageSlices.forEach((pageSlice, pageIndex) => {
          const pageCanvas = document.createElement("canvas");
          pageCanvas.width = canvas.width;
          pageCanvas.height = pageSlice.height;

          const pageContext = pageCanvas.getContext("2d");
          if (!pageContext) {
            throw new Error("Failed to create PDF page canvas context.");
          }

          pageContext.fillStyle = "#ffffff";
          pageContext.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
          pageContext.drawImage(
            canvas,
            0,
            pageSlice.top,
            canvas.width,
            pageSlice.height,
            0,
            0,
            canvas.width,
            pageSlice.height
          );

          const pageImageData = pageCanvas.toDataURL("image/jpeg", 0.92);
          const renderedSliceHeightMm = (pageSlice.height / canvas.width) * contentWidth;

          if (pageIndex > 0) {
            pdf.addPage();
          }

          pdf.setFillColor(pageBackgroundRgb.r, pageBackgroundRgb.g, pageBackgroundRgb.b);
          pdf.rect(0, 0, pageWidth, pageHeight, "F");

          pdf.addImage(
            pageImageData,
            "JPEG",
            sidePageMarginMm,
            pageSlice.offsetTopMm,
            contentWidth,
            renderedSliceHeightMm
          );

          pdf.setTextColor(71, 85, 105);
          pdf.setFontSize(5.5);
          const footerDisclaimerLines = pdf.splitTextToSize(
            footerDisclaimer,
            pageWidth - sidePageMarginMm * 2
          );
          const footerRegionTop = pageHeight - bottomPageMarginMm + 1.0;
          const footerLineHeightMm = 2.15;
          const footerGapMm = 1.6;
          const footerTextY = footerRegionTop;
          const footerPageNumberY =
            footerTextY +
            footerDisclaimerLines.length * footerLineHeightMm +
            footerGapMm;
          pdf.text(footerDisclaimerLines, pageWidth / 2, footerTextY, {
            align: "center",
            baseline: "top",
          });
          pdf.setFontSize(6);
          pdf.text(`Page ${pageIndex + 1} of ${pageSlices.length}`, pageWidth / 2, footerPageNumberY, {
            align: "center",
            baseline: "top",
          });
        });
      } else {
        const basePdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "letter" });
        const pageWidth = basePdf.internal.pageSize.getWidth();
        const renderedHeightMm = (canvas.height / canvas.width) * pageWidth;
        pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: [pageWidth, renderedHeightMm] });
        const imgData = canvas.toDataURL("image/jpeg", 0.92);
        pdf.addImage(imgData, "JPEG", 0, 0, pageWidth, renderedHeightMm);
      }

      const reportAssessmentId = getAttemptAssessmentId(selectedReportAttempt);
      const reportAssessmentName =
        selectedReportAttempt?.assessment?.name ??
        selectedReportAttempt?.assessment_name ??
        `Assessment #${reportAssessmentId || "—"}`;

      const assessmentName =
        String(
          reportAssessmentName ||
            (isInvoicePreview ? "CognitrackX_Invoice" : "CognitrackX_Report")
        )
          .trim()
          .replace(/\s+/g, "_")
          .replace(/[^a-zA-Z0-9_-]/g, "") ||
        (isInvoicePreview ? "CognitrackX_Invoice" : "CognitrackX_Report");
      const reportPatientName = `${patient?.first_name ?? ""} ${patient?.last_name ?? ""}`.trim();
      const patientFileName =
        String(reportPatientName || patient?.email || "Patient")
          .trim()
          .replace(/\s+/g, "_")
          .replace(/[^a-zA-Z0-9_-]/g, "") || "Patient";
      const dateStamp = new Date().toISOString().slice(0, 10);

      const documentLabel = isInvoicePreview ? "Invoice" : "Report";
      pdf.save(`${assessmentName}_${documentLabel}_${patientFileName}_${dateStamp}.pdf`);
    } catch (error) {
      console.error("Failed to download generated report PDF", error);
    } finally {
      setIsDownloadingGeneratedReport(false);
    }
  }, [
    isDownloadingGeneratedReport,
    patient?.email,
    patient?.first_name,
    patient?.last_name,
    selectedGeneratedPreviewType,
    selectedReportAttempt,
    selectedReportDocumentName,
    selectedReportDocumentUrl,
  ]);

  const sendAssessmentLinkEmail = useCallback(async ({ recipientEmail, assessmentLink }) => {
    const trimmedRecipientEmail = String(recipientEmail || "").trim();
    if (!trimmedRecipientEmail) {
      throw new Error("Patient email is missing.");
    }

    const preferredLanguageCode = String(
      patient?.preferred_language?.code ?? "en"
    )
      .trim()
      .toLowerCase();
    const isSpanish = preferredLanguageCode === "es";

    const emailHtml = buildAssessmentLinkEmailHtml({
      firstName: String(patient?.first_name || "").trim(),
      companyName: primaryCompanyName,
      assessmentLink,
      languageCode: preferredLanguageCode,
    });

    const basePayload = {
      to_addresses: [trimmedRecipientEmail],
      subject: isSpanish
        ? "Su evaluación CogniTrackX está lista – Acción requerida"
        : "Your CogniTrackX Assessment Is Ready – Action Required",
      body_html: emailHtml,
    };

    let response = await apiRequest(SEND_EMAIL_API, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(basePayload),
    });

    if (response.status === 404) {
      response = await apiRequest(SEND_EMAIL_API_ALT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(basePayload),
      });
    }

    if (response.ok) {
      return;
    }

    const apiError = await getResponseErrorMessage(response);
    const normalizedError = String(apiError || "");

    if (/MessageRejected|not verified|failed the check/i.test(normalizedError)) {
      throw new Error(
        "Send assessment email failed because SES rejected the identity. " +
          "Verify sender and recipient identities in SES for region US-WEST-2 or move the account out of SES sandbox. " +
          `API detail: ${normalizedError}`
      );
    }

    throw new Error(
      `Send assessment email failed with status ${response.status}: ${normalizedError}`
    );
  }, [patient?.first_name, patient?.preferred_language?.code, primaryCompanyName]);

  const getOrCreateAttemptToken = useCallback(async (attempt) => {
    const existingToken = getAttemptTokenValue(attempt);

    if (existingToken) {
      return existingToken;
    }

    const attemptId = getAttemptId(attempt);
    if (!attemptId) {
      throw new Error("Could not resolve attempt id for token generation.");
    }

    const tokenRes = await apiRequest(PATIENT_TOKENS_CREATE_API, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        patient_assessment_attempt: attemptId,
      }),
    });

    if (!tokenRes.ok) {
      throw new Error(`Token create failed with status ${tokenRes.status}`);
    }

    const tokenPayload = await tokenRes.json();
    const createdToken = String(tokenPayload?.token ?? tokenPayload?.value ?? "").trim();
    if (!createdToken) {
      throw new Error("Token create response did not include token value.");
    }

    return createdToken;
  }, []);

  const fetchTokenExpiration = useCallback(async (tokenValue) => {
    const normalizedToken = String(tokenValue ?? "").trim();
    if (!normalizedToken) return "";

    const response = await apiRequest(
      `${API_BASE}/api/patient-tokens/${encodeURIComponent(normalizedToken)}/expiration/`
    );
    if (!response.ok) {
      throw new Error(`Token expiration request failed with status ${response.status}`);
    }

    const payload = await response.json();
    return String(payload?.expires_at ?? "").trim();
  }, []);

  const loadAttemptsForPatient = useCallback(async () => {
    if (!patient?.patient_id) {
      setAttempts([]);
      return [];
    }

    const patientId = Number(patient.patient_id);
    const attemptsUrl = `${PATIENT_ASSESSMENT_ATTEMPTS_API}?patient=${patientId}`;
    const attemptsRes = await apiRequest(attemptsUrl);
    const attemptsData = await attemptsRes.json();
    const attemptRows = Array.isArray(attemptsData)
      ? attemptsData
      : Array.isArray(attemptsData?.results)
        ? attemptsData.results
        : [];

    const normalizedAttempts = attemptRows.map((attempt) => {
      const resolvedAttemptId = attempt.id ?? getAttemptId(attempt);
      return {
        ...attempt,
        patient_assessment_attempt_id:
          attempt.patient_assessment_attempt_id ?? resolvedAttemptId,
      };
    });

    setAttempts(normalizedAttempts);
    return normalizedAttempts;
  }, [patient?.patient_id]);

  const loadAssessments = useCallback(async () => {
    const assessmentsRes = await apiRequest(ASSESSMENTS_API);
    const assessmentsData = await assessmentsRes.json();
    setAssessments(assessmentsData);
  }, []);

  const loadPatientEvents = useCallback(async () => {
    if (!patient?.patient_id) {
      setPatientEvents([]);
      return;
    }

    const patientId = Number(patient.patient_id);
    const eventsUrl = `${PATIENT_EVENTS_API}?patient=${patientId}`;
    const eventsRes = await apiRequest(eventsUrl);
    const eventsData = await eventsRes.json();
    const filtered = Array.isArray(eventsData)
      ? eventsData
      : Array.isArray(eventsData?.results)
        ? eventsData.results
        : [];
    setPatientEvents(filtered);
  }, [patient?.patient_id]);

  const assignableAssessments = useMemo(() => {
    return assessments.filter((assessment) => {
      const assessmentId = Number(assessment.assessment_id);
      const matchingAttempts = attempts.filter(
        (attempt) => getAttemptAssessmentId(attempt) === assessmentId
      );

      if (matchingAttempts.length === 0) return true;

      const hasActiveAssignment = matchingAttempts.some(
        (attempt) => {
          const status = String(getAttemptStatus(attempt) ?? "").trim().toLowerCase();
          return status === "assigned" || status === "in_progress";
        }
      );

      return !hasActiveAssignment;
    });
  }, [assessments, attempts]);

  const displayedAttempts = useMemo(() => {
    const statusOrder = {
      assigned: 1,
      in_progress: 2,
      completed: 3,
      removed: 4,
    };

    const filteredAttempts = showRemoved
      ? attempts
      : attempts.filter((attempt) => getAttemptStatus(attempt) !== "removed");

    return [...filteredAttempts].sort((left, right) => {
      const leftStatus = String(getAttemptStatus(left)).toLowerCase();
      const rightStatus = String(getAttemptStatus(right)).toLowerCase();

      const leftPriority = statusOrder[leftStatus] ?? Number.MAX_SAFE_INTEGER;
      const rightPriority = statusOrder[rightStatus] ?? Number.MAX_SAFE_INTEGER;

      if (leftPriority !== rightPriority) {
        return leftPriority - rightPriority;
      }

      const leftAttemptId = Number(getAttemptId(left) ?? 0);
      const rightAttemptId = Number(getAttemptId(right) ?? 0);

      return rightAttemptId - leftAttemptId;
    });
  }, [attempts, showRemoved]);

  const inProgressAttemptIds = useMemo(() => {
    const ids = attempts
      .filter((attempt) => String(getAttemptStatus(attempt)).toLowerCase() === "in_progress")
      .map((attempt) => Number(getAttemptId(attempt)))
      .filter((attemptId) => Number.isFinite(attemptId) && attemptId > 0);

    return ids.sort((left, right) => left - right);
  }, [attempts]);

  const inProgressAttemptIdsKey = useMemo(
    () => inProgressAttemptIds.join(","),
    [inProgressAttemptIds]
  );

  useEffect(() => {
    if (!inProgressAttemptIdsKey) {
      setAttemptProgressByAttemptId({});
      return;
    }

    if (!patient?.patient_id) {
      setAttemptProgressByAttemptId({});
      return;
    }

    let cancelled = false;

    const loadAttemptProgress = async () => {
      try {
        const patientId = Number(patient.patient_id);
        const progressUrl = `${PATIENT_ATTEMPT_PROGRESS_API}?patient_id=${patientId}`;
        const progressRes = await apiRequest(progressUrl);
        const progressData = await progressRes.json();

        const rows = Array.isArray(progressData)
          ? progressData
          : Array.isArray(progressData?.results)
            ? progressData.results
            : [];

        const inProgressSet = new Set(inProgressAttemptIds);
        const nextProgressByAttemptId = {};

        rows.forEach((row) => {
          const attemptId = getAttemptProgressAttemptId(row);
          if (!attemptId || !inProgressSet.has(attemptId)) return;

          const questionCount = Number(row?.question_count ?? 0);
          const currentQuestionProgress = Number(row?.current_question_progress ?? 0);

          nextProgressByAttemptId[attemptId] = {
            questionCount: Number.isFinite(questionCount) ? Math.max(0, questionCount) : 0,
            currentQuestionProgress: Number.isFinite(currentQuestionProgress)
              ? Math.max(0, currentQuestionProgress)
              : 0,
          };
        });

        if (!cancelled) {
          setAttemptProgressByAttemptId(nextProgressByAttemptId);
        }
      } catch (err) {
        console.error("Load patient attempt progress failed", err);
        if (!cancelled) {
          setAttemptProgressByAttemptId({});
        }
      }
    };

    loadAttemptProgress();

    return () => {
      cancelled = true;
    };
  }, [inProgressAttemptIds, inProgressAttemptIdsKey, patient?.patient_id]);

  const activeAttemptTokens = useMemo(() => attempts
    .filter((attempt) => String(getAttemptStatus(attempt)).trim().toLowerCase() !== "completed")
    .map((attempt) => ({
      attemptId: Number(getAttemptId(attempt)),
      token: getAttemptTokenValue(attempt),
    }))
    .filter(({ attemptId, token }) => Number.isFinite(attemptId) && attemptId > 0 && token), [attempts]);

  useEffect(() => {
    let cancelled = false;

    const loadTokenExpirations = async () => {
      const expirationEntries = await Promise.all(activeAttemptTokens.map(async ({ attemptId, token }) => {
        try {
          return [attemptId, await fetchTokenExpiration(token)];
        } catch (error) {
          console.error(`Load token expiration failed for attempt ${attemptId}`, error);
          return [attemptId, ""];
        }
      }));

      if (!cancelled) {
        setTokenExpirationByAttemptId(Object.fromEntries(expirationEntries));
      }
    };

    loadTokenExpirations();
    return () => {
      cancelled = true;
    };
  }, [activeAttemptTokens, fetchTokenExpiration]);

  useEffect(() => {
    const loadData = async () => {
      if (!patient?.patient_id) return;

      setLoading(true);
      try {
        await Promise.all([
          loadAttemptsForPatient(),
          loadAssessments(),
          loadPatientEvents(),
        ]);
      } catch (err) {
        console.error("Load patient assessments failed", err);
        setAttempts([]);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [patient?.patient_id, loadAttemptsForPatient, loadAssessments, loadPatientEvents]);

  const formatEventOptionDate = (value) => {
    return formatDateOnly(value);
  };

  const formatCompletedAtDate = (value) => {
    if (!value) return "";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return String(value);

    return parsed.toLocaleString([], {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const formatTokenExpirationDate = (value) => {
    if (!value) return "";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return "";

    return parsed.toLocaleDateString([], {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatReportDate = (value) => {
    if (!value) return "";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return String(value);
    return parsed.toLocaleDateString();
  };

  const truncateEventLabelWithEllipsis = (label) => {
    const normalized = String(label ?? "");
    return normalized.length > 100 ? `${normalized.slice(0, 100)}...` : normalized;
  };

  const getEventFullText = (eventItem, index) => {
    if (!eventItem) return "";
    const eventLabel = eventItem.event || `Event ${index + 1}`;
    const eventDate = formatEventOptionDate(eventItem.event_date);
    return eventDate ? `${eventLabel} (${eventDate})` : eventLabel;
  };

  const getEventTruncatedText = (eventItem, index) => {
    if (!eventItem) return "";
    const eventLabel = eventItem.event || `Event ${index + 1}`;
    const eventDate = formatEventOptionDate(eventItem.event_date);
    const truncatedLabel = truncateEventLabelWithEllipsis(eventLabel);
    return eventDate ? `${truncatedLabel} (${eventDate})` : truncatedLabel;
  };

  useEffect(() => {
    if (!isEventDropdownOpen) return;

    const handleOutsideClick = (event) => {
      const container = eventDropdownRef.current;
      if (!container) return;
      if (container.contains(event.target)) return;
      setIsEventDropdownOpen(false);
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [isEventDropdownOpen]);

  const getAssessmentName = useCallback((attempt) => {
    const attemptName = attempt.assessment?.name ?? attempt.assessment_name;
    if (attemptName) return attemptName;

    const assessmentId = getAttemptAssessmentId(attempt);
    const found = assessments.find((a) => Number(a.assessment_id) === assessmentId);
    return found?.name ?? `Assessment #${assessmentId || "—"}`;
  }, [assessments]);

  const getAssessmentCodename = useCallback((attempt) => {
    const attemptCodename =
      attempt?.assessment?.codename ??
      attempt?.assessment_codename ??
      attempt?.codename;
    if (attemptCodename) return String(attemptCodename).trim();

    const assessmentId = getAttemptAssessmentId(attempt);
    const found = assessments.find((assessment) => Number(assessment?.assessment_id) === assessmentId);
    return String(found?.codename ?? "").trim();
  }, [assessments]);

  const getAttemptFeedbackKey = (attempt) => {
    const attemptId = getAttemptId(attempt);
    const assessmentId = getAttemptAssessmentId(attempt);
    return `${attemptId ?? "tmp"}-${assessmentId ?? "na"}`;
  };

  const handleOpenAttemptLogs = async (attempt) => {
    const attemptId = Number(getAttemptId(attempt));
    if (!Number.isFinite(attemptId) || attemptId <= 0) return;

    setSelectedLogsAttempt(attempt);
    setAttemptLogs([]);
    setAttemptLogsError("");
    setExpandedAttemptLogRows(new Set());
    setAttemptLogsLoading(true);
    setShowAttemptLogsModal(true);

    try {
      const response = await apiRequest(
        `${PATIENT_ASSESSMENT_LOGS_API}?assessment_attempt=${encodeURIComponent(attemptId)}`
      );

      if (!response.ok) {
        const message = await getResponseErrorMessage(response);
        throw new Error(`Assessment logs request failed: ${message}`);
      }

      setAttemptLogs(normalizeApiRows(await response.json()));
    } catch (error) {
      console.error("Failed to load patient assessment logs", error);
      setAttemptLogsError(error?.message || "Failed to load assessment logs.");
    } finally {
      setAttemptLogsLoading(false);
    }
  };

  const attemptLogColumns = useMemo(() => {
    const excludedFields = new Set([
      "patient_assessment_log_id",
      "assessment_attempt_id",
    ]);
    const preferredFields = ["timestamp", "api_call", "type", "status_code", "response"];
    const availableFields = new Set(
      attemptLogs.flatMap((log) => Object.keys(log || {})).filter((field) => !excludedFields.has(field))
    );

    return [
      ...preferredFields.filter((field) => availableFields.has(field)),
      ...Array.from(availableFields).filter((field) => !preferredFields.includes(field)),
    ];
  }, [attemptLogs]);

  const formatAttemptLogColumn = (field) => field
    .split("_")
    .filter(Boolean)
    .map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
    .join(" ");

  const renderAttemptLogValue = (field, value, rowKey) => {
    if (value === null || value === undefined || value === "") return "—";

    if (field === "timestamp") {
      const parsed = new Date(value);
      if (!Number.isNaN(parsed.getTime())) return parsed.toLocaleString();
    }

    if (field === "api_call") {
      try {
        const parsedUrl = new URL(String(value), window.location.origin);
        return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
      } catch {
        return String(value);
      }
    }

    if (field === "response") {
      const parsedJson = parseAttemptLogJson(value);
      const isExpanded = expandedAttemptLogRows.has(rowKey);

      return (
        <div
          className={`assessment-log-response-toggle ${isExpanded ? "is-expanded" : ""}`}
        >
          <button
            type="button"
            className="assessment-log-response-expand-btn"
            aria-expanded={isExpanded}
            aria-label={isExpanded ? "Collapse response" : "Expand response"}
            title={isExpanded ? "Collapse response" : "Expand response"}
            onClick={() => {
              setExpandedAttemptLogRows((previousRows) => {
                const nextRows = new Set(previousRows);
                if (nextRows.has(rowKey)) {
                  nextRows.delete(rowKey);
                } else {
                  nextRows.add(rowKey);
                }
                return nextRows;
              });
            }}
          >
            {isExpanded ? <FaChevronDown /> : <FaChevronRight />}
          </button>
          <pre className="assessment-log-json">
            <code>{parsedJson === null ? String(value) : renderHighlightedJson(parsedJson)}</code>
          </pre>
        </div>
      );
    }

    if (typeof value === "object") {
      return (
        <pre className="assessment-log-json">
          <code>{renderHighlightedJson(value)}</code>
        </pre>
      );
    }

    return String(value);
  };

  const fetchAttemptProgressByAttemptId = useCallback(async (attemptId) => {
    const normalizedAttemptId = Number(attemptId);
    if (!Number.isFinite(normalizedAttemptId) || normalizedAttemptId <= 0) {
      return null;
    }

    const progressUrl = `${PATIENT_ATTEMPT_PROGRESS_API}?attempt_id=${normalizedAttemptId}`;
    const progressRes = await apiRequest(progressUrl);
    const progressData = await progressRes.json();
    const rows = Array.isArray(progressData)
      ? progressData
      : Array.isArray(progressData?.results)
        ? progressData.results
        : [];

    const targetRow = rows.find(
      (row) => getAttemptProgressAttemptId(row) === normalizedAttemptId
    );

    if (!targetRow) {
      return null;
    }

    const questionCount = Number(targetRow?.question_count ?? 0);
    const currentQuestionProgress = Number(targetRow?.current_question_progress ?? 0);

    return {
      questionCount: Number.isFinite(questionCount) ? Math.max(0, questionCount) : 0,
      currentQuestionProgress: Number.isFinite(currentQuestionProgress)
        ? Math.max(0, currentQuestionProgress)
        : 0,
    };
  }, []);

  const handleRefreshAttemptCard = useCallback(async (attempt) => {
    const attemptId = Number(getAttemptId(attempt));
    if (!Number.isFinite(attemptId) || attemptId <= 0) {
      return;
    }

    const feedbackKey = getAttemptFeedbackKey(attempt);
    setRefreshingAttemptByKey((prev) => ({ ...prev, [feedbackKey]: true }));

    try {
      let refreshedAttempt = null;

      const detailRes = await apiRequest(`${PATIENT_ASSESSMENT_ATTEMPTS_API}${attemptId}/`);
      if (detailRes.ok) {
        refreshedAttempt = await detailRes.json();
      }

      if (!refreshedAttempt) {
        const listRes = await apiRequest(PATIENT_ASSESSMENT_ATTEMPTS_API);
        const listRows = await listRes.json();
        const rows = Array.isArray(listRows)
          ? listRows
          : Array.isArray(listRows?.results)
            ? listRows.results
            : [];
        refreshedAttempt = rows.find((row) => Number(getAttemptId(row)) === attemptId) || null;
      }

      if (!refreshedAttempt) {
        throw new Error(`Attempt ${attemptId} not found during card refresh.`);
      }

      const normalizedRefreshedAttempt = {
        ...refreshedAttempt,
        patient_assessment_attempt_id:
          refreshedAttempt.patient_assessment_attempt_id ?? getAttemptId(refreshedAttempt) ?? attemptId,
      };

      setAttempts((prev) =>
        prev.map((row) =>
          Number(getAttemptId(row)) === attemptId
            ? {
                ...row,
                ...normalizedRefreshedAttempt,
              }
            : row
        )
      );

      const refreshedStatus = String(getAttemptStatus(normalizedRefreshedAttempt)).toLowerCase();
      if (refreshedStatus === "in_progress") {
        const progressSnapshot = await fetchAttemptProgressByAttemptId(attemptId);
        setAttemptProgressByAttemptId((prev) => ({
          ...prev,
          [attemptId]: progressSnapshot || {
            questionCount: 0,
            currentQuestionProgress: 0,
          },
        }));
      } else {
        setAttemptProgressByAttemptId((prev) => {
          if (!Object.prototype.hasOwnProperty.call(prev, attemptId)) {
            return prev;
          }
          const next = { ...prev };
          delete next[attemptId];
          return next;
        });
      }
    } catch (error) {
      console.error("Refresh patient assessment card failed", error);
    } finally {
      setRefreshingAttemptByKey((prev) => {
        const next = { ...prev };
        delete next[feedbackKey];
        return next;
      });
    }
  }, [fetchAttemptProgressByAttemptId]);

  const fetchResponseHistoryForAttempt = useCallback(async (attemptId) => {
    const normalizedAttemptId = Number(attemptId);
    if (!Number.isFinite(normalizedAttemptId) || normalizedAttemptId <= 0) {
      return [];
    }

    const response = await apiRequest(
      `${PATIENT_RESPONSES_HISTORY_API}?assessment_attempt_id=${normalizedAttemptId}`
    );

    if (!response.ok) {
      throw new Error(`Failed to load assessment answers (status ${response.status}).`);
    }

    return normalizeApiRows(await response.json());
  }, []);

  const handleViewAnswers = useCallback(async (attempt) => {
    const attemptId = Number(getAttemptId(attempt));
    if (!Number.isFinite(attemptId) || attemptId <= 0) {
      return;
    }

    setShowAnswersModal(true);
    setSelectedAnswersAttempt(attempt);
    setAnswersLoading(true);
    setAnswersError("");
    setAnswerDocumentError("");
    setAnswerDocumentLoadingId(null);
    setAnswerRows([]);
    setLoadingAnswersAttemptId(attemptId);

    try {
      const responses = await fetchResponseHistoryForAttempt(attemptId);
      const sortedResponses = [...responses].sort((leftResponse, rightResponse) => (
        getPatientResponseId(leftResponse) - getPatientResponseId(rightResponse)
      ));

      const nextRows = [];

      sortedResponses.forEach((responseItem, index) => {
        const questionId = getResponseQuestionId(responseItem);
        const questionText =
          String(responseItem?.question ?? "").trim() ||
          `Question #${questionId || index + 1}`;
        const subQuestionPrompt =
          String(
            responseItem?.sub_question_prompt ??
            ""
          ).trim();
        const resolvedAnswerValue = normalizeStoredAnswerValue(
          responseItem?.answer_value ??
            responseItem?.answerValue ??
            responseItem?.response_value ??
            responseItem?.responseValue ??
            responseItem?.answer ??
            null
        );

        if (
          resolvedAnswerValue &&
          typeof resolvedAnswerValue === "object" &&
          !Array.isArray(resolvedAnswerValue) &&
          Object.prototype.hasOwnProperty.call(resolvedAnswerValue, "signature_data_url")
        ) {
          const patientDocumentId = Number(resolvedAnswerValue?.patient_document_id ?? 0) || 0;
          const signatureUrl = String(resolvedAnswerValue?.signature_data_url ?? "").trim();

          const answerItems = Object.entries(resolvedAnswerValue)
            .map(([key, value]) => {
              if (key === "signature_data_url") {
                return null;
              }

              if (key === "patient_document_id") {
                if (!patientDocumentId) return null;
                return {
                  label: "Document",
                  value: {
                    kind: "patient_document_link",
                    patientDocumentId,
                  },
                };
              }

              if (isBlankAnswerValue(value)) {
                return null;
              }

              return {
                label: formatAnswerKeyLabel(key),
                value: formatAnswerValueForTable(value),
              };
            })
            .filter(Boolean);

          answerItems.push({
            label: "Signed",
            value: signatureUrl ? "Yes" : "No",
          });

          if (answerItems.length) {
            nextRows.push({
              key: `${questionId || index}-signature-answer`,
              question: questionText,
              answer: {
                kind: "labeled_answer_group",
                items: answerItems,
              },
            });
          }

          return;
        }

        if (isBlankAnswerValue(resolvedAnswerValue)) {
          return;
        }

        const questionSubPrompt = hasSubAnswerValue(resolvedAnswerValue)
          ? subQuestionPrompt
          : "";

        nextRows.push({
          key: `${questionId || index}-answer`,
          question: questionText,
          questionSubPrompt,
          answer: formatAnswerValueForTable(resolvedAnswerValue),
        });
      });

      setAnswerRows(nextRows);
    } catch (error) {
      console.error("Load assessment answers failed", error);
      setAnswersError(error?.message || "Failed to load assessment answers.");
      setAnswerRows([]);
    } finally {
      setAnswersLoading(false);
      setLoadingAnswersAttemptId(null);
    }
  }, [fetchResponseHistoryForAttempt]);

  const showLinkFeedback = useCallback((attempt, message) => {
    const feedbackKey = getAttemptFeedbackKey(attempt);
    setLinkFeedbackByAttemptKey((prev) => ({
      ...prev,
      [feedbackKey]: message,
    }));

    const existingTimeout = linkFeedbackTimeoutsRef.current[feedbackKey];
    if (existingTimeout) {
      window.clearTimeout(existingTimeout);
    }

    linkFeedbackTimeoutsRef.current[feedbackKey] = window.setTimeout(() => {
      setLinkFeedbackByAttemptKey((prev) => {
        if (!Object.prototype.hasOwnProperty.call(prev, feedbackKey)) {
          return prev;
        }

        const next = { ...prev };
        delete next[feedbackKey];
        return next;
      });
      delete linkFeedbackTimeoutsRef.current[feedbackKey];
    }, 5000);
  }, []);

  useEffect(() => {
    return () => {
      Object.values(linkFeedbackTimeoutsRef.current).forEach((timeoutId) => {
        window.clearTimeout(timeoutId);
      });
      linkFeedbackTimeoutsRef.current = {};
    };
  }, []);

  const openPatientDocument = useCallback(async (patientDocumentId) => {
    const resolvedDocumentId = Number(patientDocumentId) || 0;
    if (!resolvedDocumentId) return;

    const previewWindow = window.open("about:blank", "_blank");
    if (previewWindow) {
      previewWindow.opener = null;
      previewWindow.document.title = "Loading document";
      previewWindow.document.body.innerHTML = "<p style=\"font-family:Arial,sans-serif;padding:24px\">Loading document...</p>";
    }

    setAnswerDocumentError("");
    setAnswerDocumentLoadingId(resolvedDocumentId);

    const requestUrlWithSlash = `${DOCUMENT_DOWNLOAD_GET_LINK_API}${resolvedDocumentId}/`;
    const requestUrlWithoutSlash = `${DOCUMENT_DOWNLOAD_GET_LINK_API}${resolvedDocumentId}`;

    try {
      let response = await apiRequest(requestUrlWithSlash);
      if (!response.ok && response.status === 404) {
        response = await apiRequest(requestUrlWithoutSlash);
      }

      if (!response.ok) {
        const message = await response.text().catch(() => "");
        throw new Error(`Document link request failed (${response.status}) ${message}`);
      }

      const payload = await response.json().catch(() => null);
      const documentUrl = String(payload?.document_url ?? "").trim();
      if (!documentUrl) {
        throw new Error("Document download response is missing document_url.");
      }

      if (previewWindow) {
        previewWindow.location.replace(documentUrl);
        previewWindow.focus();
      } else {
        window.open(documentUrl, "_blank", "noopener,noreferrer");
      }
    } catch (error) {
      if (previewWindow && !previewWindow.closed) {
        previewWindow.close();
      }
      setAnswerDocumentError(error?.message || "Failed to open document.");
    } finally {
      setAnswerDocumentLoadingId(null);
    }
  }, []);

  const handleSendLink = useCallback(async (attempt) => {
    const status = getAttemptStatus(attempt);
    if (status === "removed" || status === "completed") return;

    try {
      const tokenValue = await getOrCreateAttemptToken(attempt);
      const assessmentLink = buildAssessmentLink(tokenValue);

      console.log(
        `[PatientAssessmentsModal] patient assessment link is (${assessmentLink})`
      );

      await sendAssessmentLinkEmail({
        recipientEmail: patient?.email,
        assessmentLink,
      });
      showLinkFeedback(attempt, "Success! Link sent");
    } catch (err) {
      console.error("Send assessment link failed", err);
    }
  }, [
    buildAssessmentLink,
    getOrCreateAttemptToken,
    patient?.email,
    sendAssessmentLinkEmail,
    showLinkFeedback,
  ]);

  const handleCopyAssessmentUrl = useCallback(async (attempt) => {
    const status = String(getAttemptStatus(attempt) ?? "").trim().toLowerCase();
    if (status === "removed" || status === "completed") return;

    try {
      const tokenValue = await getOrCreateAttemptToken(attempt);
      const assessmentLink = buildAssessmentLink(tokenValue);
      await navigator.clipboard.writeText(assessmentLink);
      showLinkFeedback(attempt, "URL copied to clipboard");
    } catch (error) {
      console.error("Copy assessment URL failed", error);
      showLinkFeedback(attempt, "Unable to copy URL");
    }
  }, [buildAssessmentLink, getOrCreateAttemptToken, showLinkFeedback]);

  const handleGenerateNewLink = useCallback(async (attempt) => {
    const status = String(getAttemptStatus(attempt) ?? "").trim().toLowerCase();
    if (status === "removed" || status === "completed") return;

    const attemptId = Number(getAttemptId(attempt));
    if (!Number.isFinite(attemptId) || attemptId <= 0) {
      console.error("Generate new assessment link failed: missing attempt id.");
      return;
    }

    setRotatingTokenAttemptId(attemptId);

    try {
      const rotateResponse = await apiRequest(PATIENT_TOKENS_ROTATE_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          patient_assessment_attempt: attemptId,
        }),
      });

      if (!rotateResponse.ok) {
        const errorMessage = await getResponseErrorMessage(rotateResponse);
        throw new Error(
          `Token rotation failed with status ${rotateResponse.status}: ${errorMessage}`
        );
      }

      const tokenPayload = await rotateResponse.json();
      const rotatedToken = String(tokenPayload?.token ?? "").trim();
      if (!rotatedToken) {
        throw new Error("Token rotation response did not include token value.");
      }
      const rotatedTokenExpiration = await fetchTokenExpiration(rotatedToken);

      setAttempts((prev) => prev.map((attemptItem) =>
        Number(getAttemptId(attemptItem)) === attemptId
          ? {
              ...attemptItem,
              token: rotatedToken,
            }
          : attemptItem
      ));
      setTokenExpirationByAttemptId((prev) => ({
        ...prev,
        [attemptId]: rotatedTokenExpiration,
      }));

      await sendAssessmentLinkEmail({
        recipientEmail: patient?.email,
        assessmentLink: buildAssessmentLink(rotatedToken),
      });
      showLinkFeedback(attempt, "Success! New link generated and emailed");
    } catch (error) {
      console.error("Generate new assessment link failed", error);
    } finally {
      setRotatingTokenAttemptId(null);
    }
  }, [buildAssessmentLink, fetchTokenExpiration, patient?.email, sendAssessmentLinkEmail, showLinkFeedback]);

  const handleDisableAssessment = async (attempt) => {
    let attemptId =
      attempt?.patient_assessment_attempt_id ?? getAttemptId(attempt);

    console.log("Removed Assessment");

    if (!attemptId) {
      try {
        const latestAttempts = await loadAttemptsForPatient();
        const activeMatch = latestAttempts.find(
          (a) =>
            getAttemptAssessmentId(a) === getAttemptAssessmentId(attempt) &&
            getAttemptStatus(a) !== "removed"
        );
        attemptId =
          activeMatch?.patient_assessment_attempt_id ?? getAttemptId(activeMatch);
      } catch (resolveErr) {
        console.error("Could not resolve attempt id for remove action", resolveErr);
      }
    }

    if (!attemptId) {
      console.error("Could not resolve attempt id for remove action", attempt);
      return;
    }

    setAttempts((prev) =>
      prev.map((a) =>
        getAttemptId(a) === attemptId ? { ...a, status: "removed" } : a
      )
    );

    try {
      const endpoint = `${PATIENT_ASSESSMENT_ATTEMPTS_API}${attemptId}/`;
      const patientId = getAttemptPatientId(attempt);
      const assessmentId = getAttemptAssessmentId(attempt);

      const getErrorBody = async (response) => {
        try {
          return await response.json();
        } catch {
          try {
            return { detail: await response.text() };
          } catch {
            return {};
          }
        }
      };

      const inferStatusField = (errorBody) => {
        if (errorBody && Object.prototype.hasOwnProperty.call(errorBody, "attempt_status")) {
          return "attempt_status";
        }
        if (errorBody && Object.prototype.hasOwnProperty.call(errorBody, "status")) {
          return "status";
        }
        return Object.prototype.hasOwnProperty.call(attempt, "attempt_status")
          ? "attempt_status"
          : "status";
      };

      const buildPayload = ({ statusField, includePatientKey, includeAssessmentKey }) => {
        const payload = { [statusField]: "removed" };

        if (includePatientKey === "patient") payload.patient = patientId;
        if (includePatientKey === "patient_id") payload.patient_id = patientId;

        if (includeAssessmentKey === "assessment") payload.assessment = assessmentId;
        if (includeAssessmentKey === "assessment_id") payload.assessment_id = assessmentId;

        if (attempt.final_score !== undefined) {
          payload.final_score = attempt.final_score;
        }

        return payload;
      };

      let statusField = Object.prototype.hasOwnProperty.call(attempt, "attempt_status")
        ? "attempt_status"
        : "status";

      let response = await apiRequest(endpoint, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(buildPayload({ statusField })),
      });

      if (!response.ok) {
        const errorBody = await getErrorBody(response);
        statusField = inferStatusField(errorBody);

        const needsPatient = Object.prototype.hasOwnProperty.call(errorBody, "patient")
          ? "patient"
          : Object.prototype.hasOwnProperty.call(errorBody, "patient_id")
            ? "patient_id"
            : undefined;

        const needsAssessment = Object.prototype.hasOwnProperty.call(errorBody, "assessment")
          ? "assessment"
          : Object.prototype.hasOwnProperty.call(errorBody, "assessment_id")
            ? "assessment_id"
            : undefined;

        response = await apiRequest(endpoint, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(
            buildPayload({
              statusField,
              includePatientKey: needsPatient,
              includeAssessmentKey: needsAssessment,
            })
          ),
        });

        if (!response.ok) {
          response = await apiRequest(endpoint, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(
              buildPayload({
                statusField,
                includePatientKey: needsPatient || "patient_id",
                includeAssessmentKey: needsAssessment || "assessment_id",
              })
            ),
          });

          if (!response.ok) {
            const finalErrorBody = await getErrorBody(response);
            throw new Error(
              `Disable failed with status ${response.status}: ${JSON.stringify(finalErrorBody)}`
            );
          }
        }
      }

      if (!response.ok) {
        throw new Error(`Disable failed with status ${response.status}`);
      }

      const updatedAttempt = await response.json();
      setAttempts((prev) =>
        prev.map((a) =>
          getAttemptId(a) === attemptId
            ? { ...a, ...updatedAttempt, status: "removed" }
            : a
        )
      );
      await loadAttemptsForPatient();
    } catch (err) {
      console.error("Disable assessment failed", err);
      await loadAttemptsForPatient();
    }
  };

  const handleAssignAssessment = async () => {
    if (!selectedAssessmentId || !selectedPatientEventId) return;

    setAssigning(true);
    try {
      const selectedAssessmentNumericId = Number(selectedAssessmentId);
      const activePatientCompanyEntries = (patient?.companies ?? []).filter(
        (entry) => entry?.is_active !== false
      );
      const matchingPatientCompanyEntry = activePatientCompanyEntries.find((entry) => {
        const patientCompanyData = entry?.company ?? entry;
        return questionControlsApplyToAssessment(
          patientCompanyData?.question_controls,
          selectedAssessmentNumericId
        );
      }) ?? activePatientCompanyEntries[0] ?? null;
      const patientCompanyData =
        matchingPatientCompanyEntry?.company ?? matchingPatientCompanyEntry;
      const patientCompanyId = Number(
        patientCompanyData?.company_id ?? patientCompanyData?.id ?? 0
      );

      if (!Number.isFinite(patientCompanyId) || patientCompanyId <= 0) {
        throw new Error("Unable to assign assessment: patient company is missing.");
      }

      const companyResponse = await apiRequest(`${COMPANIES_API}${patientCompanyId}/`);
      if (!companyResponse.ok) {
        throw new Error(
          `Unable to load patient company controls (status ${companyResponse.status}).`
        );
      }

      const companyPayload = await companyResponse.json();
      const companyData = companyPayload?.company ?? companyPayload;
      const apiQuestionControls = companyData?.question_controls;
      const patientCompanyQuestionControls = patientCompanyData?.question_controls;
      const companyQuestionControls = hasPopulatedQuestionControls(apiQuestionControls)
        ? apiQuestionControls
        : hasPopulatedQuestionControls(patientCompanyQuestionControls)
          ? patientCompanyQuestionControls
          : apiQuestionControls ?? patientCompanyQuestionControls ?? null;

      const payload = {
        patient_id: patient.patient_id,
        assessment_id: selectedAssessmentNumericId,
        status: "assigned",
        patient_event_id: Number(selectedPatientEventId),
        question_controls: companyQuestionControls ?? null,
      };

      const response = await apiRequest(PATIENT_ASSESSMENT_ATTEMPTS_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Assign failed with status ${response.status}`);
      }

      let createdAttempt = await response.json();
      const createdAttemptId =
        getAttemptId(createdAttempt) ??
        createdAttempt?.patient_assessment_attempt_id ??
        createdAttempt?.id ??
        null;
      const createdQuestionControls = createdAttempt?.question_controls;
      const hasCreatedQuestionControls = hasPopulatedQuestionControls(createdQuestionControls);

      if (
        createdAttemptId &&
        hasPopulatedQuestionControls(companyQuestionControls) &&
        !hasCreatedQuestionControls
      ) {
        const controlsPatchResponse = await apiRequest(
          `${PATIENT_ASSESSMENT_ATTEMPTS_API}${createdAttemptId}/`,
          {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ question_controls: companyQuestionControls }),
          }
        );

        if (!controlsPatchResponse.ok) {
          throw new Error(
            `Assessment was created, but question controls were not copied (status ${controlsPatchResponse.status}).`
          );
        }

        const patchedAttempt = await controlsPatchResponse.json().catch(() => null);
        if (patchedAttempt && Object.prototype.hasOwnProperty.call(patchedAttempt, "question_controls")) {
          const patchedQuestionControls = patchedAttempt.question_controls;
          const hasPatchedQuestionControls = hasPopulatedQuestionControls(
            patchedQuestionControls
          );

          if (!hasPatchedQuestionControls) {
            console.warn(
              "[PatientAssessmentsModal] Attempt response did not reflect question_controls after PATCH",
              {
                attemptId: createdAttemptId,
                requestedQuestionControls: companyQuestionControls,
                responseQuestionControls: patchedQuestionControls,
              }
            );
          } else {
            createdAttempt = {
              ...createdAttempt,
              ...patchedAttempt,
            };
          }
        }
      }

      if (createdAttemptId) {
        try {
          await handleSendLink({
            ...createdAttempt,
            patient_assessment_attempt_id: createdAttemptId,
            status: createdAttempt?.status ?? "assigned",
          });
        } catch (sendErr) {
          console.error("Assign assessment link/email failed", sendErr);
        }
      } else {
        console.warn(
          "[PatientAssessmentsModal] Could not resolve patient_assessment_attempt_id from assign response",
          createdAttempt
        );
      }

      await loadAttemptsForPatient();

      setSelectedAssessmentId("");
      setSelectedPatientEventId("");
    } catch (err) {
      console.error("Assign assessment failed", err);
    } finally {
      setAssigning(false);
    }
  };

  const handleCloseModal = () => {
    onClose();
  };

  const patientName = `${patient?.first_name ?? ""} ${patient?.last_name ?? ""}`.trim();

  const handleOpenGeneratedReport = async (attempt, matchedEvent) => {
    const attemptId = Number(getAttemptId(attempt));
    if (!Number.isFinite(attemptId) || attemptId <= 0) {
      return;
    }

    setLoadingGeneratedPreview({ attemptId, type: "report" });

    const reportFields = {
      fullName: patientName || patient?.email || "",
      dateOfBirth: formatReportDate(patient?.dob),
      dateOfInjury: formatReportDate(matchedEvent?.event_date ?? attempt?.patient_event?.event_date),
      dateOfAssessment: formatReportDate(
        attempt?.completed_at ??
        attempt?.completedAt ??
        attempt?.completed_on ??
        attempt?.updated_at ??
        ""
      ),
    };

    try {
      const assessmentCodename = getAssessmentCodename(attempt).toLowerCase();
      const expectedDocumentType = assessmentCodename ? `${assessmentCodename}_report` : "";
      const documentsResponse = await apiRequest(
        `${PATIENT_DOCUMENTS_API}?assessment_attempt=${encodeURIComponent(attemptId)}`
      );

      if (!documentsResponse.ok) {
        const errorMessage = await getResponseErrorMessage(documentsResponse);
        throw new Error(`Patient documents request failed: ${errorMessage}`);
      }

      const matchingDocuments = normalizeApiRows(await documentsResponse.json())
        .filter((document) => (
          expectedDocumentType &&
          String(document?.document_type?.description ?? "").trim().toLowerCase() === expectedDocumentType
        ))
        .sort((left, right) => {
          const dateDifference =
            (new Date(right?.created_date).getTime() || 0) -
            (new Date(left?.created_date).getTime() || 0);
          if (dateDifference !== 0) return dateDifference;
          return Number(right?.patient_document_id ?? 0) - Number(left?.patient_document_id ?? 0);
        });

      const latestReportDocument = matchingDocuments[0];
      const patientDocumentId = Number(latestReportDocument?.patient_document_id ?? 0);

      if (patientDocumentId > 0) {
        let documentLinkResponse = await apiRequest(
          `${DOCUMENT_DOWNLOAD_GET_LINK_API}${patientDocumentId}/`
        );
        if (!documentLinkResponse.ok && documentLinkResponse.status === 404) {
          documentLinkResponse = await apiRequest(
            `${DOCUMENT_DOWNLOAD_GET_LINK_API}${patientDocumentId}`
          );
        }

        if (!documentLinkResponse.ok) {
          const errorMessage = await getResponseErrorMessage(documentLinkResponse);
          throw new Error(`Document download link request failed: ${errorMessage}`);
        }

        const documentLinkPayload = await documentLinkResponse.json().catch(() => null);
        const documentUrl = String(documentLinkPayload?.document_url ?? "").trim();
        if (!documentUrl) {
          throw new Error("Document download response did not include document_url.");
        }

        const locationPath = String(latestReportDocument?.location ?? "").split("?")[0];
        const documentName = decodeURIComponent(locationPath.split("/").pop() || "assessment-report.pdf");

        setSelectedGeneratedPreviewType("report");
        setSelectedReportAttempt(attempt);
        setSelectedReportFields(reportFields);
        setSelectedReportHtml("");
        setSelectedReportDocumentUrl(documentUrl);
        setSelectedReportDocumentName(documentName);
        setShowGeneratedReportModal(true);
        return;
      }

      const reportResponse = await apiRequest(COGNITRACKX_REPORT_HTML_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          patient_assessment_attempt_id: attemptId,
        }),
      });

      if (!reportResponse.ok) {
        const errorMessage = await getResponseErrorMessage(reportResponse);
        throw new Error(`Report request failed: ${errorMessage}`);
      }

      const reportPayload = await reportResponse.json().catch(() => null);
      const reportHtml = String(reportPayload?.html ?? "").trim();

      if (!reportHtml) {
        throw new Error("Report response did not include html.");
      }

      const pdfBlob = await createPdfBlobFromHtml(reportHtml);
      let documentTypeId = 2;

      try {
        const documentTypesResponse = await apiRequest(
          `${DOCUMENT_TYPES_API}?search=${encodeURIComponent(expectedDocumentType)}`
        );
        if (documentTypesResponse.ok) {
          const matchingDocumentType = normalizeApiRows(await documentTypesResponse.json())
            .find((documentType) => (
              String(documentType?.description ?? "").trim().toLowerCase() === expectedDocumentType
            ));
          documentTypeId = Number(matchingDocumentType?.document_type_id ?? 0) || 2;
        }
      } catch {
        documentTypeId = 2;
      }

      const resolvedPatientId = Number(
        patient?.patient_id ?? attempt?.patient_id ?? attempt?.patient?.patient_id ?? 0
      );
      const resolvedCompanyId = Number(
        attempt?.company_id ??
        attempt?.company?.company_id ??
        patient?.companies?.[0]?.company?.company_id ??
        patient?.companies?.[0]?.company_id ??
        primaryCompanyId ??
        0
      );
      const completedDate = new Date(
        attempt?.completed_date ??
        attempt?.completed_at ??
        attempt?.completedAt ??
        attempt?.completed_on ??
        attempt?.updated_at ??
        Date.now()
      );
      const validCompletedDate = Number.isNaN(completedDate.getTime()) ? new Date() : completedDate;
      const completedDateText = [
        String(validCompletedDate.getMonth() + 1).padStart(2, "0"),
        String(validCompletedDate.getDate()).padStart(2, "0"),
        validCompletedDate.getFullYear(),
      ].join("-");
      const sanitizeFilenamePart = (value, fallback) => (
        String(value ?? "")
          .trim()
          .replace(/\s+/g, "-")
          .replace(/[^a-zA-Z0-9_-]/g, "") || fallback
      );
      const documentName = `${sanitizeFilenamePart(assessmentCodename, "assessment")}-report-${sanitizeFilenamePart(patient?.first_name, "patient")}-${sanitizeFilenamePart(patient?.last_name, "report")}-${completedDateText}.pdf`;

      const uploadLinkResponse = await apiRequest(DOCUMENT_UPLOAD_GET_LINK_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          patient_id: resolvedPatientId,
          company_id: resolvedCompanyId,
          document_type: documentTypeId,
          assessment_attempt_id: attemptId,
          document_name: documentName,
        }),
      });

      if (!uploadLinkResponse.ok) {
        const errorMessage = await getResponseErrorMessage(uploadLinkResponse);
        throw new Error(`Document upload link request failed: ${errorMessage}`);
      }

      const uploadLinkPayload = await uploadLinkResponse.json().catch(() => null);
      const uploadUrl = String(uploadLinkPayload?.upload_url ?? "").trim();
      const uploadedPatientDocumentId = Number(uploadLinkPayload?.patient_document_id ?? 0);
      if (!uploadUrl || uploadedPatientDocumentId <= 0) {
        throw new Error("Document upload response did not include upload_url or patient_document_id.");
      }

      const uploadResponse = await fetch(uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": "application/pdf" },
        body: pdfBlob,
      });
      if (!uploadResponse.ok) {
        const uploadError = await uploadResponse.text().catch(() => "");
        throw new Error(`Report PDF upload failed (${uploadResponse.status}) ${uploadError}`);
      }

      let documentLinkResponse = await apiRequest(
        `${DOCUMENT_DOWNLOAD_GET_LINK_API}${uploadedPatientDocumentId}/`
      );
      if (!documentLinkResponse.ok && documentLinkResponse.status === 404) {
        documentLinkResponse = await apiRequest(
          `${DOCUMENT_DOWNLOAD_GET_LINK_API}${uploadedPatientDocumentId}`
        );
      }

      if (!documentLinkResponse.ok) {
        const errorMessage = await getResponseErrorMessage(documentLinkResponse);
        throw new Error(`Document download link request failed: ${errorMessage}`);
      }

      const documentLinkPayload = await documentLinkResponse.json().catch(() => null);
      const documentUrl = String(documentLinkPayload?.document_url ?? "").trim();
      if (!documentUrl) {
        throw new Error("Document download response did not include document_url.");
      }

      setSelectedGeneratedPreviewType("report");
      setSelectedReportAttempt(attempt);
      setSelectedReportFields(reportFields);
      setSelectedReportHtml("");
      setSelectedReportDocumentUrl(documentUrl);
      setSelectedReportDocumentName(documentName);
      setShowGeneratedReportModal(true);
    } catch (error) {
      console.error("Failed to load generated report HTML", error);
    } finally {
      setLoadingGeneratedPreview(null);
    }
  };

  const handleOpenGeneratedInvoice = useCallback(async (attempt) => {
    const attemptId = Number(getAttemptId(attempt));
    if (!Number.isFinite(attemptId) || attemptId <= 0) {
      return;
    }

    setLoadingGeneratedPreview({ attemptId, type: "invoice" });

    const invoiceFields = {
      assessmentName: getAssessmentName(attempt),
      fullName: patientName || patient?.email || "",
      companyName: primaryCompanyName,
      invoiceNumber: `${primaryCompanyId}-${attemptId}`,
      assessmentDate:
        attempt?.completed_at ??
        attempt?.completedAt ??
        attempt?.completed_on ??
        attempt?.started_at ??
        attempt?.startedAt ??
        attempt?.updated_at ??
        new Date().toISOString(),
    };

    try {
      setSelectedGeneratedPreviewType("invoice");
      setSelectedReportAttempt(attempt);
      setSelectedReportFields(invoiceFields);
      setSelectedReportHtml("");
      setSelectedReportDocumentUrl("");
      setSelectedReportDocumentName("");
      setShowGeneratedReportModal(true);
    } finally {
      setLoadingGeneratedPreview(null);
    }
  }, [getAssessmentName, patient?.email, patientName, primaryCompanyId, primaryCompanyName]);

  const selectedEventNumericId = Number(selectedPatientEventId);
  const selectedEvent = patientEvents.find(
    (eventItem) => Number(getPatientEventId(eventItem)) === selectedEventNumericId
  );
  const selectedEventDisplayText = selectedEvent
    ? getEventFullText(selectedEvent, 0)
    : "No Event";

  return (
    <div className="modal-overlay">
      <div className="modal modern patient-assessments-modal">
        <div className="modal-header">
          <h3>{patientName || patientLabels.singular} - Assessments</h3>
          <button className="icon-close" onClick={handleCloseModal}>✕</button>
        </div>

        <div className="assessment-event-row">
          <div className="assessment-dropdown-label">Associated Injury Event</div>
          <div className="assessment-event-dropdown" ref={eventDropdownRef}>
            <button
              type="button"
              className="assessment-event-dropdown-trigger"
              onClick={() => setIsEventDropdownOpen((prev) => !prev)}
              aria-haspopup="listbox"
              aria-expanded={isEventDropdownOpen}
            >
              <span className="assessment-event-dropdown-value">
                {selectedEventDisplayText}
              </span>
              <span className="assessment-event-dropdown-caret" aria-hidden="true">▾</span>
            </button>

            {isEventDropdownOpen && (
              <div className="assessment-event-dropdown-menu" role="listbox">
                <button
                  type="button"
                  className="assessment-event-dropdown-option"
                  onClick={() => {
                    setSelectedPatientEventId("");
                    setIsEventDropdownOpen(false);
                  }}
                >
                  No Event
                </button>

                {patientEvents.map((eventItem, index) => {
                  const eventId = getPatientEventId(eventItem);
                  if (!eventId) return null;
                  const truncatedText = getEventTruncatedText(eventItem, index);
                  const fullText = getEventFullText(eventItem, index);

                  return (
                    <button
                      key={eventId}
                      type="button"
                      className="assessment-event-dropdown-option"
                      title={fullText}
                      onClick={() => {
                        setSelectedPatientEventId(String(eventId));
                        setIsEventDropdownOpen(false);
                      }}
                    >
                      {truncatedText}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="assessment-assign-row">
          <div className="assessment-dropdown-label">Assessments</div>
          <select
            value={selectedAssessmentId}
            onChange={(e) => setSelectedAssessmentId(e.target.value)}
          >
            <option value="">Select assessment</option>
            {assignableAssessments.map((assessment) => (
              <option key={assessment.assessment_id} value={assessment.assessment_id}>
                {assessment.name}
              </option>
            ))}
          </select>
          <button
            className="primary"
            type="button"
            disabled={assigning || !selectedAssessmentId || !selectedPatientEventId}
            onClick={handleAssignAssessment}
          >
            {assigning ? "Assigning..." : `Assign to ${patientLabels.singular}`}
          </button>
        </div>

        <div className="assessment-filters-row">
          <label className="checkbox-row">
            <input
              type="checkbox"
              checked={showRemoved}
              onChange={(e) => setShowRemoved(e.target.checked)}
            />
            Show Removed
          </label>
        </div>

        {loading ? (
          <div className="assessment-cards-wrap" aria-live="polite" aria-busy="true">
            <div className="assessment-cards assessment-cards-skeleton">
              {[1, 2, 3].map((slot) => (
                <div className="assessment-card assessment-card-skeleton" key={`assessment-skeleton-${slot}`}>
                  <div className="skeleton-line skeleton-title" />

                  <div className="assessment-card-meta">
                    <div className="skeleton-line skeleton-badge" />
                    <div className="skeleton-line skeleton-progress" />
                  </div>

                  <div className="assessment-card-footer">
                    <div className="skeleton-line skeleton-event" />
                    <div className="assessment-card-actions">
                      <div className="skeleton-line skeleton-action" />
                      <div className="skeleton-line skeleton-action" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="assessment-cards-wrap">
            {displayedAttempts.length === 0 ? (
              <div className="people-empty">No assessments assigned.</div>
            ) : (
              <div className="assessment-cards">
                {displayedAttempts.map((attempt) => {
                  const status = getAttemptStatus(attempt);
                  const statusKey = String(status).trim().toLowerCase();
                  const statusLabel = {
                    assigned: "Assigned",
                    in_progress: "In Progress",
                    completed: "Completed",
                    removed: "Removed",
                  }[statusKey] ?? String(status);
                  const isDisabledForActions =
                    statusKey === "removed" || statusKey === "completed";
                  const attemptId = getAttemptId(attempt);
                  const isRotatingToken = Number(rotatingTokenAttemptId) === Number(attemptId);
                  const attemptFeedbackKey = getAttemptFeedbackKey(attempt);
                  const isRefreshingCard = Boolean(refreshingAttemptByKey[attemptFeedbackKey]);
                  const linkFeedbackMessage = linkFeedbackByAttemptKey[attemptFeedbackKey];
                  const attemptProgress =
                    statusKey === "in_progress" && attemptId
                      ? attemptProgressByAttemptId[Number(attemptId)]
                      : null;
                  const progressMax = Math.max(1, Number(attemptProgress?.questionCount ?? 0));
                  const progressValue = Math.min(
                    progressMax,
                    Math.max(0, Number(attemptProgress?.currentQuestionProgress ?? 0))
                  );
                  const progressPercent = Math.round((progressValue / progressMax) * 100);
                  const patientEventId = getAttemptPatientEventId(attempt);
                  const matchedEvent = patientEvents.find(
                    (eventItem) => Number(getPatientEventId(eventItem)) === patientEventId
                  );
                  const eventText =
                    attempt.patient_event?.event ??
                    matchedEvent?.event ??
                    "No Event";
                  const isCompleted = statusKey === "completed";
                  const completedAtText = formatCompletedAtDate(
                    attempt?.completed_at ??
                    attempt?.completedAt ??
                    attempt?.completed_on ??
                    attempt?.updated_at ??
                    ""
                  );
                  const tokenExpirationText = formatTokenExpirationDate(
                    tokenExpirationByAttemptId[Number(attemptId)] ??
                    getAttemptTokenExpiration(attempt)
                  );
                  const classificationLabel = String(attempt?.classification || "").trim() || "Classification Pending";
                  const classificationPillStyle = getAssessmentClassificationPillStyle(classificationLabel);

                  return (
                    <div
                      className="assessment-card"
                      key={`${attemptId ?? "tmp"}-${getAttemptAssessmentId(attempt)}`}
                    >
                      <div className="assessment-card-header">
                        <div className="assessment-card-title">
                          {getAssessmentName(attempt)}
                          {userTypeId === 3 && attemptId != null ? ` (${attemptId})` : ""}
                        </div>
                        {linkFeedbackMessage && (
                          <div
                            className="assessment-card-link-feedback"
                            role="status"
                            aria-live="polite"
                          >
                            {linkFeedbackMessage}
                          </div>
                        )}
                        <div className="assessment-card-header-actions">
                          {!isDisabledForActions && attemptId && (
                            <button
                              type="button"
                              className="assessment-card-refresh-btn"
                              title={isRefreshingCard ? "Refreshing assessment" : "Refresh this assessment"}
                              aria-label={isRefreshingCard ? "Refreshing assessment" : "Refresh this assessment"}
                              onClick={() => handleRefreshAttemptCard(attempt)}
                              disabled={isRefreshingCard}
                            >
                              <RefreshCw className={isRefreshingCard ? "spin" : ""} strokeWidth={1.5} />
                            </button>
                          )}
                          {userTypeId === 3 && attemptId && (
                            <button
                              type="button"
                              className="assessment-card-header-icon-btn"
                              title="View assessment logs"
                              aria-label="View assessment logs"
                              onClick={() => handleOpenAttemptLogs(attempt)}
                            >
                              <List strokeWidth={1.5} />
                            </button>
                          )}
                          {!isDisabledForActions && (
                            <button
                              type="button"
                              className="assessment-card-header-icon-btn assessment-card-delete-icon-btn"
                              title="Remove assessment"
                              aria-label="Remove assessment"
                              onClick={() => {
                                setDeletingAssessmentAttempt(attempt);
                                setIsDeleteAssessmentOpen(true);
                              }}
                            >
                              <Trash2 strokeWidth={1.5} />
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="assessment-card-meta">
                        <div
                          className={`assessment-status-row ${
                            isCompleted
                              ? "completed-layout"
                              : statusKey === "in_progress"
                                ? "in-progress-layout"
                                : statusKey === "assigned"
                                  ? "assigned-layout"
                                : ""
                          }`}
                        >
                          <span className="assessment-status-group">
                            <strong>Status:</strong>
                            <span
                              className={`assessment-status-badge ${statusKey}`}
                            >
                              {statusLabel}
                            </span>
                          </span>
                          {isCompleted && (
                            <span
                              className="assessment-classification-badge"
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                padding: "4px 10px",
                                borderRadius: "999px",
                                fontSize: "0.8rem",
                                fontWeight: 700,
                                letterSpacing: "0.02em",
                                ...classificationPillStyle,
                              }}
                            >
                              {classificationLabel}
                            </span>
                          )}
                          {isCompleted && completedAtText && (
                            <span
                              className="assessment-completed-date"
                              style={{ fontWeight: 400, color: "#cbd5e1" }}
                            >
                              <span style={{ color: "#7dd3fc" }}>Date:</span>{" "}
                              {completedAtText}
                            </span>
                          )}
                          {statusKey === "in_progress" && (
                            <span className="assessment-progress-inline">
                              <progress value={progressValue} max={progressMax} />
                              <span className="assessment-progress-text">
                                {progressPercent}% Completed
                              </span>
                            </span>
                          )}
                          {(statusKey === "in_progress" || statusKey === "assigned") && tokenExpirationText && (
                            <span className="assessment-token-expiration">
                              <strong>Link Expires On:</strong> {tokenExpirationText}
                            </span>
                          )}
                        </div>
                        {statusKey !== "in_progress" && statusKey !== "assigned" && tokenExpirationText && (
                          <div className="assessment-token-expiration">
                            <strong>Link Expires On:</strong> {tokenExpirationText}
                          </div>
                        )}
                      </div>

                      <div className="assessment-card-footer">
                        <div className="assessment-card-event">
                          <strong>Associated Event:</strong> {eventText}
                        </div>

                        <div
                          className="assessment-card-actions"
                          style={{
                            marginLeft: "auto",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "flex-end",
                              flexWrap: "nowrap",
                              gap: "6px",
                            }}
                          >
                            {isCompleted && !(
                              loadingGeneratedPreview?.attemptId === attemptId &&
                              loadingGeneratedPreview?.type === "invoice"
                            ) && (
                              <button
                                type="button"
                                className="assessments-action-btn"
                                onClick={() => handleOpenGeneratedInvoice(attempt)}
                              >
                                Generate Invoice
                              </button>
                            )}
                            {isCompleted && !(
                              loadingGeneratedPreview?.attemptId === attemptId &&
                              loadingGeneratedPreview?.type === "report"
                            ) && (
                              <button
                                type="button"
                                className="assessments-action-btn"
                                onClick={() => handleOpenGeneratedReport(attempt, matchedEvent)}
                              >
                                Show Report
                              </button>
                            )}
                            {userTypeId === 3 && isCompleted && loadingAnswersAttemptId !== attemptId && (
                              <button
                                type="button"
                                className="assessments-action-btn"
                                onClick={() => handleViewAnswers(attempt)}
                              >
                                View Answers
                              </button>
                            )}
                            {!isCompleted && !isDisabledForActions && (
                              <button
                                type="button"
                                className="assessments-action-btn"
                                onClick={() => handleCopyAssessmentUrl(attempt)}
                              >
                                Copy URL
                              </button>
                            )}
                            {!isDisabledForActions && !isRotatingToken && (
                              <button
                                type="button"
                                className="assessments-action-btn"
                                onClick={() => {
                                  handleSendLink(attempt);
                                }}
                              >
                                Resend Email
                              </button>
                            )}
                            {!isDisabledForActions && !isRotatingToken && (
                              <button
                                type="button"
                                className="assessments-action-btn"
                                onClick={() => {
                                  handleGenerateNewLink(attempt);
                                }}
                              >
                                Generate New Link
                              </button>
                            )}
                          </div>

                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        <div className="modal-actions">
          <button onClick={handleCloseModal}>Close</button>
        </div>

        {showGeneratedReportModal && selectedReportFields && (
          <div
            className="generated-report-overlay"
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(15, 23, 42, 0.56)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              padding: "20px",
              zIndex: 1300,
            }}
          >
            <div
              className="generated-report-modal"
              style={{
                width: "min(1280px, 100%)",
                height: "min(92vh, 980px)",
                background: "#ffffff",
                borderRadius: "20px",
                overflow: "hidden",
                boxShadow: "0 30px 80px rgba(15, 23, 42, 0.32)",
                border: "1px solid rgba(219, 234, 254, 0.8)",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <div
                className="generated-report-header"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "14px 18px",
                  borderBottom: "1px solid #e2e8f0",
                  background: "#f8fafc",
                }}
              >
                <div>
                  <div className="generated-report-title" style={{ fontWeight: 800, color: "#0f172a" }}>
                    {selectedGeneratedPreviewType === "invoice" ? "Generated Invoice" : "Generated Report"}
                  </div>
                  <div className="generated-report-subtitle" style={{ fontSize: "0.85rem", color: "#64748b", marginTop: "2px" }}>
                    {`${getAssessmentName(selectedReportAttempt)} - ${patientName || patient?.email || "Patient"}`}
                  </div>
                </div>
                <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                  <button
                    className="generated-report-download-btn"
                    onClick={handleDownloadGeneratedReportPdf}
                    disabled={isDownloadingGeneratedReport}
                    style={{
                      background: isDownloadingGeneratedReport ? "#cbd5e1" : "#0f172a",
                      color: isDownloadingGeneratedReport ? "#475569" : "#ffffff",
                      border: "1px solid #0f172a",
                      borderRadius: "10px",
                      padding: "8px 12px",
                      cursor: isDownloadingGeneratedReport ? "not-allowed" : "pointer",
                      fontWeight: 700,
                    }}
                  >
                    {isDownloadingGeneratedReport ? "Generating PDF..." : "Download PDF"}
                  </button>
                  <button
                    className="generated-report-close-btn"
                    onClick={() => {
                      setShowGeneratedReportModal(false);
                      setSelectedReportAttempt(null);
                      setSelectedReportFields(null);
                      setSelectedReportHtml("");
                      setSelectedReportDocumentUrl("");
                      setSelectedReportDocumentName("");
                      setSelectedGeneratedPreviewType("report");
                    }}
                    style={{
                      background: "#fff",
                      color: "#334155",
                      border: "1px solid #cbd5e1",
                      borderRadius: "10px",
                      padding: "8px 12px",
                      cursor: "pointer",
                      fontWeight: 700,
                    }}
                  >
                    Close
                  </button>
                </div>
              </div>
              <div className="generated-report-body" style={{ flex: 1, overflow: "auto" }}>
                <div
                  ref={generatedReportRef}
                  style={
                    selectedGeneratedPreviewType === "invoice"
                      ? { display: "table", margin: "0 auto", background: "#ffffff" }
                      : { height: "100%" }
                  }
                >
                  {selectedGeneratedPreviewType === "invoice" ? (
                    <CognitrackXInvoice
                      assessmentName={selectedReportFields?.assessmentName}
                      patientName={selectedReportFields?.fullName}
                      companyName={selectedReportFields?.companyName}
                      invoiceNumber={selectedReportFields?.invoiceNumber}
                      assessmentDate={selectedReportFields?.assessmentDate}
                    />
                  ) : selectedGeneratedPreviewType === "report" ? (
                    <iframe
                      title="Generated report PDF preview"
                      src={selectedReportDocumentUrl}
                      style={{
                        width: "100%",
                        height: "100%",
                        minHeight: "calc(92vh - 120px)",
                        border: 0,
                        background: "#ffffff",
                      }}
                    />
                  ) : (
                    <iframe
                      ref={generatedReportIframeRef}
                      title="Generated report preview"
                      srcDoc={selectedReportHtml}
                      style={{
                        width: "100%",
                        height: "100%",
                        minHeight: "calc(92vh - 120px)",
                        border: 0,
                        background: "#ffffff",
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {showAnswersModal && (
          <div className="modal-overlay" style={{ zIndex: 1300 }}>
            <div className="modal modern patient-answers-modal">
              <div className="modal-header">
                <div className="patient-answers-modal-title-row">
                  <h3>
                    {`${getAssessmentName(selectedAnswersAttempt)} - ${`${patient?.first_name ?? ""} ${patient?.last_name ?? ""}`.trim() || patient?.email || "Patient"}`}
                  </h3>
                  <div className="patient-answers-score-pill-wrap">
                    <span
                      className="patient-answers-score-pill"
                      style={{
                        ...getAssessmentClassificationPillStyle(selectedAnswersAttempt?.classification),
                        fontWeight: 700,
                      }}
                    >
                      {String(selectedAnswersAttempt?.classification || "").trim() || "Classification Pending"}
                    </span>
                  </div>
                </div>
                <button className="icon-close" onClick={() => setShowAnswersModal(false)}>✕</button>
              </div>

              <div className="patient-answers-modal-body">
                {answersLoading ? (
                  <div className="patient-answers-table-wrap patient-answers-table-skeleton-wrap" aria-hidden="true">
                    <table className="patient-answers-table">
                      <thead>
                        <tr>
                          <th>Question</th>
                          <th>Answer</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[1, 2, 3, 4, 5].map((row) => (
                          <tr key={`answer-skeleton-${row}`}>
                            <td>
                              <div className="skeleton-line patient-answers-skeleton-question" />
                            </td>
                            <td>
                              <div className="skeleton-line patient-answers-skeleton-answer" />
                              <div className="skeleton-line patient-answers-skeleton-answer short" />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : answersError ? (
                  <div className="api-debug-error">{answersError}</div>
                ) : answerRows.length === 0 ? (
                  <div className="people-empty">No answers found for this assessment.</div>
                ) : (
                  <>
                    {answerDocumentError ? (
                      <div className="api-debug-error">{answerDocumentError}</div>
                    ) : null}

                    <div className="patient-answers-table-wrap">
                      <table className="patient-answers-table">
                        <thead>
                          <tr>
                            <th>Question</th>
                            <th>Answer</th>
                          </tr>
                        </thead>
                        <tbody>
                          {answerRows.map((row) => (
                            <tr key={row.key}>
                              <td>
                                <div>{row.question}</div>
                                {row.questionSubPrompt ? (
                                  <div style={{ fontWeight: 400, color: "#475569", marginTop: "4px" }}>
                                    ({row.questionSubPrompt})
                                  </div>
                                ) : null}
                              </td>
                              <td>
                                {renderAnswerContent(row.answer, {
                                  onOpenPatientDocument: openPatientDocument,
                                  loadingDocumentId: answerDocumentLoadingId,
                                })}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>

              <div className="modal-actions">
                <button onClick={() => setShowAnswersModal(false)}>Close</button>
              </div>
            </div>
          </div>
        )}

        {showAttemptLogsModal && (
          <div className="modal-overlay" style={{ zIndex: 1300 }}>
            <div className="modal modern assessment-logs-modal">
              <div className="modal-header">
                <div>
                  <h3>Assessment Attempt Logs</h3>
                  <div className="assessment-logs-subtitle">
                    {`${getAssessmentName(selectedLogsAttempt)} - Attempt ${getAttemptId(selectedLogsAttempt) ?? "—"}`}
                  </div>
                </div>
                <button className="icon-close" onClick={() => setShowAttemptLogsModal(false)}>✕</button>
              </div>

              <div className="assessment-logs-modal-body">
                {attemptLogsLoading ? (
                  <div className="people-empty">Loading assessment logs...</div>
                ) : attemptLogsError ? (
                  <div className="api-debug-error">{attemptLogsError}</div>
                ) : attemptLogs.length === 0 ? (
                  <div className="people-empty">No logs found for this assessment attempt.</div>
                ) : (
                  <div className="assessment-logs-table-wrap">
                    <table className="assessment-logs-table">
                      <thead>
                        <tr>
                          {attemptLogColumns.map((field) => (
                            <th key={field}>{formatAttemptLogColumn(field)}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {attemptLogs.map((log, index) => {
                          const rowKey = log?.patient_assessment_log_id ?? `assessment-log-${index}`;
                          const isResponseExpanded = expandedAttemptLogRows.has(rowKey);

                          return (
                            <tr
                              key={rowKey}
                              className={isResponseExpanded ? "assessment-log-row-expanded" : undefined}
                            >
                              {isResponseExpanded ? (
                                <td
                                  className="assessment-log-expanded-response"
                                  colSpan={attemptLogColumns.length}
                                >
                                  {renderAttemptLogValue("response", log?.response, rowKey)}
                                </td>
                              ) : (
                                attemptLogColumns.map((field) => (
                                  <td key={field} className={`assessment-log-field-${field}`}>
                                    {renderAttemptLogValue(field, log?.[field], rowKey)}
                                  </td>
                                ))
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="modal-actions">
                <button onClick={() => setShowAttemptLogsModal(false)}>Close</button>
              </div>
            </div>
          </div>
        )}

        {isDeleteAssessmentOpen && deletingAssessmentAttempt && (
          <div className="modal-overlay assessment-delete-overlay" style={{ zIndex: 1300 }}>
            <div className="modal modern assessment-delete-modal">
              <h3>
                Remove Assessment
              </h3>

              <p>
                Are you sure you want to remove this assessment from this {patientLabels.singularLower}?
              </p>

              <div className="modal-actions">
                <button
                  disabled={deletingAssessmentInProgress}
                  onClick={() => {
                    if (deletingAssessmentInProgress) return;
                    setIsDeleteAssessmentOpen(false);
                    setDeletingAssessmentAttempt(null);
                  }}
                >
                  Cancel
                </button>

                <button
                  className="assessment-delete-confirm-btn"
                  disabled={deletingAssessmentInProgress}
                  onClick={async () => {
                    if (deletingAssessmentInProgress) return;
                    setDeletingAssessmentInProgress(true);
                    try {
                      await handleDisableAssessment(deletingAssessmentAttempt);
                    } finally {
                      setDeletingAssessmentInProgress(false);
                      setIsDeleteAssessmentOpen(false);
                      setDeletingAssessmentAttempt(null);
                    }
                  }}
                >
                  {deletingAssessmentInProgress ? "Removing..." : "Yes, Remove"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};



/* ----------------------------------
   Reusable Detail Row
---------------------------------- */

const Detail = ({ label, children, className = "", style = undefined }) => (
  <div className={`detail-row ${className}`.trim()} style={style}>
    <div className="detail-label">{label}</div>
    <div className="detail-value">{children}</div>
  </div>
);
